import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import ExpandDiv from "../common/ExpandDiv";
import {
  Input,
  Select,
  Checkbox,
  RadioButton,
  TextArea,
  FieldTable,
  PhoneInput,
  ToggleSwitch,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleAddFieldTableRow,
  handleEditFieldTableRow,
  handleSaveFieldTableRow,
  handleDeleteFieldTableRow,
  handleFieldTableChange,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function OHPSD() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  const handleRowChange = (e, index, fieldname) => {
    const rowObj = handleFieldTableChange(data, e, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleSaveRow = (index, fieldname) => {
    const rowObj = handleSaveFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleDeleteRow = (index, fieldname) => {
    const rowObj = handleDeleteFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleEditRow = (index, fieldname) => {
    const rowObj = handleEditFieldTableRow(data, index, fieldname);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  const handleAddRow = (fieldname, newRow) => {
    const rowObj = handleAddFieldTableRow(data, fieldname, newRow);
    const obj = { ...data };
    obj[fieldname]["@value"] = rowObj;
    setData(obj);
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setExpandStatus(true);
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [expandStatus, setExpandStatus] = useState(false);
  const [showNarrative, setShowNarrative] = useState("Hide");

  const handleExpandStatusChange = (overrideValue, callback) => {
    if (overrideValue !== undefined) {
      setExpandStatus(overrideValue);
      if (callback) {
        window.setTimeout(callback, 5);
      }
    } else setExpandStatus(!expandStatus);
  };

  const handleEducationStatusChange = (e) => {
    handleChange(e);
    if (e.currentTarget.value === "0") {
      const employedInput = {
        currentTarget: {
          type: "radio",
          name: "CurrentlyEmployed",
          value: "0",
        },
      };
      handleChange(employedInput);

      const militaryInput = {
        currentTarget: {
          type: "radio",
          name: "MilitaryHistory",
          value: "0",
        },
      };
      handleChange(militaryInput);
    }
  };

  const handleTraumaSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TraumaticExposureInput = {
        currentTarget: {
          type: "checkbox",
          name: "TraumaticExposure",
          checked: false,
        },
      };
      handleChange(TraumaticExposureInput);

      const AvoidantTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "AvoidantTrauma",
          checked: false,
        },
      };
      handleChange(AvoidantTraumaInput);

      const HypervigilanceTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "HypervigilanceTrauma",
          checked: false,
        },
      };
      handleChange(HypervigilanceTraumaInput);

      const IntrusiveDreamsInput = {
        currentTarget: {
          type: "checkbox",
          name: "IntrusiveDreams",
          checked: false,
        },
      };
      handleChange(IntrusiveDreamsInput);

      const NegativeThoughtsInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeThoughts",
          checked: false,
        },
      };
      handleChange(NegativeThoughtsInput);

      const CocentrationProblemsInput = {
        currentTarget: {
          type: "checkbox",
          name: "CocentrationProblems",
          checked: false,
        },
      };
      handleChange(CocentrationProblemsInput);

      const RecklessBehaviorTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "RecklessBehaviorTrauma",
          checked: false,
        },
      };
      handleChange(RecklessBehaviorTraumaInput);

      const SleepDisturbancesTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesTrauma",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesTraumaInput);

      const OtherTraumaInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherTrauma",
          checked: false,
        },
      };
      handleChange(OtherTraumaInput);

      const OtherTraumaDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherTraumaDetails",
          value: "",
        },
      };
      handleChange(OtherTraumaDetailsInput);

      const TraumaSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsFrequency",
          value: "",
        },
      };
      handleChange(TraumaSymptomsFrequencyInput);

      const TraumaSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsIntensity",
          value: "",
        },
      };
      handleChange(TraumaSymptomsIntensityInput);

      const TraumaSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsDuration",
          value: "",
        },
      };
      handleChange(TraumaSymptomsDurationInput);

      const TraumaSymptomsOnsetInput = {
        currentTarget: {
          type: "input",
          name: "TraumaSymptomsOnset",
          value: "",
        },
      };
      handleChange(TraumaSymptomsOnsetInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleAnxietyDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AnxietyDisorderExcessWorryInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderExcessWorry",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderExcessWorryInput);

      const AnxietyDisorderEasilyFatiguedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderEasilyFatigued",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderEasilyFatiguedInput);

      const MuscleTensionInput = {
        currentTarget: {
          type: "checkbox",
          name: "MuscleTension",
          checked: false,
        },
      };
      handleChange(MuscleTensionInput);

      const OverworriedInput = {
        currentTarget: {
          type: "checkbox",
          name: "Overworried",
          checked: false,
        },
      };
      handleChange(OverworriedInput);

      const AnxietyDisorderDifficultyConcentratingInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderDifficultyConcentrating",
          checked: false,
        },
      };
      handleChange(AnxietyDisorderDifficultyConcentratingInput);

      const SleepDisturbancesAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepDisturbancesAnxiety",
          checked: false,
        },
      };
      handleChange(SleepDisturbancesAnxietyInput);

      const RestlessAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "RestlessAnxiety",
          checked: false,
        },
      };
      handleChange(RestlessAnxietyInput);

      const IrritabilityInput = {
        currentTarget: {
          type: "checkbox",
          name: "Irritability",
          checked: false,
        },
      };
      handleChange(IrritabilityInput);

      const OtherAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAnxiety",
          checked: false,
        },
      };
      handleChange(OtherAnxietyInput);

      const OtherAnxietyDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAnxietyDetails",
          value: "",
        },
      };
      handleChange(OtherAnxietyDetailsInput);

      const AnxietyFrequencyInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderFrequency",
          checked: false,
        },
      };
      handleChange(AnxietyFrequencyInput);

      const AnxietyIntensityInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderIntensity",
          checked: false,
        },
      };
      handleChange(AnxietyIntensityInput);

      const AnxietyDurationInput = {
        currentTarget: {
          type: "checkbox",
          name: "AnxietyDisorderDuration",
          checked: false,
        },
      };
      handleChange(AnxietyDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleDepressionChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DepressedMoodInput = {
        currentTarget: {
          type: "checkbox",
          name: "DepressedMood",
          checked: false,
        },
      };
      handleChange(DepressedMoodInput);

      const SleepChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SleepChanges",
          checked: false,
        },
      };
      handleChange(SleepChangesInput);

      const GuiltInput = {
        currentTarget: {
          type: "checkbox",
          name: "Guilt",
          checked: false,
        },
      };
      handleChange(GuiltInput);

      const DiminishedInterestInput = {
        currentTarget: {
          type: "checkbox",
          name: "DiminishedInterest",
          checked: false,
        },
      };
      handleChange(DiminishedInterestInput);

      const PurposelessMovementInput = {
        currentTarget: {
          type: "checkbox",
          name: "PurposelessMovement",
          checked: false,
        },
      };
      handleChange(PurposelessMovementInput);

      const DifficultyConcentratingDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyConcentratingDepression",
          checked: false,
        },
      };
      handleChange(DifficultyConcentratingDepressionInput);

      const WeightChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "WeightChanges",
          checked: false,
        },
      };
      handleChange(WeightChangesInput);

      const FatigueDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "FatigueDepression",
          checked: false,
        },
      };
      handleChange(FatigueDepressionInput);

      const ThoughtsOfDeathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ThoughtsOfDeath",
          checked: false,
        },
      };
      handleChange(ThoughtsOfDeathInput);

      const OtherDepressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherDepression",
          checked: false,
        },
      };
      handleChange(OtherDepressionInput);

      const OtherDepressionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherDepressionDetails",
          value: "",
        },
      };
      handleChange(OtherDepressionDetailsInput);

      const DepressionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DepressionFrequency",
          value: "",
        },
      };
      handleChange(DepressionFrequencyInput);

      const DepressionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DepressionIntensity",
          value: "",
        },
      };
      handleChange(DepressionIntensityInput);

      const DepressionDurationInput = {
        currentTarget: {
          type: "input",
          name: "DepressionDuration",
          value: "",
        },
      };
      handleChange(DepressionDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleAttentionHyperactivityChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AttentionHyperactivityEasilyDistractedInput = {
        currentTarget: {
          type: "checkbox",
          name: "AttentionHyperactivityEasilyDistracted",
          checked: false,
        },
      };
      handleChange(AttentionHyperactivityEasilyDistractedInput);

      const DifficultyFollowThroughInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyFollowThrough",
          checked: false,
        },
      };
      handleChange(DifficultyFollowThroughInput);

      const ForgetfulInput = {
        currentTarget: {
          type: "checkbox",
          name: "Forgetful",
          checked: false,
        },
      };
      handleChange(ForgetfulInput);

      const DifficultyListeningInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyListening",
          checked: false,
        },
      };
      handleChange(DifficultyListeningInput);

      const DisorganizedAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "DisorganizedAttention",
          checked: false,
        },
      };
      handleChange(DisorganizedAttentionInput);

      const FidgetyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Fidgety",
          checked: false,
        },
      };
      handleChange(FidgetyInput);

      const LackOfDetailsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LackOfDetails",
          checked: false,
        },
      };
      handleChange(LackOfDetailsInput);

      const LosesThingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "LosesThings",
          checked: false,
        },
      };
      handleChange(LosesThingsInput);

      const InterruptsOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "InterruptsOthers",
          checked: false,
        },
      };
      handleChange(InterruptsOthersInput);

      const UnableToSitStillInput = {
        currentTarget: {
          type: "checkbox",
          name: "UnableToSitStill",
          checked: false,
        },
      };
      handleChange(UnableToSitStillInput);

      const ExcessiveTalkingInput = {
        currentTarget: {
          type: "checkbox",
          name: "ExcessiveTalking",
          checked: false,
        },
      };
      handleChange(ExcessiveTalkingInput);

      const DifficultyWaitingInput = {
        currentTarget: {
          type: "checkbox",
          name: "DifficultyWaiting",
          checked: false,
        },
      };
      handleChange(DifficultyWaitingInput);

      const OtherAttentionInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherAttention",
          checked: false,
        },
      };
      handleChange(OtherAttentionInput);

      const OtherAttentionDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherAttentionDetails",
          value: "",
        },
      };
      handleChange(OtherAttentionDetailsInput);

      const AttentionFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "AttentionFrequency",
          value: "",
        },
      };
      handleChange(AttentionFrequencyInput);

      const AttentionIntensityInput = {
        currentTarget: {
          type: "input",
          name: "AttentionIntensity",
          value: "",
        },
      };
      handleChange(AttentionIntensityInput);

      const AttentionDurationInput = {
        currentTarget: {
          type: "input",
          name: "AttentionDuration",
          value: "",
        },
      };
      handleChange(AttentionDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleSubstanceUseSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PatternOfSubstanceUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "PatternOfSubstanceUse",
          checked: false,
        },
      };
      handleChange(PatternOfSubstanceUseInput);

      const DesiresCutDownUseInput = {
        currentTarget: {
          type: "checkbox",
          name: "DesiresCutDownUse",
          checked: false,
        },
      };
      handleChange(DesiresCutDownUseInput);

      const TimeSpentObtainingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeSpentObtaining",
          checked: false,
        },
      };
      handleChange(TimeSpentObtainingInput);

      const CravingsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Cravings",
          checked: false,
        },
      };
      handleChange(CravingsInput);

      const FailureOfResponsibilitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "FailureOfResponsibilities",
          checked: false,
        },
      };
      handleChange(FailureOfResponsibilitiesInput);

      const NegativeImpactInput = {
        currentTarget: {
          type: "checkbox",
          name: "NegativeImpact",
          checked: false,
        },
      };
      handleChange(NegativeImpactInput);

      const AbandonsActivitiesInput = {
        currentTarget: {
          type: "checkbox",
          name: "AbandonsActivities",
          checked: false,
        },
      };
      handleChange(AbandonsActivitiesInput);

      const ToleranceInput = {
        currentTarget: {
          type: "checkbox",
          name: "Tolerance",
          checked: false,
        },
      };
      handleChange(ToleranceInput);

      const SUWithdrawalInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUWithdrawal",
          checked: false,
        },
      };
      handleChange(SUWithdrawalInput);

      const SUOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "SUOther",
          checked: false,
        },
      };
      handleChange(SUOtherInput);

      const SUOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "SUOtherDetails",
          value: "",
        },
      };
      handleChange(SUOtherDetailsInput);

      const SubstanceUseFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseFrequency",
          value: "",
        },
      };
      handleChange(SubstanceUseFrequencyInput);

      const SubstanceUseIntensityInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseIntensity",
          value: "",
        },
      };
      handleChange(SubstanceUseIntensityInput);

      const SubstanceUseDurationInput = {
        currentTarget: {
          type: "input",
          name: "SubstanceUseDuration",
          value: "",
        },
      };
      handleChange(SubstanceUseDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleDMDDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const TemperInput = {
        currentTarget: {
          type: "checkbox",
          name: "Temper",
          checked: false,
        },
      };
      handleChange(TemperInput);

      const OutsideOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutsideOutbursts",
          checked: false,
        },
      };
      handleChange(OutsideOutburstsInput);

      const FrequentOutburstsInput = {
        currentTarget: {
          type: "checkbox",
          name: "FrequentOutbursts",
          checked: false,
        },
      };
      handleChange(FrequentOutburstsInput);

      const DMDDIrritableInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDIrritable",
          checked: false,
        },
      };
      handleChange(DMDDIrritableInput);

      const OutburstsAroundOthersInput = {
        currentTarget: {
          type: "checkbox",
          name: "OutburstsAroundOthers",
          checked: false,
        },
      };
      handleChange(OutburstsAroundOthersInput);

      const DMDDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DMDDOther",
          checked: false,
        },
      };
      handleChange(DMDDOtherInput);

      const DMDDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DMDDOtherDetails",
          value: "",
        },
      };
      handleChange(DMDDOtherDetailsInput);

      const DMDDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "DMDDFrequency",
          value: "",
        },
      };
      handleChange(DMDDFrequencyInput);

      const DMDDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "DMDDIntensity",
          value: "",
        },
      };
      handleChange(DMDDIntensityInput);

      const DMDDDurationInput = {
        currentTarget: {
          type: "input",
          name: "DMDDDuration",
          value: "",
        },
      };
      handleChange(DMDDDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleConductDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AggressionInput = {
        currentTarget: {
          type: "checkbox",
          name: "Aggression",
          checked: false,
        },
      };
      handleChange(AggressionInput);

      const DestructionOfPropertyInput = {
        currentTarget: {
          type: "checkbox",
          name: "DestructionOfProperty",
          checked: false,
        },
      };
      handleChange(DestructionOfPropertyInput);

      const TheftInput = {
        currentTarget: {
          type: "checkbox",
          name: "Theft",
          checked: false,
        },
      };
      handleChange(TheftInput);

      const SeriousViolationsInput = {
        currentTarget: {
          type: "checkbox",
          name: "SeriousViolations",
          checked: false,
        },
      };
      handleChange(SeriousViolationsInput);

      const OtherConductDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherConductDisorder",
          checked: false,
        },
      };
      handleChange(OtherConductDisorderInput);

      const OtherConductDisorderDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherConductDisorderDetails",
          value: "",
        },
      };
      handleChange(OtherConductDisorderDetailsInput);

      const ConductDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderFrequency",
          value: "",
        },
      };
      handleChange(ConductDisorderFrequencyInput);

      const ConductDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderIntensity",
          value: "",
        },
      };
      handleChange(ConductDisorderIntensityInput);

      const ConductDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "ConductDisorderDuration",
          value: "",
        },
      };
      handleChange(ConductDisorderDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleODDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const IrritableODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "IrritableODD",
          checked: false,
        },
      };
      handleChange(IrritableODDInput);

      const ArgumentativeInput = {
        currentTarget: {
          type: "checkbox",
          name: "Argumentative",
          checked: false,
        },
      };
      handleChange(ArgumentativeInput);

      const VindictiveInput = {
        currentTarget: {
          type: "checkbox",
          name: "Vindictive",
          checked: false,
        },
      };
      handleChange(VindictiveInput);

      const OtherODDInput = {
        currentTarget: {
          type: "checkbox",
          name: "OtherODD",
          checked: false,
        },
      };
      handleChange(OtherODDInput);

      const OtherODDDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherODDDetails",
          value: "",
        },
      };
      handleChange(OtherODDDetailsInput);

      const ODDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "ODDFrequency",
          value: "",
        },
      };
      handleChange(ODDFrequencyInput);

      const ODDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "ODDIntensity",
          value: "",
        },
      };
      handleChange(ODDIntensityInput);

      const ODDDurationInput = {
        currentTarget: {
          type: "input",
          name: "ODDDuration",
          value: "",
        },
      };
      handleChange(ODDDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleOCDChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OCDPresenceInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDPresence",
          checked: false,
        },
      };
      handleChange(OCDPresenceInput);

      const ObsessionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Obsessions",
          checked: false,
        },
      };
      handleChange(ObsessionsInput);

      const TimeConsumingInput = {
        currentTarget: {
          type: "checkbox",
          name: "TimeConsuming",
          checked: false,
        },
      };
      handleChange(TimeConsumingInput);

      const CompulsionsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Compulsions",
          checked: false,
        },
      };
      handleChange(CompulsionsInput);

      const ReduceAnxietyInput = {
        currentTarget: {
          type: "checkbox",
          name: "ReduceAnxiety",
          checked: false,
        },
      };
      handleChange(ReduceAnxietyInput);

      const SuppressUrgesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SuppressUrges",
          checked: false,
        },
      };
      handleChange(SuppressUrgesInput);

      const OCDOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "OCDOther",
          checked: false,
        },
      };
      handleChange(OCDOtherInput);

      const OCDOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OCDOtherDetails",
          value: "",
        },
      };
      handleChange(OCDOtherDetailsInput);

      const OCDFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OCDFrequency",
          value: "",
        },
      };
      handleChange(OCDFrequencyInput);

      const OCDIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OCDIntensity",
          value: "",
        },
      };
      handleChange(OCDIntensityInput);

      const OCDDurationInput = {
        currentTarget: {
          type: "input",
          name: "OCDDuration",
          value: "",
        },
      };
      handleChange(OCDDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handlePanicDisorderChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PoundingHeartInput = {
        currentTarget: {
          type: "checkbox",
          name: "PoundingHeart",
          checked: false,
        },
      };
      handleChange(PoundingHeartInput);

      const PanicDisorderSweatingInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderSweating",
          checked: false,
        },
      };
      handleChange(PanicDisorderSweatingInput);

      const TremblingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Trembling",
          checked: false,
        },
      };
      handleChange(TremblingInput);

      const ShortnessOfBreathInput = {
        currentTarget: {
          type: "checkbox",
          name: "ShortnessOfBreath",
          checked: false,
        },
      };
      handleChange(ShortnessOfBreathInput);

      const ChokingInput = {
        currentTarget: {
          type: "checkbox",
          name: "Choking",
          checked: false,
        },
      };
      handleChange(ChokingInput);

      const ChestPainInput = {
        currentTarget: {
          type: "checkbox",
          name: "ChestPain",
          checked: false,
        },
      };
      handleChange(ChestPainInput);

      const PanicDisorderNauseaInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderNausea",
          checked: false,
        },
      };
      handleChange(PanicDisorderNauseaInput);

      const DizzyInput = {
        currentTarget: {
          type: "checkbox",
          name: "Dizzy",
          checked: false,
        },
      };
      handleChange(DizzyInput);

      const ChillsInput = {
        currentTarget: {
          type: "checkbox",
          name: "Chills",
          checked: false,
        },
      };
      handleChange(ChillsInput);

      const DerealizationInput = {
        currentTarget: {
          type: "checkbox",
          name: "Derealization",
          checked: false,
        },
      };
      handleChange(DerealizationInput);

      const FearOfCrazyInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfCrazy",
          checked: false,
        },
      };
      handleChange(FearOfCrazyInput);

      const FearOfDyingInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearOfDying",
          checked: false,
        },
      };
      handleChange(FearOfDyingInput);

      const PanicDisorderOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PanicDisorderOther",
          checked: false,
        },
      };
      handleChange(PanicDisorderOtherInput);

      const PanicDisorderOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderOtherDetails",
          value: "",
        },
      };
      handleChange(PanicDisorderOtherDetailsInput);

      const PanicDisorderFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderFrequency",
          value: "",
        },
      };
      handleChange(PanicDisorderFrequencyInput);

      const PanicDisorderIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderIntensity",
          value: "",
        },
      };
      handleChange(PanicDisorderIntensityInput);

      const PanicDisorderDurationInput = {
        currentTarget: {
          type: "input",
          name: "PanicDisorderDuration",
          value: "",
        },
      };
      handleChange(PanicDisorderDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handlePhobiasChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const PhobiasSpecificFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasSpecificFear",
          checked: false,
        },
      };
      handleChange(PhobiasSpecificFearInput);

      const ImmediateFearInput = {
        currentTarget: {
          type: "checkbox",
          name: "ImmediateFear",
          checked: false,
        },
      };
      handleChange(ImmediateFearInput);

      const PhobiasAvoidanceInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasAvoidance",
          checked: false,
        },
      };
      handleChange(PhobiasAvoidanceInput);

      const FearExaggeratedInput = {
        currentTarget: {
          type: "checkbox",
          name: "FearExaggerated",
          checked: false,
        },
      };
      handleChange(FearExaggeratedInput);

      const PhobiasOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "PhobiasOther",
          checked: false,
        },
      };
      handleChange(PhobiasOtherInput);

      const PhobiasOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasOtherDetails",
          value: "",
        },
      };
      handleChange(PhobiasOtherDetailsInput);

      const PhobiasFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasFrequency",
          value: "",
        },
      };
      handleChange(PhobiasFrequencyInput);

      const PhobiasIntensityInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasIntensity",
          value: "",
        },
      };
      handleChange(PhobiasIntensityInput);

      const PhobiasDurationInput = {
        currentTarget: {
          type: "input",
          name: "PhobiasDuration",
          value: "",
        },
      };
      handleChange(PhobiasDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleDIChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const DILowIQInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILowIQ",
          checked: false,
        },
      };
      handleChange(DILowIQInput);

      const DISensoryImpairmentsInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISensoryImpairments",
          checked: false,
        },
      };
      handleChange(DISensoryImpairmentsInput);

      const DISpeechDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpeechDelays",
          checked: false,
        },
      };
      handleChange(DISpeechDelaysInput);

      const DIMotorCoordinationDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DIMotorCoordinationDelays",
          checked: false,
        },
      };
      handleChange(DIMotorCoordinationDelaysInput);

      const DILearningDisorderInput = {
        currentTarget: {
          type: "checkbox",
          name: "DILearningDisorder",
          checked: false,
        },
      };
      handleChange(DILearningDisorderInput);

      const DISpatialDelaysInput = {
        currentTarget: {
          type: "checkbox",
          name: "DISpatialDelays",
          checked: false,
        },
      };
      handleChange(DISpatialDelaysInput);

      const DevelopmentalImpairmentsOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "DevelopmentalImpairmentsOther",
          checked: false,
        },
      };
      handleChange(DevelopmentalImpairmentsOtherInput);

      const DevelopmentalImpairmentsOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "DevelopmentalImpairmentsOtherDetails",
          value: "",
        },
      };
      handleChange(DevelopmentalImpairmentsOtherDetailsInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleBipolarChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const ManicEpisodesInput = {
        currentTarget: {
          type: "checkbox",
          name: "ManicEpisodes",
          checked: false,
        },
      };
      handleChange(ManicEpisodesInput);

      const DepressiveEpisodesInput = {
        currentTarget: {
          type: "checkbox",
          name: "DepressiveEpisodes",
          checked: false,
        },
      };
      handleChange(DepressiveEpisodesInput);

      const SignificantMoodChangesInput = {
        currentTarget: {
          type: "checkbox",
          name: "SignificantMoodChanges",
          checked: false,
        },
      };
      handleChange(SignificantMoodChangesInput);

      const BipolarOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "BipolarOther",
          checked: false,
        },
      };
      handleChange(BipolarOtherInput);

      const BipolarOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "BipolarOtherDetails",
          value: "",
        },
      };
      handleChange(BipolarOtherDetailsInput);

      const BipolarFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "BipolarFrequency",
          value: "",
        },
      };
      handleChange(BipolarFrequencyInput);

      const BipolarIntensityInput = {
        currentTarget: {
          type: "input",
          name: "BipolarIntensity",
          value: "",
        },
      };
      handleChange(BipolarIntensityInput);

      const BipolarDurationInput = {
        currentTarget: {
          type: "input",
          name: "BipolarDuration",
          value: "",
        },
      };
      handleChange(BipolarDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleBorderlineChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const AvoidAbandonmentInput = {
        currentTarget: {
          type: "checkbox",
          name: "AvoidAbandonment",
          checked: false,
        },
      };
      handleChange(AvoidAbandonmentInput);

      const UnstableRelationshipsInput = {
        currentTarget: {
          type: "checkbox",
          name: "UnstableRelationships",
          checked: false,
        },
      };
      handleChange(UnstableRelationshipsInput);

      const IdentityDisturbancesInput = {
        currentTarget: {
          type: "checkbox",
          name: "IdentityDisturbances",
          checked: false,
        },
      };
      handleChange(IdentityDisturbancesInput);

      const ImpulsivityInput = {
        currentTarget: {
          type: "checkbox",
          name: "Impulsivity",
          checked: false,
        },
      };
      handleChange(ImpulsivityInput);

      const RecurrentSelfHarmInput = {
        currentTarget: {
          type: "checkbox",
          name: "RecurrentSelfHarm",
          checked: false,
        },
      };
      handleChange(RecurrentSelfHarmInput);

      const AffectiveInstabilityInput = {
        currentTarget: {
          type: "checkbox",
          name: "AffectiveInstability",
          checked: false,
        },
      };
      handleChange(AffectiveInstabilityInput);

      const EmptinessInput = {
        currentTarget: {
          type: "checkbox",
          name: "Emptiness",
          checked: false,
        },
      };
      handleChange(EmptinessInput);

      const PoorAngerManagementInput = {
        currentTarget: {
          type: "checkbox",
          name: "PoorAngerManagement",
          checked: false,
        },
      };
      handleChange(PoorAngerManagementInput);

      const ParanoidIdeationInput = {
        currentTarget: {
          type: "checkbox",
          name: "ParanoidIdeation",
          checked: false,
        },
      };
      handleChange(ParanoidIdeationInput);

      const BorderlineOtherInput = {
        currentTarget: {
          type: "checkbox",
          name: "BorderlineOther",
          checked: false,
        },
      };
      handleChange(BorderlineOtherInput);

      const BorderlineOtherDetailsInput = {
        currentTarget: {
          type: "input",
          name: "BorderlineOtherDetails",
          value: "",
        },
      };
      handleChange(BorderlineOtherDetailsInput);

      const BorderlineFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "BorderlineFrequency",
          value: "",
        },
      };
      handleChange(BorderlineFrequencyInput);

      const BorderlineIntensityInput = {
        currentTarget: {
          type: "input",
          name: "BorderlineIntensity",
          value: "",
        },
      };
      handleChange(BorderlineIntensityInput);

      const BorderlineDurationInput = {
        currentTarget: {
          type: "input",
          name: "BorderlineDuration",
          value: "",
        },
      };
      handleChange(BorderlineDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const handleOtherSymptomsChange = (e) => {
    handleChange(e);

    if (!e.currentTarget.checked) {
      const OtherSymptomsDetailsInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDetails",
          value: "",
        },
      };
      handleChange(OtherSymptomsDetailsInput);

      const OtherSymptomsFrequencyInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsFrequency",
          value: "",
        },
      };
      handleChange(OtherSymptomsFrequencyInput);

      const OtherSymptomsIntensityInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsIntensity",
          value: "",
        },
      };
      handleChange(OtherSymptomsIntensityInput);

      const OtherSymptomsDurationInput = {
        currentTarget: {
          type: "input",
          name: "OtherSymptomsDuration",
          value: "",
        },
      };
      handleChange(OtherSymptomsDurationInput);
    }

    buildClinicalNarrativeSummary();
  };

  const getFamilyMemberConcerns = (item) => {
    const concerns = [];

    if (item.MentalHealth === "true") concerns.push("Mental Health");
    if (item.Medical === "true") concerns.push("Medical");
    if (item.Disability === "true") concerns.push("Disability");
    if (item.SubstanceAbuse === "true") concerns.push("Substance Abuse");
    if (item.Suicidal === "true") concerns.push("Suicidal");
    if (item.Legal === "true") concerns.push("Legal");
    if (item.Trauma === "true") concerns.push("Trauma");
    if (item.Perpetration === "true") concerns.push("Perpetration");
    if (item.Other === "true") concerns.push("Other");

    if (concerns.length > 0) return concerns.join(", ");
    else return "None reported";
  };

  const getTreatmentRecommendationStatus = (item) => {
    let statuses = [];

    if (
      item.Deferred === "true" ||
      (item.Deferred !== "false" && item.StatusDeferred === "1")
    )
      statuses.push("SAFY will be providing");

    if (
      item.Referred === "true" ||
      (item.Referred !== "false" && item.StatusReferred === "1")
    )
      statuses.push("SAFY will be referring to another agency/individual");

    if (item.Declined === "true") statuses.push("Declined by Client");

    return statuses.join(", ");
  };

  const handleSubstanceUseNAChange = (e) => {
    handleChange(e);
    const noPastTreatmentInput = {
      currentTarget: {
        type: "checkbox",
        name: "NoPastTreatment",
        checked: true,
      },
    };
    handleChange(noPastTreatmentInput);
  };

  const sexAtBirthOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Intersex", name: "Intersex" },
    { _id: "Sex not listed", name: "Sex not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const genderIdentityOptions = [
    { _id: "Female", name: "Female" },
    { _id: "Male", name: "Male" },
    { _id: "Non-binary", name: "Non-binary" },
    { _id: "Agender", name: "Agender" },
    { _id: "Transgender (FtM)", name: "Transgender (FtM)" },
    { _id: "Transgender (MtF)", name: "Transgender (MtF)" },
    { _id: "Identity not listed", name: "Identity not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const pronounOptions = [
    { _id: "She/Her/Hers", name: "She/Her/Hers" },
    { _id: "He/Him/His", name: "He/Him/His" },
    { _id: "They/Them/Their", name: "They/Them/Their" },
    { _id: "Prefer to self-describe", name: "Prefer to self-describe" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const divisionOptions = [
    { _id: "1", name: "Canton/Akron" },
    { _id: "2", name: "Cincinnati" },
    { _id: "3", name: "Cleveland" },
    { _id: "4", name: "Columbus" },
    { _id: "5", name: "Dayton" },
    { _id: "6", name: "Findlay" },
    { _id: "7", name: "Lima" },
    { _id: "8", name: "Sidney" },
  ];

  const psdTypeOptions = [
    { _id: "1", name: "Initial" },
    { _id: "2", name: "Review/Update" },
  ];

  const yesNoOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
  ];

  const yesNoNAOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
    { _id: "NA", name: "N/A" },
  ];

  const yesNoUnknownOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
    { _id: "UK", name: "Unknown" },
  ];

  const yesNoNoneOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
    { _id: "NR", name: "None reported" },
  ];

  const yesNoNoneNAOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
    { _id: "NR", name: "None reported" },
    { _id: "NA", name: "N/A" },
  ];

  const educationOptions = [
    { _id: "0", name: "Client has not started school (skip section)" },
    { _id: "1", name: "Currently enrolled in school" },
    { _id: "2", name: "Graduated" },
    { _id: "3", name: "GED" },
    { _id: "4", name: "Dropout/Did not complete" },
  ];

  const sexualOrientationOptions = [
    {
      _id: "Heterosexual",
      name: "Heterosexual",
    },
    {
      _id: "Gay",
      name: "Gay",
    },
    { _id: "Lesbian", name: "Lesbian" },
    { _id: "Bisexual", name: "Bisexual" },
    { _id: "Pansexual", name: "Pansexual" },
    { _id: "Polysexual", name: "Polysexual" },
    { _id: "Asexual", name: "Asexual" },
    { _id: "Queer/Questioning", name: "Queer/Questioning" },
    { _id: "Orientation not listed", name: "Orientation not listed" },
    { _id: "Prefer not to say", name: "Prefer not to say" },
  ];

  const timeframeOptions = [
    { _id: "0", name: "None/Never" },
    { _id: "1", name: "Current" },
    { _id: "2", name: "Last 30 Days" },
    { _id: "3", name: "Last 6 Months" },
    { _id: "4", name: "More Than 6 Months Ago" },
  ];

  const safetyPlanStatusOptions = [
    { _id: "0", name: "Not Needed" },
    { _id: "1", name: "Active/Effective" },
    { _id: "2", name: "Ineffective/Updates Completed" },
  ];

  const ageGroupOptions = [
    { _id: "0", name: "Adolescent" },
    { _id: "1", name: "Adult" },
  ];

  const functioningLevelOptions = [
    { _id: "0", name: "Age Appropriate" },
    { _id: "1", name: "Regressed" },
    { _id: "2", name: "Advanced" },
    { _id: "3", name: "N/A" },
    { _id: "4", name: "Unable to Assess" },
  ];

  const complianceOptions = [
    { _id: "0", name: "Consistently takes as directed" },
    { _id: "1", name: "Inconsistent with directed use" },
    { _id: "2", name: "Refuses to take" },
    { _id: "3", name: "Forgets to take" },
  ];

  const traumaOptions = [
    { _id: "Physical Abuse", name: "Physical Abuse" },
    { _id: "Sexual Abuse", name: "Sexual Abuse" },
    { _id: "Neglect", name: "Neglect" },
    { _id: "Emotional Abuse", name: "Emotional Abuse" },
    { _id: "Domestic Violence", name: "Domestic Violence" },
    { _id: "Accident", name: "Accident" },
    { _id: "Acts of Violence", name: "Acts of Violence" },
    { _id: "Bullying", name: "Bullying" },
    { _id: "Death/Tragedy", name: "Death/Tragedy" },
    { _id: "Natural Disaster", name: "Natural Disaster" },
    { _id: "Community Violence", name: "Community Violence" },
    { _id: "Medical/Serious Illness", name: "Medical/Serious Illness" },
    { _id: "Abandonment", name: "Abandonment" },
    { _id: "War/Terrorism", name: "War/Terrorism" },
    { _id: "Exploitation/Trafficking", name: "Exploitation/Trafficking" },
    { _id: "Criminal Activity", name: "Criminal Activity" },
    { _id: "Caregiver Disruptions", name: "Caregiver Disruptions" },
    { _id: "Kidnapping", name: "Kidnapping" },
    { _id: "Other", name: "Other" },
  ];

  const statusOptions = [
    { _id: "Victim", name: "Victim" },
    { _id: "Witnessed", name: "Witnessed" },
    { _id: "Suspected", name: "Suspected" },
    { _id: "Threatened", name: "Threatened" },
    { _id: "Perceived", name: "Perceived" },
    { _id: "Repeated Exposure", name: "Repeated Exposure" },
    { _id: "Indirect Exposure", name: "Indirect Exposure" },
  ];

  const riskOptions = [
    { _id: "Low", name: "Low" },
    { _id: "Moderate", name: "Moderate" },
    { _id: "High", name: "High" },
    { _id: "None", name: "None Noted" },
  ];

  const levelOfCareOptions = [
    { _id: "0", name: "LEVEL  0.5 EARLY INTERVENTION" },
    { _id: "1", name: "OPT LEVEL 1.0 EARLY INTERVENTION" },
    { _id: "2", name: "LEVEL 1.0 OP SERVICES" },
    { _id: "3", name: "LEVEL 2.1 IOP SERVICES" },
    { _id: "4", name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES" },
    {
      _id: "5",
      name: "LEVEL 3.1 CLINICALLY-MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
    },
    {
      _id: "6",
      name: "LEVEL 3.3 CLINICALLY-MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICES",
    },
    {
      _id: "7",
      name: "LEVEL 3.5 CLINICALLY-MANAGED HIGH-INTENSITY RESIDENTIAL SERVICES",
    },
    {
      _id: "8",
      name: "LEVEL 3.7 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
    },
    {
      _id: "9",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
    },
  ];

  const adolescentDimension1Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title: "No withdrawal risk.",
    },
    { _id: "1", name: "LEVEL 1.0 OP SERVICES", title: "No withdrawal risk." },
    {
      _id: "2",
      name: "LEVEL 2.1 IOP SERVICES",
      title: "Experiencing minimal withdrawal, or is at risk of withdrawal.",
    },
    {
      _id: "3",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title: "Experiencing mild withdrawal, or is at risk of withdrawal.",
    },
    {
      _id: "4",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "The adolescent state of withdrawal (or risk of withdrawal) is being managed concurrently at another level of care.",
    },
    {
      _id: "5",
      name: "LEVEL 3.5 CLINICALLY MANAGED MEDIUM-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Adolescent is experiencing mild to moderate withdrawal (or is at risk of withdrawal), but does not need pharmacological management or frequent medical or nursing monitoring.",
    },
    {
      _id: "6",
      name: "LEVEL 3.7 CLINCALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICE",
      title:
        "Adolescent is experiencing moderate to severe withdrawal (or is at risk of withdrawal), but this is manageable at Level 3.7.",
    },
    {
      _id: "7",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Adolescent is experiencing severe withdrawal (or is at risk of withdrawal) and requires intensive active medical management.",
    },
  ];

  const adolescentDimension2Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title: "None or very stable.",
    },
    {
      _id: "1",
      name: "LEVEL 1.0 OP SERVICES",
      title:
        "None or very stable, or is receiving concurrent medical monitoring.",
    },
    {
      _id: "2",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "None, are stable, or distracting from treatment at a less intensive level of care. Such problems are manageable at Level 2.1.",
    },
    {
      _id: "3",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "None or stable, or distracting from treatment at a less intensive level of care. Such problems are manageable at Level 2.5.",
    },
    {
      _id: "4",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "None or stable, or receiving concurrent medical monitoring as needed.",
    },
    {
      _id: "5",
      name: "LEVEL 3.5 CLINICALLY MANAGED MEDIUM-INTENSITY RESIDENTIAL SERVICES",
      title:
        "None or stable, or receiving concurrent medical monitoring as needed.",
    },
    {
      _id: "6",
      name: "LEVEL 3.7 CLINCALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICE",
      title:
        "Requires a 24-hour medical monitoring, but not intensive treatment.",
    },
    {
      _id: "7",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Requires a 24-hour medical and nursing care and the full resources of a licensed hospital.",
    },
  ];

  const adolescentDimension3Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title:
        "None or very stable. Any Dimension 3 issues are being addressed through concurrent mental health services and do not interfere with early intervention addiction treatment services.",
    },
    {
      _id: "1",
      name: "LEVEL 1.0 OP SERVICES",
      title:
        "The adolescent's status in dimension 3 features one or more of the following: A) the adolescent is not at risk of harm, B) there is minimal interference, C) minimal to mild impairment, D) the adolescent is experiencing minimal current difficulties with activities of daily living, but there is significant risk of deterioration, E) the adolescent is at minimal imminent risk, which predicts a need for some monitoring or interventions.",
    },
    {
      _id: "2",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "The adolescent's status in dimension 3 features one or more of the following: A) the adolescent is at low risk of harm and is safe between sessions, B) mild interfenrence requires the intensity of this level of care to support treatment engagement, C) mild to moderate impairment but can sustain responsibilities, D) the adolescent is experiencing mild to moderate difficulties with activities of daily living and requires frequent monitoring or interventions, E) the adolescent history (combined with the present situation) predicts the need for frequent monitoring or interventions.",
    },
    {
      _id: "3",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "The adolescent's status in dimension 3 features one or more of the following: A) the adolescent is at low risk of harm and is safe overnight, B) moderate interference requires the intensity of this level of care to support treatment engagement, C) moderate impairment but can sustain responsibilities, D) the adolescent is experiencing moderate difficulties with activities of daily living and requires near/daily monitoring or interventions, E) the adolescent history (combined with the present situation) predicts the need for near daily monitoring or interventions.",
    },
    {
      _id: "4",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "The adolescent's status in dimension 3 features one or more of the following: A) the adolescent needs a stable living environment for safety, B) moderate interference requiring limited 24-hour supervision to support treatment engagement, C) moderate impairment needing limited 24-hour supervision to sustain responsibilities , D) moderate difficulties with activities of daily living requiring limited 24-hour supervision and frequent prompting, E) the adolescent history (combined with the present situation) predicts instability without limited 24-hour supervision.",
    },
    {
      _id: "5",
      name: "LEVEL 3.5 CLINICALLY MANAGED MEDIUM-INTENSITY RESIDENTIAL SERVICES",
      title:
        "The adolescent's status in dimension 3 features one or more of the following: A) moderate but stable risk of harm, B) moderate to severe interference requiring medium intensity residential treatment to support engagement, C) moderate to severe impairment that cannot be managed at a less intensive level of care, D) moderate to severe difficulties with activities of daily living requiring 24-hour supervision and medium-intensity staff assistance, E) the adolescent history (combined with the present situation) predicts destabilization without medium-intensity residential treatment.",
    },
    {
      _id: "6",
      name: "LEVEL 3.7 CLINCALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICE",
      title:
        "The adolescent's status in dimension 3 features one or more of the following: A) moderate risk of harm, needing high-intensity 24-hour monitoring or treatment for safety, B) severe interference requiring high intensity residential treatment to support engagement, C) severe impairment that cannot be managed at a less intensive level of care, D) severe difficulties with activities of daily living requiring 24-hour supervision and high-intensity staff assistance, E) the adolescent history (combined with the present situation) predicts destabilization without high-intensity residential treatment.",
    },
    {
      _id: "7",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "The adolescent's status in dimension 3 features one or more of the following: A) severe risk of harm, B) very severe, almost overwhelming interference renders the adolescent incapable of participating in treatment at a less intensive level of care, C) very severe, dangerous impairment requiring frequent medical and nursing interventions, D) very severe difficulties with activities of daily living requiring frequent medical and nursing interventions, E) the adolescent history (combined with the present situation) predicts destabilization without inpatient medical management.",
    },
  ];

  const adolescentDimension4Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title:
        "Willingness to explore how current alcohol, tobacco, medication, other drug use, and/or high-risk behaviors may affect acheivement of personal goals.",
    },
    {
      _id: "1",
      name: "LEVEL 1.0 OP SERVICES",
      title:
        "Willingness to engage in treatment, and is at least contemplating change, but needs motivating and monitoring strategies.",
    },
    {
      _id: "2",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "Requires close monitoring or support several times a week to promot progress through the stages of change because of variable treatment engagement, or no interest in getting assistance.",
    },
    {
      _id: "3",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "Requires a near daily structured program to promote progress through the stages of change because of little treatment engagement or escalating use and impairment, or no awareness of the role of alcohol, tobacco, and/or other drugs play in his/her present problems.",
    },
    {
      _id: "4",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Open to recovery, but needs limited 24-hour supervision to promote or sustain change.",
    },
    {
      _id: "5",
      name: "LEVEL 3.5 CLINICALLY MANAGED MEDIUM-INTENSITY RESIDENTIAL SERVICES",
      title:
        "The adolescent needs intensive motivating strategies in a 24-hour structured program to address minimal engagement in, or opposition to treatment, or to address their lack of recognition of current severe impairment.",
    },
    {
      _id: "6",
      name: "LEVEL 3.7 CLINCALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICE",
      title:
        "The adolescent needs intensive motivating strategies in a 24-hour medically monitored program due to no treatment engagedment associated with a biomedical, emotional, or behavioral condition, or because they activel opposes treatment, requiring secure placement to remain safe, or because they need high-intensity case management to create linkages that would support outpatient treatment.",
    },
    {
      _id: "7",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Problems in this dimension do not qualify the patient for Level 4 services. If the patient's only severity is in Dimension 4, 5, and/or 6 without high severity in Dimension 1, 2, and/or 3, then the patient does not qualify for Level 4.",
    },
  ];

  const adolescentDimension5Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title:
        "Needs an understanding of, or skills to change current alcohol, tobacco, orther drug or medication use patterns, and/or high-risk behaviors.",
    },
    {
      _id: "1",
      name: "LEVEL 1.0 OP SERVICES",
      title:
        "Able to maintain abstinence or control use and pursue recovery or motivational goals with minimal support.",
    },
    {
      _id: "2",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "Significant risks of relapse or continued use or continued problems and deterioration in level of functioning. Has poor prevention skills and needs close monitoring or support.",
    },
    {
      _id: "3",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "High risk of relapse or continued use, or continued problems in deterioration in level of functioning. Has minimal prevention skills and needs near daily monitoring and support.",
    },
    {
      _id: "4",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Understand the potential for continued use and/or has emerging recovery skills but needs supervision to reinforce recovery and relapse prevention skills, limited exposure to substances and/or environmental triggers or maintain therapeutic gains.",
    },
    {
      _id: "5",
      name: "LEVEL 3.5 CLINICALLY MANAGED MEDIUM-INTENSITY RESIDENTIAL SERVICES",
      title:
        "High risk of relapse or continued use, or continued problems in deterioration in level of functioning. Has minimal prevention skills and needs near daily monitoring and support.",
    },
    {
      _id: "6",
      name: "LEVEL 3.7 CLINCALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICE",
      title:
        "Unable to interrupt high-severity or high-frequency pattern of use/or behaviors and avoid dangerous consequences without high-intensity 24-hour interventions (because of an emotional, behavioral, or cognitive condition, severe impulse control problems, withdrawal symptoms, and the like.",
    },
    {
      _id: "7",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Problems in this dimension do not qualify the patient for Level 4 services. If the patient's only severity is in Dimension 4, 5, and/or 6 without high severity in Dimension 1, 2, and/or 3, then the patient does not qualify for Level 4.",
    },
  ];

  const adolescentDimension6Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title:
        "Adolescent's risk of initiation of or progression in substance use and/or high-risk behaviors is increased by substance use or values about use. High-risk behaviors of family, peers, or others in adolescent social support system.",
    },
    {
      _id: "1",
      name: "LEVEL 1.0 OP SERVICES",
      title: "Family environment can support recovery with limited assistance.",
    },
    {
      _id: "2",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "Adolescent's environment is impeding their recovery, and adolescent requires close monitoring or support to overcome that barrier.",
    },
    {
      _id: "3",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "Adolescent's environment renders recovery unlikely without near/daily monitoring or support, or frequent relief from their home environment.",
    },
    {
      _id: "4",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Environment poses a risk to their recovery so that they require alternative residential, secure placement or support.",
    },
    {
      _id: "5",
      name: "LEVEL 3.5 CLINICALLY MANAGED MEDIUM-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Environment is dangerous to their recovery, so that they require residential treatment to promote recovery goals, or for protection.",
    },
    {
      _id: "6",
      name: "LEVEL 3.7 CLINCALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICE",
      title:
        "Environment is dangerous to their recovery, so that they require residential treatment to promote recovery goals, or for protection, and to help him or her establish a successful transition to a less intensive level of care.",
    },
    {
      _id: "7",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Problems in this dimension do not qualify the patient for Level 4 services. If the patient's only severity is in Dimension 4, 5, and/or 6 without high severity in Dimension 1, 2, and/or 3, then the patient does not qualify for Level 4.",
    },
  ];

  const adultDimension1Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title: "No withdrawal risk.",
    },
    {
      _id: "1",
      name: "LEVEL 1 OTP",
      title:
        "Physiologically dependent on opioids and requires OTP to prevent withdrawal.",
    },
    {
      _id: "2",
      name: "LEVEL 1 OP SERVICES",
      title:
        "Not experiencing significant withdrawal, or at minimal risk of severe withdrawal. Manageable at Level 1 withdrawal management.",
    },
    {
      _id: "3",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "Minimal risk of severe withdrawal. Manageable at Level 2 withdrawal management.",
    },
    {
      _id: "4",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "Moderate risk of severe withdrawal. Manageable at Level 2 withdrawal management.",
    },
    {
      _id: "5",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "No withdrawal risk, or minimal or stable withdrawal. Concurrently receiving Level 1 or Level 2 withdrawal management services.",
    },
    {
      _id: "6",
      name: "LEVEL 3.3 CLINICALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "At minimal risk of severe withdrawal. If withdrawal is present, manageable at Level 3.2 withdrawal management.",
    },
    {
      _id: "7",
      name: "LEVEL 3.5 CLINICALLY MANAGED HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "At minimal risk of severe withdrawal. If withdrawal is present, manageable at Level 3.2 withdrawal management.",
    },
    {
      _id: "8",
      name: "LEVEL 3.7 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "At high risk of withdrawal, but manageable at Level 3.7 withdrawal management and does not require the full resources of a licensed hospital.",
    },
    {
      _id: "9",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "At high risk of withdrawal and requires Level 4 withdrawal management and the full resources of a licensed hospital.",
    },
  ];

  const adultDimension2Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title: "None or very stable.",
    },
    {
      _id: "1",
      name: "LEVEL 1 OTP",
      title: "None or manageable with outpatient medical monitoring.",
    },
    {
      _id: "2",
      name: "LEVEL 1 OP SERVICES",
      title:
        "None or very stable, or is receiving concurrent medical monitoring.",
    },
    {
      _id: "3",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "None or not a distraction from treatment. Such problems are manageable at Level 2.1.",
    },
    {
      _id: "4",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "None or not sufficient to distract from treatment. Such problems are manageable at Level 2.5.",
    },
    {
      _id: "5",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "None or stable, or receiving concurrent medical monitoring as needed.",
    },
    {
      _id: "6",
      name: "LEVEL 3.3 CLINICALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "None or stable, or receiving concurrent medical monitoring as needed.",
    },
    {
      _id: "7",
      name: "LEVEL 3.5 CLINICALLY MANAGED HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "None or stable, or receiving concurrent medical monitoring as needed.",
    },
    {
      _id: "8",
      name: "LEVEL 3.7 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Requires a 24-hour medical monitoring, but not intensive treatment.",
    },
    {
      _id: "9",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Requires a 24-hour medical and nursing care and the full resources of a licensed hospital.",
    },
  ];

  const adultDimension3Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title: "None or very stable.",
    },
    {
      _id: "1",
      name: "LEVEL 1 OTP",
      title: "None or manageable in an outpatient structured environment.",
    },
    {
      _id: "2",
      name: "LEVEL 1 OP SERVICES",
      title:
        "None or very stable, or is receiving concurrent medical health monitoring.",
    },
    {
      _id: "3",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "Mild severity, with potential to distract from recovery; needs monitoring.",
    },
    {
      _id: "4",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "Mild or moderate severity, with potential to distract from recovery, needs stabilization.",
    },
    {
      _id: "5",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "None or minimal, not distracting to recovery. If stable, a co-occurring capable program is appropriate. If not, a co-occurring enhanced program is required.",
    },
    {
      _id: "6",
      name: "LEVEL 3.3 CLINICALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Mild to moderate severity; needs structure to focus on recovery. Treatment should be designed to address significant cognitive deficits. If stable, a co-occurring capable program is appropriate, if not, a co-occurring enhanced program is required.",
    },
    {
      _id: "7",
      name: "LEVEL 3.5 CLINICALLY MANAGED HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Demonstrates repeated inability to control impulses, or unstable and dangerouse signs/symptoms require stabilization. Other functional; deficits require stabilization and a 24-hour setting to prepare for community integration and continuing care. A co-occurring enhanced setting is required for those with severe and chronic mental illness.",
    },
    {
      _id: "8",
      name: "LEVEL 3.7 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Moderate severity; needs a 24-hour structured setting. If the patient has a co-occurring mental disorder, requires concurring mental health services in a medically monitored setting.",
    },
    {
      _id: "9",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Because of severe and unstable problems, requires 24 hour psychiatric care with concimitant addiction treatment (co-occurring enhanced).",
    },
  ];

  const adultDimension4Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title:
        "Willingness to explore how current alcohol, tobacco, medication, other drug use, and/or high-risk behaviors may affect acheivement of personal goals.",
    },
    {
      _id: "1",
      name: "LEVEL 1 OTP",
      title:
        "Ready to change the negative effects of opioid use, but is not ready for total abstinence from illicit prescription or non-prescription drug use.",
    },
    {
      _id: "2",
      name: "LEVEL 1 OP SERVICES",
      title:
        "Ready for recovery but needs motivating and monitoring strategies to strengthen readiness. Or needs ongoing monitoring and disease management. Or high severity in this dimension but not in other dimenstions, needs level 1 motivational enhancement strategies.",
    },
    {
      _id: "3",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "Has variable engagement in treatment, ambivalence, or lack of awareness of the substance use or mental health problems, and requires a structured program several times a week to promote progress through the stages of change.",
    },
    {
      _id: "4",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "Open to recovery, but needs a structured environment to maintain therapeutic gains.",
    },
    {
      _id: "5",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Open to recovery, but needs a structured environment to maintain therapeutic gains.",
    },
    {
      _id: "6",
      name: "LEVEL 3.3 CLINICALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Has little awareness and needs interventions available at Level 3.3 to engage and stay in treatment. If there is high severity in Dimension 4 but not in any other dimension, motivational enhancement strategies should be provided in Level 1.",
    },
    {
      _id: "7",
      name: "LEVEL 3.5 CLINICALLY MANAGED HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Has marked difficulty with, or opposition to, treatment with dangerous consequences. If there is a severity in Dimension 4 but not in any other dimension, motivational enhancement strategies should be provided in Level 1.",
    },
    {
      _id: "8",
      name: "LEVEL 3.7 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Low interest in treatment and impulse control is poor, despite negative consequences; needs motivating strategies only safely available in a 24-hour structured setting. If there is high severity in Dimension 4 but not in any other dimension, motivational enhancement strategies should be provided in Level 1.",
    },
    {
      _id: "9",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Problems in this dimension do not qualify the patient for Level 4 services. If the patient's only severity is in Dimension 4, 5, and/or 6 without high severity in Dimension 1, 2, and/or 3, then the patient does not qualify for Level 4.",
    },
  ];

  const adultDimension5Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title:
        "Needs an understanding of, or skills to change current alcohol, tobacco, orther drug or medication use patterns, and/or high-risk behaviors.",
    },
    {
      _id: "1",
      name: "LEVEL 1 OTP",
      title:
        "At high risk of relapse or continued use without OTP and structured therapy to promote treatment progress.",
    },
    {
      _id: "2",
      name: "LEVEL 1 OP SERVICES",
      title:
        "Able to maintain abstinence or control use and/or addictive behaviors and pursue recovery or motivational goals with minimal support.",
    },
    {
      _id: "3",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "Intensification of addiction or mental health symptoms indicate a high likelihood of relapse or continued problems without close monitoring and support several times a week.",
    },
    {
      _id: "4",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "Understands relapse, but needs structure to maintain therapeutic gains.",
    },
    {
      _id: "5",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Has little awareness and needs interventions available only at Level 3.3 to prevent continued use, with imminent dangerous consequences, because of cognitive deficits or comparable dysfunction.",
    },
    {
      _id: "6",
      name: "LEVEL 3.3 CLINICALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Has no recognition of the skills needed to prevent continued use, with imminently dangerous consequences.",
    },
    {
      _id: "7",
      name: "LEVEL 3.5 CLINICALLY MANAGED HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Unable to control use, with imminently dangerous consequences, despite active participation at less intensive levels of care.",
    },
    {
      _id: "8",
      name: "LEVEL 3.7 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Unable to control use, with imminently dangerous consequences, despite active participation at less intensive levels of care.",
    },
    {
      _id: "9",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Problems in this dimension do not qualify the patient for Level 4 services. If the patient's only severity is in Dimension 4, 5, and/or 6 without high severity in Dimension 1, 2, and/or 3, then the patient does not qualify for Level 4.",
    },
  ];

  const adultDimension6Options = [
    {
      _id: "0",
      name: "LEVEL 0.5 EARLY INTERVENTION",
      title:
        "Social support system or significant others increase risk of personal conflict about alcohol, tobacco, or other drug use.",
    },
    {
      _id: "1",
      name: "LEVEL 1 OTP",
      title:
        "Recovery environment is supportive and/or the patient has skills to cope.",
    },
    {
      _id: "2",
      name: "LEVEL 1 OP SERVICES",
      title:
        "Recovery environment is supportive and/or the patient has skills to cope.",
    },
    {
      _id: "3",
      name: "LEVEL 2.1 IOP SERVICES",
      title:
        "Recovery environment is not supportive but with structure and support and relief from the home environment, the patient can cope.",
    },
    {
      _id: "4",
      name: "LEVEL 2.5 PARTIAL HOSPITALIZATION SERVICES",
      title:
        "Environment is dangerous, but recovery is achievable if Level 3.1 24-hour structure is achievable.",
    },
    {
      _id: "5",
      name: "LEVEL 3.1 CLINICALLY MANAGED LOW-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Environment is dangerous and patient needs 24-hour structure to learn to cope.",
    },
    {
      _id: "6",
      name: "LEVEL 3.3 CLINICALLY MANAGED POPULATION SPECIFIC HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Environment is dangerous and the patient has skills to cope outside of a highly structured 24-hour setting.",
    },
    {
      _id: "7",
      name: "LEVEL 3.5 CLINICALLY MANAGED HIGH-INTENSITY RESIDENTIAL SERVICES",
      title:
        "Environment is dangerous, but recovery is achievable if Level 3.1 24-hour structure is achievable.",
    },
    {
      _id: "8",
      name: "LEVEL 3.7 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Environment is dangerous, but recovery is achievable if Level 3.1  24-hour structure is achievable.",
    },
    {
      _id: "9",
      name: "LEVEL 4.0 MEDICALLY-MONITORED INTENSIVE INPATIENT SERVICES",
      title:
        "Problems in this dimension do not qualify the patient for Level 4 services. If the patient's only severity is in Dimension 4, 5, and/or 6 without high severity in Dimension 1, 2, and/or 3, then the patient does not qualify for Level 4.",
    },
  ];

  const reasonNotPlacedOptions = [
    { _id: "NA", name: "N/A" },
    { _id: "0", name: "CLIENT ON WAITING LIST" },
    { _id: "1", name: "LOC NOT ACCESSIBLE" },
    { _id: "2", name: "PROVIDER JUDGEMENT" },
    { _id: "3", name: "CLIENT PREFERS TO NOT ATTEND LOC" },
    { _id: "4", name: "LANGUAGE BARRIER" },
    { _id: "5", name: "FINANCIAL BARRIER" },
    { _id: "6", name: "FINANCIAL RESPONSIBILITY" },
    { _id: "7", name: "OTHER" },
  ];

  const treatmentRecommendationOptions = [
    {
      _id: "Individual Counseling/Therapy",
      name: "Individual Counseling/Therapy",
    },
    { _id: "Family Counseling/Therapy", name: "Family Counseling/Therapy" },
    { _id: "Group Counseling/Therapy", name: "Group Counseling/Therapy" },
    { _id: "Mental Health Consultation", name: "Mental Health Consultation" },
    {
      _id: "Crisis Intervention and Resolution",
      name: "Crisis Intervention and Resolution",
    },
    { _id: "Case Management Services", name: "Case Management Services" },
    { _id: "Psychoeducation", name: "Psychoeducation" },
    {
      _id: "Community Psychiatric Support Services (CPST)",
      name: "Community Psychiatric Support Services (CPST)",
    },
    {
      _id: "Psychiatric Evaluation Services",
      name: "Psychiatric Services",
    },
    {
      _id: "Therapeutic Behavioral Services (TBS)",
      name: "Therapeutic Behavioral Services (TBS)",
    },
    {
      _id: "Early Childhood Intervention Services",
      name: "Early Childhood Intervention Services",
    },
    {
      _id: "Substance Abuse Specific Treatment",
      name: "Substance Abuse Specific Treatment",
    },
    {
      _id: "Intensive Home Based Treatment (IHBT)",
      name: "Intensive Home Based Treatment (IHBT)",
    },
  ];

  const buildClinicalNarrativeSummary = () => {
    let summary = "";

    // Presenting Problem & Strengths
    let presentingProblemSummary = "";
    if (
      data.PresentingProblem["@value"] &&
      data.PresentingProblem["@value"] !== ""
    )
      presentingProblemSummary += `Presenting Problem: ${data.PresentingProblem["@value"]}; `;

    if (data.ClientStrengths["@value"] && data.ClientStrengths["@value"] !== "")
      presentingProblemSummary += `Client strengths, abilities, and skills: ${data.ClientStrengths["@value"]}; `;

    if (data.Supports["@value"] && data.Supports["@value"] !== "")
      presentingProblemSummary += `Supports for the client or caregivers: ${data.Supports["@value"]};`;

    if (presentingProblemSummary !== "")
      summary += `Presenting Problem and Strengths:\r\n${presentingProblemSummary.trimEnd()}\r\n\r\n`;

    //Family Environment and History
    let familyHistorySummary = "";
    if (
      data.CurrentPlacement["@value"] &&
      data.CurrentPlacement["@value"] !== ""
    )
      familyHistorySummary += `Current placement: ${data.CurrentPlacement["@value"]}; `;

    if (
      data.PlacementHistory["@value"] &&
      data.PlacementHistory["@value"] !== ""
    )
      familyHistorySummary += `Placement History: ${data.PlacementHistory["@value"]}; `;

    if (
      data.FamilyHistoryDetails["@value"] &&
      data.FamilyHistoryDetails["@value"] !== "" &&
      data.FamilyHistoryDetails["@value"] !== '[{"IsEditable": true}]'
    )
      familyHistorySummary += `Family Members: ${JSON.parse(
        data.FamilyHistoryDetails["@value"]
      )
        .map(
          (item) =>
            `${item.Name}${
              item.Comments !== undefined && item.Comments !== ""
                ? `- ${item.Comments}`
                : ""
            }`
        )
        .join(", ")
        .trimEnd()}; `;

    if (
      data.FamilyHistoryComments["@value"] &&
      data.FamilyHistoryComments["@value"] !== ""
    )
      familyHistorySummary += `Comments: ${data.FamilyHistoryComments["@value"]}; `;

    if (familyHistorySummary !== "")
      summary += `Family History:\r\n${familyHistorySummary.trimEnd()}\r\n\r\n`;

    // Culture/Ethnicity/Religion/Spirituality
    let cultureSummary = "";
    if (data.RaceEthnicity["@value"] && data.RaceEthnicity["@value"] !== "")
      cultureSummary += `Identified race and ethnicity: ${data.RaceEthnicity["@value"]}; `;

    if (data.PlacesLived["@value"] && data.PlacesLived["@value"] !== "")
      cultureSummary += `Places lived: ${data.PlacesLived["@value"]}; `;

    if (
      data.SocioeconomicStatus["@value"] &&
      data.SocioeconomicStatus["@value"] !== ""
    )
      cultureSummary += `Socioeconomic Status: ${data.SocioeconomicStatus["@value"]}; `;

    if (data.Religion["@value"] && data.Religion["@value"] !== "")
      cultureSummary += `Identified religion/belief system and role of faith in client's life: ${data.Religion["@value"]}; `;

    if (
      data.CulturalDifferencesComments["@value"] &&
      data.CulturalDifferencesComments["@value"] !== ""
    )
      cultureSummary += `Comments: ${data.CulturalDifferencesComments["@value"]}; `;

    if (cultureSummary !== "")
      summary += `Culture/Ethnicity/Religion/Spirituality:\r\n${cultureSummary.trimEnd()}\r\n\r\n`;

    // Developmental History
    let developmentalSummary = "";
    if (data.PHComments["@value"] && data.PHComments["@value"] !== "")
      developmentalSummary += `Mother's Pregnancy History Comments: ${data.PHComments["@value"]}; `;

    if (
      data.DevelopmentalImpairmentsDetails["@value"] &&
      data.DevelopmentalImpairmentsDetails["@value"] !== ""
    )
      developmentalSummary += `Developmental delays or special needs: ${data.DevelopmentalImpairmentsDetails["@value"]}; `;

    if (
      data.SpecializedServicesDetails["@value"] &&
      data.SpecializedServicesDetails["@value"] !== ""
    )
      developmentalSummary += `Specialized Services: ${data.SpecializedServicesDetails["@value"]}; `;

    if (developmentalSummary !== "")
      summary += `Developmental History:\r\n${developmentalSummary.trimEnd()}\r\n\r\n`;

    //Education/Employment
    let educationSummary = "";
    if (data.SchoolName["@value"] && data.SchoolName["@value"] !== "")
      educationSummary += `School Name: ${data.SchoolName["@value"]}; `;

    if (data.CurrentGrade["@value"] && data.CurrentGrade["@value"] !== "")
      educationSummary += `Current Grade: ${data.CurrentGrade["@value"]}; `;

    if (
      data.IEPClassification["@value"] &&
      data.IEPClassification["@value"] !== ""
    )
      educationSummary += `IEP Classification: ${data.IEPClassification["@value"]}; `;

    if (
      data.SchoolExperience["@value"] &&
      data.SchoolExperience["@value"] !== ""
    )
      educationSummary += `Summary of Client's School Experience: ${data.SchoolExperience["@value"]}; `;

    if (
      data.EmployerAndTitle["@value"] &&
      data.EmployerAndTitle["@value"] !== ""
    )
      educationSummary += `Name of employer and title: ${data.EmployerAndTitle["@value"]}; `;

    if (
      data.EmploymentInterests["@value"] &&
      data.EmploymentInterests["@value"] !== ""
    )
      educationSummary += `Employment Interests/Skills/Concerns: ${data.EmploymentInterests["@value"]}; `;

    if (educationSummary !== "")
      summary += `Education/Employment:\r\n${educationSummary.trimEnd()}\r\n\r\n`;

    // Activites of Daily Living
    let dailyLivingSummary = "";
    if (
      data.MoneyManagementDetails["@value"] &&
      data.MoneyManagementDetails["@value"] !== ""
    )
      dailyLivingSummary += `Money management: ${data.MoneyManagementDetails["@value"]}; `;

    if (
      data.FoodManagementDetails["@value"] &&
      data.FoodManagementDetails["@value"] !== ""
    )
      dailyLivingSummary += `Food management: ${data.FoodManagementDetails["@value"]}; `;

    if (data.HygieneDetails["@value"] && data.HygieneDetails["@value"] !== "")
      dailyLivingSummary += `Personal appearance and hygiene: ${data.HygieneDetails["@value"]}; `;

    if (data.HealthDetails["@value"] && data.HealthDetails["@value"] !== "")
      dailyLivingSummary += `Health awareness/Personal Health: ${data.HealthDetails["@value"]}; `;

    if (
      data.HousekeepingDetails["@value"] &&
      data.HousekeepingDetails["@value"] !== ""
    )
      dailyLivingSummary += `Housekeeping: ${data.HousekeepingDetails["@value"]}; `;

    if (
      data.TransportationDetails["@value"] &&
      data.TransportationDetails["@value"] !== ""
    )
      dailyLivingSummary += `Transportation: ${data.TransportationDetails["@value"]}; `;

    if (
      data.SafetySkillsDetails["@value"] &&
      data.SafetySkillsDetails["@value"] !== ""
    )
      dailyLivingSummary += `Emergency and safety skills: ${data.SafetySkillsDetails["@value"]}; `;

    if (
      data.ResourceKnowledgeDetails["@value"] &&
      data.ResourceKnowledgeDetails["@value"] !== ""
    )
      dailyLivingSummary += `Knowledge of community resources: ${data.ResourceKnowledgeDetails["@value"]}; `;

    if (
      data.TimeManagementDetails["@value"] &&
      data.TimeManagementDetails["@value"] !== ""
    )
      dailyLivingSummary += `Time management: ${data.TimeManagementDetails["@value"]}; `;

    if (
      data.InteractionDetails["@value"] &&
      data.InteractionDetails["@value"] !== ""
    )
      dailyLivingSummary += `Interactions with Others: ${data.InteractionDetails["@value"]}; `;

    if (dailyLivingSummary !== "")
      summary += `Activities of Daily Living:\r\n${dailyLivingSummary.trimEnd()}\r\n\r\n`;

    // Friendship/Social Peer Support/Relationships
    let relationshipsSummary = "";
    if (
      data.RelationshipsSummary["@value"] &&
      data.RelationshipsSummary["@value"] !== ""
    )
      relationshipsSummary += `Friendship/Social Support/Relationships Summary Narrative: ${data.RelationshipsSummary["@value"]};`;

    if (relationshipsSummary !== "")
      summary += `${relationshipsSummary}\r\n\r\n`;

    // Legal History
    let legalSummary = "";
    if (
      data.LegalInvolvementDetails["@value"] &&
      data.LegalInvolvementDetails["@value"] !== ""
    )
      legalSummary += `Current involvement with the Legal System: ${data.LegalInvolvementDetails["@value"]}; `;

    if (
      data.CPSInvolvementDetails["@value"] &&
      data.CPSInvolvementDetails["@value"] !== ""
    )
      legalSummary += `Current Children's Protective Services (CPS) Involvement with Family: ${data.CPSInvolvementDetails["@value"]}; `;

    if (
      data.CPSCaseworkerName["@value"] &&
      data.CPSCaseworkerName["@value"] !== ""
    )
      legalSummary += `CPS Caseworker: ${data.CPSCaseworkerName["@value"]}; `;

    if (
      data.ProbationOfficerName["@value"] &&
      data.ProbationOfficerName["@value"] !== ""
    )
      legalSummary += `Probation/Parole Officer: ${data.ProbationOfficerName["@value"]}; `;

    if (
      data.OtherLegalDetails["@value"] &&
      data.OtherLegalDetails["@value"] !== ""
    )
      legalSummary += `Other Court/Custody Related Issues: ${data.OtherLegalDetails["@value"]}; `;

    if (legalSummary !== "")
      summary += `Legal History:\r\n${legalSummary.trimEnd()}\r\n\r\n`;

    // Medical History/Medications
    let medicalSummary = "";
    if (data.PCP["@value"] && data.PCP["@value"] !== "")
      if (data.PCPPhone["@value"] && data.PCPPhone["@value"] !== "")
        medicalSummary += `Primary Care Physician: ${data.PCP["@value"]}, Phone: ${data.PCPPhone["@value"]}; `;
      else medicalSummary += `Primary Care Physician: ${data.PCP["@value"]}; `;

    if (
      data.LastMedicalExamDate["@value"] &&
      data.LastMedicalExamDate["@value"] !== ""
    )
      medicalSummary += `Date of last medical exam: ${moment(
        data.LastMedicalExamDate["@value"]
      ).format("L")}; `;

    if (
      data.LastDentalExamDate["@value"] &&
      data.LastDentalExamDate["@value"] !== ""
    )
      medicalSummary += `Date of last dental exam: ${moment(
        data.LastDentalExamDate["@value"]
      ).format("L")}; `;

    if (
      data.AllergiesDetails["@value"] &&
      data.AllergiesDetails["@value"] !== ""
    )
      medicalSummary += `Allergies: ${data.AllergiesDetails["@value"]}; `;

    if (
      data.MedicalConditionsDetails["@value"] &&
      data.MedicalConditionsDetails["@value"] !== ""
    )
      medicalSummary += `Medical conditions/concerns: ${data.MedicalConditionsDetails["@value"]}; `;

    if (
      data.NutritionalConcernsDetails["@value"] &&
      data.NutritionalConcernsDetails["@value"] !== ""
    )
      medicalSummary += `Nutritional concerns: ${data.NutritionalConcernsDetails["@value"]}; `;

    if (
      data.DentalConcernsDetails["@value"] &&
      data.DentalConcernsDetails["@value"] !== ""
    )
      medicalSummary += `Dental concerns: ${data.DentalConcernsDetails["@value"]}; `;

    if (data.MedicalComments["@value"] && data.MedicalComments["@value"] !== "")
      medicalSummary += `Comments: ${data.MedicalComments["@value"]}; `;

    if (
      data.CurrentMedicationDetails["@value"] &&
      data.CurrentMedicationDetails["@value"] !== "" &&
      data.CurrentMedicationDetails["@value"] !== '[{"IsEditable": true}]'
    )
      medicalSummary += `Current Medications: ${JSON.parse(
        data.CurrentMedicationDetails["@value"]
      )
        .map((medication) => medication.Name)
        .join(", ")}; `;

    if (
      data.SideEffectsDetails["@value"] &&
      data.SideEffectsDetails["@value"] !== ""
    )
      medicalSummary += `Side effects from medications: ${data.SideEffectsDetails["@value"]}; `;

    if (
      data.PsychotropicMedsDetails["@value"] &&
      data.PsychotropicMedsDetails["@value"] !== ""
    )
      medicalSummary += `Past psychotropic nedications: ${data.PsychotropicMedsDetails["@value"]}; `;

    if (
      data.MedicationComments["@value"] &&
      data.MedicationComments["@value"] !== ""
    )
      medicalSummary += `Comments: ${data.MedicationComments["@value"]}; `;

    if (medicalSummary !== "")
      summary += `Medical History/Medications:\r\n${medicalSummary.trimEnd()}\r\n\r\n`;

    // Sexual History to Include Pertinent Sexual Issues/Concerns
    let sexualSummary = "";
    if (data.GenderIdentity["@value"] && data.GenderIdentity["@value"] !== "")
      sexualSummary += `Gender identity: ${data.GenderIdentity["@value"]}; `;

    if (
      data.SexualOrientation["@value"] &&
      data.SexualOrientation["@value"] !== ""
    )
      sexualSummary += `Sexual orientation: ${data.SexualOrientation["@value"]}; `;

    if (data.SexualBehavior["@value"] && data.SexualBehavior["@value"] !== "")
      sexualSummary += `Current or historical sexual behavior: ${data.SexualBehavior["@value"]}; `;

    if (data.SexualAbuse["@value"] && data.SexualAbuse["@value"] !== "")
      sexualSummary += `Current or history of sexual abuse or perpetration: ${data.SexualAbuse["@value"]}; `;

    if (
      data.SexualHistoryComments["@value"] &&
      data.SexualHistoryComments["@value"] !== ""
    )
      sexualSummary += `Comments: ${data.SexualHistoryComments["@value"]}; `;

    if (sexualSummary !== "")
      summary += `Sexual History to Include Pertinent Sexual Issues/Concerns:\r\n${sexualSummary.trimEnd()}\r\n\r\n`;

    // Mental Health Treatment History
    let mentalHealthSummary = "";
    if (
      data.PastMentalHealthDiagnosis["@value"] &&
      data.PastMentalHealthDiagnosis["@value"] !== ""
    )
      mentalHealthSummary += `Previous mental health diagnosis: ${data.PastMentalHealthDiagnosis["@value"]}; `;

    if (
      data.MentalHealthComments["@value"] &&
      data.MentalHealthComments["@value"] !== ""
    )
      mentalHealthSummary += `Comments: ${data.MentalHealthComments["@value"]}; `;

    if (
      data.PerspectiveOfTreatment["@value"] &&
      data.PerspectiveOfTreatment["@value"] !== ""
    )
      mentalHealthSummary += `Client and caregiver perspective of treatment/counseling: ${data.PerspectiveOfTreatment["@value"]}; `;

    if (mentalHealthSummary !== "")
      summary += `Mental Health Treatment History:\r\n${mentalHealthSummary.trimEnd()}\r\n\r\n`;

    // Alcohol/Drug Use History
    let substanceUseSummary = "";
    if (
      data.PastSubstanceUse["@value"] &&
      data.PastSubstanceUse["@value"] !== ""
    )
      substanceUseSummary += `History of past drug or alcohol use: ${data.PastSubstanceUse["@value"]}; `;

    if (
      data.SubstanceUseHistory["@value"] &&
      data.SubstanceUseHistory["@value"] !== "" &&
      data.SubstanceUseHistory["@value"] !== '[{"IsEditable": true}]'
    )
      substanceUseSummary += `Current drug or alcohol use: ${JSON.parse(
        data.SubstanceUseHistory["@value"]
      )
        .map(
          (item) =>
            `Substance: ${item.Substance}, First Use: ${item.FirstUse}, Last Use: ${item.LastUse}, Frequency: ${item.Frequency}, Amount: ${item.Amount}, Method: ${item.Method}`
        )
        .join("; ")}; `;

    if (
      data.PerspectiveOfSubstanceUse["@value"] &&
      data.PerspectiveOfSubstanceUse["@value"] !== ""
    )
      substanceUseSummary += `Client and caregiver perception of substance use: ${data.PerspectiveOfSubstanceUse["@value"]}; `;

    if (
      data.SubstanceUseComments["@value"] &&
      data.SubstanceUseComments["@value"] !== ""
    )
      substanceUseSummary += `Comments: ${data.SubstanceUseComments["@value"]}; `;

    if (
      data.SubstanceUseTreatmentHistory["@value"] &&
      data.SubstanceUseTreatmentHistory["@value"] !== "" &&
      data.SubstanceUseTreatmentHistory["@value"] !== '[{"IsEditable": true}]'
    )
      substanceUseSummary += `Alcohol/Drug Treatment History: ${JSON.parse(
        data.SubstanceUseTreatmentHistory["@value"]
      )
        .map((item) => `Agency: ${item.Agency}, Dates: ${item.Dates}`)
        .join("; ")}; `;

    if (
      data.PerspectiveOfSubstanceUseTreatment["@value"] &&
      data.PerspectiveOfSubstanceUseTreatment["@value"] !== ""
    )
      substanceUseSummary += `Client and caregiver perspective of treatment: ${data.PerspectiveOfSubstanceUseTreatment["@value"]}; `;

    if (
      data.SubstanceUseTreatmentComments["@value"] &&
      data.SubstanceUseTreatmentComments["@value"] !== ""
    )
      substanceUseSummary += `Comments: ${data.SubstanceUseTreatmentComments["@value"]}; `;

    if (substanceUseSummary !== "")
      summary += `Alcohol/Drug Use History:\r\n${substanceUseSummary.trimEnd()}\r\n\r\n`;

    // Trauma History
    let traumaSummary = "";
    if (
      data.TraumaHistory["@value"] &&
      data.TraumaHistory["@value"] !== "" &&
      data.TraumaHistory["@value"] !== '[{"IsEditable": true}]'
    )
      traumaSummary += `${JSON.parse(data.TraumaHistory["@value"])
        .map((item) => `${item.Trauma}, ${item.Status}, ${item.Comments}`)
        .join(". ")}; `;

    if (
      data.CopingStrategies["@value"] &&
      data.CopingStrategies["@value"] !== ""
    )
      traumaSummary += `Coping strategies: ${data.CopingStrategies["@value"]}; `;

    if (data.TraumaSummary["@value"] && data.TraumaSummary["@value"] !== "")
      traumaSummary += `Trauma Impact Summary: ${data.TraumaSummary["@value"]}; `;

    if (traumaSummary !== "")
      summary += `Trauma History:\r\n${traumaSummary.trimEnd()}\r\n\r\n`;

    // Mental Status
    let mentalStatusSummary = "";
    let appearanceSummary = "";
    let behaviorSummary = "";
    let speechSummary = "";
    let moodSummary = "";
    let affectSummary = "";
    let perceptionSummary = "";
    let cognitionSummary = "";
    let insightSummary = "";

    if (
      data.AppropriateAppearance["@value"] &&
      data.AppropriateAppearance["@value"] === "true"
    )
      appearanceSummary += "Appropriate, ";

    if (data.Neat["@value"] && data.Neat["@value"] === "true")
      appearanceSummary += "Neat, ";

    if (data.Disheveled["@value"] && data.Disheveled["@value"] === "true")
      appearanceSummary += "Disheveled, ";

    if (
      data.BizarreAppearance["@value"] &&
      data.BizarreAppearance["@value"] === "true"
    )
      appearanceSummary += "Bizarre, ";

    if (
      data.OtherAppearance["@value"] &&
      data.OtherAppearance["@value"] === "true" &&
      data.OtherAppearanceDetails["@value"] &&
      data.OtherAppearanceDetails["@value"] !== ""
    )
      appearanceSummary += `Other: ${data.OtherAppearanceDetails["@value"]}, `;

    if (appearanceSummary !== "")
      mentalStatusSummary += `\r\nAppearance: ${appearanceSummary.slice(
        0,
        -2
      )}; `;

    if (data.Cooperative["@value"] && data.Cooperative["@value"] === "true")
      behaviorSummary += "Cooperative, ";

    if (data.Guarded["@value"] && data.Guarded["@value"] === "true")
      behaviorSummary += "Guarded, ";

    if (data.Hyperactive["@value"] && data.Hyperactive["@value"] === "true")
      behaviorSummary += "Hyperactive, ";

    if (data.Agitated["@value"] && data.Agitated["@value"] === "true")
      behaviorSummary += "Agitated, ";

    if (data.Paranoid["@value"] && data.Paranoid["@value"] === "true")
      behaviorSummary += "Paranoid, ";

    if (data.Tics["@value"] && data.Tics["@value"] === "true")
      behaviorSummary += "Tics, ";

    if (data.Impulsive["@value"] && data.Impulsive["@value"] === "true")
      behaviorSummary += "Impulsive, ";

    if (data.Aggressive["@value"] && data.Aggressive["@value"] === "true")
      behaviorSummary += "Aggressive, ";

    if (data.Withdrawn["@value"] && data.Withdrawn["@value"] === "true")
      behaviorSummary += "Withdrawn, ";

    if (
      data.BizarreBehavior["@value"] &&
      data.BizarreBehavior["@value"] === "true"
    )
      behaviorSummary += "Bizarre, ";

    if (data.Rigidity["@value"] && data.Rigidity["@value"] === "true")
      behaviorSummary += "Rigidity, ";

    if (
      data.OtherBehavior["@value"] &&
      data.OtherBehavior["@value"] === "true" &&
      data.OtherBehaviorDetails["@value"] &&
      data.OtherBehaviorDetails["@value"] !== ""
    )
      behaviorSummary += `Other: ${data.OtherBehaviorDetails["@value"]}, `;

    if (behaviorSummary !== "")
      mentalStatusSummary += `\r\nBehavior: ${behaviorSummary.slice(0, -2)}; `;

    if (data.Normal["@value"] && data.Normal["@value"] === "true")
      speechSummary += "Normal, ";

    if (data.Slurred["@value"] && data.Slurred["@value"] === "true")
      speechSummary += "Slurred, ";

    if (data.Pressured["@value"] && data.Pressured["@value"] === "true")
      speechSummary += "Pressured, ";

    if (data.Tangential["@value"] && data.Tangential["@value"] === "true")
      speechSummary += "Tangential, ";

    if (data.Incoherent["@value"] && data.Incoherent["@value"] === "true")
      speechSummary += "Incoherent, ";

    if (data.Stutters["@value"] && data.Stutters["@value"] === "true")
      speechSummary += "Stutters, ";

    if (
      data.OtherSpeech["@value"] &&
      data.OtherSpeech["@value"] === "true" &&
      data.OtherSpeechDetails["@value"] &&
      data.OtherSpeechDetails["@value"] !== ""
    )
      speechSummary += `Other: ${data.OtherSpeechDetails["@value"]}, `;

    if (speechSummary !== "")
      mentalStatusSummary += `\r\nSpeech: ${speechSummary.slice(0, -2)}; `;

    if (data.Euthymic["@value"] && data.Euthymic["@value"] === "true")
      moodSummary += "Euthymic, ";

    if (data.Anxious["@value"] && data.Anxious["@value"] === "true")
      moodSummary += "Anxious, ";

    if (data.Angry["@value"] && data.Angry["@value"] === "true")
      moodSummary += "Angry, ";

    if (data.Depressed["@value"] && data.Depressed["@value"] === "true")
      moodSummary += "Depressed, ";

    if (data.Euphoric["@value"] && data.Euphoric["@value"] === "true")
      moodSummary += "Euphoric, ";

    if (data.Irritable["@value"] && data.Irritable["@value"] === "true")
      moodSummary += "Irritable, ";

    if (data.Fearful["@value"] && data.Fearful["@value"] === "true")
      moodSummary += "Fearful, ";

    if (
      data.OtherMood["@value"] &&
      data.OtherMood["@value"] === "true" &&
      data.OtherMoodDetails["@value"] &&
      data.OtherMoodDetails["@value"] !== ""
    )
      moodSummary += `Other: ${data.OtherMoodDetails["@value"]}, `;

    if (moodSummary !== "")
      mentalStatusSummary += `\r\nMood: ${moodSummary.slice(0, -2)}; `;

    if (data.FullRange["@value"] && data.FullRange["@value"] === "true")
      affectSummary += "Full range, ";

    if (data.Restricted["@value"] && data.Restricted["@value"] === "true")
      affectSummary += "Restricted, ";

    if (data.Blunted["@value"] && data.Blunted["@value"] === "true")
      affectSummary += "Blunted, ";

    if (data.Flat["@value"] && data.Flat["@value"] === "true")
      affectSummary += "Flat, ";

    if (data.Inappropriate["@value"] && data.Inappropriate["@value"] === "true")
      affectSummary += "Inappropriate, ";

    if (
      data.OtherAffect["@value"] &&
      data.OtherAffect["@value"] === "true" &&
      data.OtherAffectDetails["@value"] &&
      data.OtherAffectDetails["@value"] !== ""
    )
      affectSummary += `Other: ${data.OtherAffectDetails["@value"]}, `;

    if (affectSummary !== "")
      mentalStatusSummary += `\r\nAffect: ${affectSummary.slice(0, -2)}; `;

    if (data.Logical["@value"] && data.Logical["@value"] === "true")
      perceptionSummary += "Logical, ";

    if (
      data.Preoccupations["@value"] &&
      data.Preoccupations["@value"] === "true"
    )
      perceptionSummary += "Preoccupations, ";

    if (data.Delusions["@value"] && data.Delusions["@value"] === "true")
      perceptionSummary += "Delusions, ";

    if (
      data.Hallucinations["@value"] &&
      data.Hallucinations["@value"] === "true"
    )
      perceptionSummary += "Hallucinations, ";

    if (data.Racing["@value"] && data.Racing["@value"] === "true")
      perceptionSummary += "Racing, ";

    if (data.Disorganized["@value"] && data.Disorganized["@value"] === "true")
      perceptionSummary += "Disorganized, ";

    if (data.Concrete["@value"] && data.Concrete["@value"] === "true")
      perceptionSummary += "Concrete, ";

    if (
      data.SuicidalIdeation["@value"] &&
      data.SuicidalIdeation["@value"] === "true"
    )
      perceptionSummary += "Suicidal Ideation, ";

    if (
      data.HomicidalIdeation["@value"] &&
      data.HomicidalIdeation["@value"] === "true"
    )
      perceptionSummary += "Homicidal Ideation, ";

    if (
      data.OtherPerception["@value"] &&
      data.OtherPerception["@value"] === "true" &&
      data.OtherPerceptionDetails["@value"] &&
      data.OtherPerceptionDetails["@value"] !== ""
    )
      perceptionSummary += `Other: ${data.OtherPerceptionDetails["@value"]}, `;

    if (perceptionSummary !== "")
      mentalStatusSummary += `\r\nThought Process/Perception: ${perceptionSummary.slice(
        0,
        -2
      )}; `;

    if (data.NoImpairment["@value"] && data.NoImpairment["@value"] === "true")
      cognitionSummary += "No Impairment, ";

    if (
      data.ShortAttentionSpan["@value"] &&
      data.ShortAttentionSpan["@value"] === "true"
    )
      cognitionSummary += "Short Attention Span, ";

    if (
      data.DifficultyConcentrating["@value"] &&
      data.DifficultyConcentrating["@value"] === "true"
    )
      cognitionSummary += "Difficulty Concentrating, ";

    if (
      data.Distractibility["@value"] &&
      data.Distractibility["@value"] === "true"
    )
      cognitionSummary += "Distractibility, ";

    if (
      data.MemoryImpairment["@value"] &&
      data.MemoryImpairment["@value"] === "true"
    )
      cognitionSummary += "Memory Impairment, ";

    if (
      data.LowIntelligence["@value"] &&
      data.LowIntelligence["@value"] === "true"
    )
      cognitionSummary += "Low Intelligence, ";

    if (
      data.OtherCognition["@value"] &&
      data.OtherCognition["@value"] === "true" &&
      data.OtherCognitionDetails["@value"] &&
      data.OtherCognitionDetails["@value"] !== ""
    )
      cognitionSummary += `Other: ${data.OtherCognitionDetails["@value"]}, `;

    if (cognitionSummary !== "")
      mentalStatusSummary += `\r\nCognition/Intellectual Functioning: ${cognitionSummary.slice(
        0,
        -2
      )}; `;

    if (
      data.AppropriateInsight["@value"] &&
      data.AppropriateInsight["@value"] === "true"
    )
      insightSummary += "Appropriate, ";

    if (data.Fair["@value"] && data.Fair["@value"] === "true")
      insightSummary += "Fair, ";

    if (data.Impaired["@value"] && data.Impaired["@value"] === "true")
      insightSummary += "Impaired, ";

    if (
      data.OtherInsight["@value"] &&
      data.OtherInsight["@value"] === "true" &&
      data.OtherInsightDetails["@value"] &&
      data.OtherInsightDetails["@value"] !== ""
    )
      insightSummary += `Other: ${data.OtherInsightDetails["@value"]}, `;

    if (insightSummary !== "")
      mentalStatusSummary += `\r\nInsight/Judgment: ${insightSummary.slice(
        0,
        -2
      )}; `;

    if (
      data.MentalStatusComments["@value"] &&
      data.MentalStatusComments["@value"] !== ""
    )
      mentalStatusSummary += `\r\nComments/Observations: ${data.MentalStatusComments["@value"]}; `;

    if (mentalStatusSummary !== "")
      summary += `Mental Status:${mentalStatusSummary.trimEnd()}\r\n\r\n`;

    // Lethality
    let lethalitySummary = "";
    if (
      data.SelfAbusiveBehaviorDetails["@value"] &&
      data.SelfAbusiveBehaviorDetails["@value"] !== ""
    )
      lethalitySummary += `Self-injurious or self-abusive behavior: ${data.SelfAbusiveBehaviorDetails["@value"]}; `;

    if (
      data.SuicidalThreatsDetails["@value"] &&
      data.SuicidalThreatsDetails["@value"] !== ""
    )
      lethalitySummary += `Suicidal threats/gestures: ${data.SuicidalThreatsDetails["@value"]}; `;

    if (
      data.SuicideAttemptDetails["@value"] &&
      data.SuicideAttemptDetails["@value"] !== ""
    )
      lethalitySummary += `Suicide attempt: ${data.SuicideAttemptDetails["@value"]}; `;

    const riskFactors = getSelfHarmRiskFactors();

    if (riskFactors !== "") {
      lethalitySummary += `Risk Factors: ${riskFactors}`;

      if (
        data.RiskFactorsComments["@value"] &&
        data.RiskFactorsComments["@value"] !== ""
      )
        lethalitySummary += `, Comments: ${data.RiskFactorsComments["@value"]}; `;
      else lethalitySummary += "; ";
    }

    const selfHarmProtectiveFactors = getSelfHarmProtectiveFactors();

    if (selfHarmProtectiveFactors !== "") {
      lethalitySummary += `Protective Factors: ${selfHarmProtectiveFactors}`;
      if (
        data.ProtectiveFactorsComments["@value"] &&
        data.ProtectiveFactorsComments["@value"] !== ""
      )
        lethalitySummary += `, Comments: ${data.ProtectiveFactorsComments["@value"]}; `;
      else lethalitySummary += "; ";
    }

    const warningSigns = getSelfHarmWarningSigns();

    if (warningSigns !== "") {
      lethalitySummary += `Current Warning Signs: ${warningSigns}`;

      if (
        data.WarningSignsComments["@value"] &&
        data.WarningSignsComments["@value"] !== ""
      )
        lethalitySummary += `, Comments: ${data.WarningSignsComments["@value"]}; `;
      else lethalitySummary += "; ";
    }

    if (
      data.CurrentSelfHarmRisk["@value"] &&
      data.CurrentSelfHarmRisk["@value"] !== ""
    ) {
      const selfHarmRisk = riskOptions.filter(
        (option) => option._id === data.CurrentSelfHarmRisk["@value"]
      )[0];
      lethalitySummary += `Current Risk of Harm to Self: ${selfHarmRisk.name}; `;

      if (
        data.CurrentSelfHarmRiskComments["@value"] &&
        data.CurrentSelfHarmRiskComments["@value"] !== ""
      )
        lethalitySummary += `Comments/Plan: ${data.CurrentSelfHarmRiskComments["@value"]}; `;
    }

    if (
      data.ThreatsToHarmOthersDetails["@value"] &&
      data.ThreatsToHarmOthersDetails["@value"] !== ""
    )
      lethalitySummary += `Threats to harm/endanger others: ${data.ThreatsToHarmOthersDetails["@value"]}; `;

    if (
      data.ThoughtsOfHarmDetails["@value"] &&
      data.ThoughtsOfHarmDetails["@value"] !== ""
    )
      lethalitySummary += `Thoughts of harming/endangering others: ${data.ThoughtsOfHarmDetails["@value"]}; `;

    if (
      data.ActsOfHarmDetails["@value"] &&
      data.ActsOfHarmDetails["@value"] !== ""
    )
      lethalitySummary += `Acts of harm/endangering others: ${data.ActsOfHarmDetails["@value"]}; `;

    if (
      data.ActsOfHarmAnimalsDetails["@value"] &&
      data.ActsOfHarmAnimalsDetails["@value"] !== ""
    )
      lethalitySummary += `Acts of harm/endangering animals: ${data.ActsOfHarmAnimalsDetails["@value"]}; `;

    if (
      data.ActsInvolvingWeaponsDetails["@value"] &&
      data.ActsInvolvingWeaponsDetails["@value"] !== ""
    )
      lethalitySummary += `Acts involving weapons: ${data.ActsInvolvingWeaponsDetails["@value"]}; `;

    if (
      data.CurrentOtherHarmRisk["@value"] &&
      data.CurrentOtherHarmRisk["@value"] !== ""
    ) {
      const othersHarmRisk = riskOptions.filter(
        (option) => option._id === data.CurrentOtherHarmRisk["@value"]
      )[0];
      lethalitySummary += `Current Risk of Harm to Others: ${othersHarmRisk.name}; `;

      if (
        data.CurrentOtherHarmRiskDetails["@value"] &&
        data.CurrentOtherHarmRiskDetails["@value"] !== ""
      )
        lethalitySummary += `Comments/Plan: ${data.CurrentOtherHarmRiskDetails["@value"]}; `;
    }

    if (lethalitySummary !== "")
      summary += `Lethality:\r\n${lethalitySummary.trimEnd()}\r\n\r\n`;

    // Symptom Checklist
    let symptomSummary = "";
    if (moment(formsyDetails.CreatedOn).isBefore(moment("3/29/2022"))) {
      if (
        data.DepressionDetails["@value"] &&
        data.DepressionDetails["@value"] !== ""
      )
        symptomSummary += `Depression: ${data.DepressionDetails["@value"]}; `;

      if (data.ManiaDetails["@value"] && data.ManiaDetails["@value"] !== "")
        symptomSummary += `Mania: ${data.ManiaDetails["@value"]}; `;

      if (data.BipolarDetails["@value"] && data.BipolarDetails["@value"] !== "")
        symptomSummary += `Bipolar: ${data.BipolarDetails["@value"]}; `;

      if (
        data.ObsessiveCompulsiveDetails["@value"] &&
        data.ObsessiveCompulsiveDetails["@value"] !== ""
      )
        symptomSummary += `Obsessive compulsive: ${data.ObsessiveCompulsiveDetails["@value"]}; `;

      if (
        data.PsychosisDetails["@value"] &&
        data.PsychosisDetails["@value"] !== ""
      )
        symptomSummary += `Psychosis: ${data.PsychosisDetails["@value"]}; `;

      if (data.PTSDDetails["@value"] && data.PTSDDetails["@value"] !== "")
        symptomSummary += `PTSD: ${data.PTSDDetails["@value"]}; `;

      if (
        data.PanicAttacksDetails["@value"] &&
        data.PanicAttacksDetails["@value"] !== ""
      )
        symptomSummary += `Panic Attacks: ${data.PanicAttacksDetails["@value"]}; `;

      if (data.AnxietyDetails["@value"] && data.AnxietyDetails["@value"] !== "")
        symptomSummary += `Anxiety: ${data.AnxietyDetails["@value"]}; `;

      if (data.PhobiaDetails["@value"] && data.PhobiaDetails["@value"] !== "")
        symptomSummary += `Phobia: ${data.PhobiaDetails["@value"]}; `;

      if (
        data.EatingDisorderDetails["@value"] &&
        data.EatingDisorderDetails["@value"] !== ""
      )
        symptomSummary += `Eating Disorder: ${data.EatingDisorderDetails["@value"]}; `;

      if (
        data.EliminationDetails["@value"] &&
        data.EliminationDetails["@value"] !== ""
      )
        symptomSummary += `Elimination: ${data.EliminationDetails["@value"]}; `;

      if (data.ConductDetails["@value"] && data.ConductDetails["@value"] !== "")
        symptomSummary += `Conduct: ${data.ConductDetails["@value"]}; `;

      if (
        data.OppositionalDetails["@value"] &&
        data.OppositionalDetails["@value"] !== ""
      )
        symptomSummary += `Oppositional Defiance: ${data.OppositionalDetails["@value"]}; `;

      if (
        data.AttentionDetails["@value"] &&
        data.AttentionDetails["@value"] !== ""
      )
        symptomSummary += `Attention: ${data.AttentionDetails["@value"]}; `;

      if (
        data.AdjustmentDetails["@value"] &&
        data.AdjustmentDetails["@value"] !== ""
      )
        symptomSummary += `Adjustment: ${data.AdjustmentDetails["@value"]}; `;

      if (
        data.AttachmentDetails["@value"] &&
        data.AttachmentDetails["@value"] !== ""
      )
        symptomSummary += `Attachment: ${data.AttachmentDetails["@value"]}; `;

      if (
        data.DevelopmentalImpairmentsDetails["@value"] &&
        data.DevelopmentalImpairmentsDetails["@value"] !== ""
      )
        symptomSummary += `Developmental Impairments: ${data.DevelopmentalImpairmentsDetails["@value"]}; `;

      if (
        data.SubstanceUseDetails["@value"] &&
        data.SubstanceUseDetails["@value"] !== ""
      )
        symptomSummary += `Substance Use: ${data.SubstanceUseDetails["@value"]}; `;

      if (
        data.OtherSymptomDetails["@value"] &&
        data.OtherSymptomDetails["@value"] !== ""
      )
        symptomSummary += `Other symptom(s): ${data.OtherSymptomDetails["@value"]}; `;
    } else {
      if (
        data.TraumaSymptoms !== undefined &&
        data.TraumaSymptoms["@value"] &&
        data.TraumaSymptoms["@value"] === "true"
      ) {
        let traumaSymptoms =
          "Symptoms of Trauma and Other Stressor-Related Disorders: ";

        if (
          data.TraumaticExposure !== undefined &&
          data.TraumaticExposure["@value"] &&
          data.TraumaticExposure["@value"] === "true"
        )
          traumaSymptoms += "exposure to a traumatic event, ";

        if (
          data.AvoidantTrauma !== undefined &&
          data.AvoidantTrauma["@value"] &&
          data.AvoidantTrauma["@value"] === "true"
        )
          traumaSymptoms += "persistent avoidant behavior, ";

        if (
          data.HypervigilanceTrauma !== undefined &&
          data.HypervigilanceTrauma["@value"] &&
          data.HypervigilanceTrauma["@value"] === "true"
        )
          traumaSymptoms += "hypervigilance, ";

        if (
          data.IntrusiveDreams !== undefined &&
          data.IntrusiveDreams["@value"] &&
          data.IntrusiveDreams["@value"] === "true"
        )
          traumaSymptoms += "intrusive dreams/memories, ";

        if (
          data.NegativeThoughts !== undefined &&
          data.NegativeThoughts["@value"] &&
          data.NegativeThoughts["@value"] === "true"
        )
          traumaSymptoms += "negative thoughts and mood, ";

        if (
          data.CocentrationProblems !== undefined &&
          data.CocentrationProblems["@value"] &&
          data.CocentrationProblems["@value"] === "true"
        )
          traumaSymptoms += "problems with concentration, ";

        if (
          data.RecklessBehaviorTrauma !== undefined &&
          data.RecklessBehaviorTrauma["@value"] &&
          data.RecklessBehaviorTrauma["@value"] === "true"
        )
          traumaSymptoms += "irritable or reckless behavior, ";

        if (
          data.SleepDisturbancesTrauma !== undefined &&
          data.SleepDisturbancesTrauma["@value"] &&
          data.SleepDisturbancesTrauma["@value"] === "true"
        )
          traumaSymptoms += "sleep disturbances, ";

        if (
          data.OtherTrauma !== undefined &&
          data.OtherTraumaDetails !== undefined &&
          data.OtherTrauma["@value"] &&
          data.OtherTrauma["@value"] === "true" &&
          data.OtherTraumaDetails["@value"] &&
          data.OtherTraumaDetails["@value"] !== ""
        )
          traumaSymptoms += `other Trauma symptom(s) - ${data.OtherTraumaDetails["@value"]}, `;

        if (
          data.TraumaSymptomsFrequency !== undefined &&
          data.TraumaSymptomsFrequency["@value"] &&
          data.TraumaSymptomsFrequency["@value"] !== ""
        )
          traumaSymptoms += `frequency - ${data.TraumaSymptomsFrequency["@value"]}, `;

        if (
          data.TraumaSymptomsIntensity !== undefined &&
          data.TraumaSymptomsIntensity["@value"] &&
          data.TraumaSymptomsIntensity["@value"] !== ""
        )
          traumaSymptoms += `intensity - ${data.TraumaSymptomsIntensity["@value"]}, `;

        if (
          data.TraumaSymptomsDuration !== undefined &&
          data.TraumaSymptomsDuration["@value"] &&
          data.TraumaSymptomsDuration["@value"] !== ""
        )
          traumaSymptoms += `duration - ${data.TraumaSymptomsDuration["@value"]}, `;

        if (
          data.TraumaSymptomsOnset !== undefined &&
          data.TraumaSymptomsOnset["@value"] &&
          data.TraumaSymptomsOnset["@value"] !== ""
        )
          traumaSymptoms += `onset - ${data.TraumaSymptomsOnset["@value"]}, `;

        console.log("traumaSymptoms: ", traumaSymptoms);
        symptomSummary += `${traumaSymptoms.slice(0, -2)}.\r\n`;
      }
      if (
        data.AnxietyDisorder !== undefined &&
        data.AnxietyDisorder["@value"] &&
        data.AnxietyDisorder["@value"] === "true"
      ) {
        let anxietySymptoms = "Symptoms of Anxiety: ";

        if (
          data.AnxietyDisorderExcessWorry !== undefined &&
          data.AnxietyDisorderExcessWorry["@value"] &&
          data.AnxietyDisorderExcessWorry["@value"] === "true"
        )
          anxietySymptoms += "excess worry, ";

        if (
          data.AnxietyDisorderEasilyFatigued !== undefined &&
          data.AnxietyDisorderEasilyFatigued["@value"] &&
          data.AnxietyDisorderEasilyFatigued["@value"] === "true"
        )
          anxietySymptoms += "easily fatigued, ";

        if (
          data.MuscleTension !== undefined &&
          data.MuscleTension["@value"] &&
          data.MuscleTension["@value"] === "true"
        )
          anxietySymptoms += "muscle tension, ";

        if (
          data.Overworried !== undefined &&
          data.Overworried["@value"] &&
          data.Overworried["@value"] === "true"
        )
          anxietySymptoms += "difficulty controlling worry, ";

        if (
          data.AnxietyDisorderDifficultyConcentrating !== undefined &&
          data.AnxietyDisorderDifficultyConcentrating["@value"] &&
          data.AnxietyDisorderDifficultyConcentrating["@value"] === "true"
        )
          anxietySymptoms += "difficulty concentrating, ";

        if (
          data.SleepDisturbancesAnxiety !== undefined &&
          data.SleepDisturbancesAnxiety["@value"] &&
          data.SleepDisturbancesAnxiety["@value"] === "true"
        )
          anxietySymptoms += "sleep difficulties, ";

        if (
          data.RestlessAnxiety !== undefined &&
          data.RestlessAnxiety["@value"] &&
          data.RestlessAnxiety["@value"] === "true"
        )
          anxietySymptoms += "restless, ";

        if (
          data.Irritability !== undefined &&
          data.Irritability["@value"] &&
          data.Irritability["@value"] === "true"
        )
          anxietySymptoms += "irritability, ";

        if (
          data.OtherAnxiety !== undefined &&
          data.OtherAnxietyDetails !== undefined &&
          data.OtherAnxiety["@value"] &&
          data.OtherAnxiety["@value"] === "true" &&
          data.OtherAnxietyDetails["@value"] &&
          data.OtherAnxietyDetails["@value"] !== ""
        )
          anxietySymptoms += `other Anxiety Disorder symptom(s) - ${data.OtherAnxietyDetails["@value"]}, `;

        if (
          data.AnxietyDisorderFrequency !== undefined &&
          data.AnxietyDisorderFrequency["@value"] &&
          data.AnxietyDisorderFrequency["@value"] !== ""
        )
          anxietySymptoms += `frequency - ${data.AnxietyDisorderFrequency["@value"]}, `;

        if (
          data.AnxietyDisorderIntensity !== undefined &&
          data.AnxietyDisorderIntensity["@value"] &&
          data.AnxietyDisorderIntensity["@value"] !== ""
        )
          anxietySymptoms += `intensity - ${data.AnxietyDisorderIntensity["@value"]}, `;

        if (
          data.AnxietyDisorderDuration !== undefined &&
          data.AnxietyDisorderDuration["@value"] &&
          data.AnxietyDisorderDuration["@value"] !== ""
        )
          anxietySymptoms += `duration - ${data.AnxietyDisorderDuration["@value"]}, `;

        console.log("anxietySymptoms: ", anxietySymptoms);
        symptomSummary += `${anxietySymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.DepressionSymptoms !== undefined &&
        data.DepressionSymptoms["@value"] &&
        data.DepressionSymptoms["@value"] === "true"
      ) {
        let depressionSymptoms = "Symptoms of Depression: ";

        if (
          data.DepressedMood !== undefined &&
          data.DepressedMood["@value"] &&
          data.DepressedMood["@value"] === "true"
        )
          depressionSymptoms +=
            "depressed mood most of the day, nearly every day, ";

        if (
          data.SleepChanges !== undefined &&
          data.SleepChanges["@value"] &&
          data.SleepChanges["@value"] === "true"
        )
          depressionSymptoms += "insomnia or hypersomnia, ";

        if (data.Guilt["@value"] && data.Guilt["@value"] === "true")
          depressionSymptoms += "feelings of worthlessness/guilt, ";

        if (
          data.DiminishedInterest !== undefined &&
          data.DiminishedInterest["@value"] &&
          data.DiminishedInterest["@value"] === "true"
        )
          depressionSymptoms +=
            "diminished interest or pleasure in most daily activities, ";

        if (
          data.PurposelessMovement !== undefined &&
          data.PurposelessMovement["@value"] &&
          data.PurposelessMovement["@value"] === "true"
        )
          depressionSymptoms += "purposeless movement, ";

        if (
          data.DifficultyConcentratingDepression !== undefined &&
          data.DifficultyConcentratingDepression["@value"] &&
          data.DifficultyConcentratingDepression["@value"] === "true"
        )
          depressionSymptoms += "inability to think/concentrate, ";

        if (
          data.WeightChanges !== undefined &&
          data.WeightChanges["@value"] &&
          data.WeightChanges["@value"] === "true"
        )
          depressionSymptoms += "significant weight loss or weight gain, ";

        if (
          data.FatigueDepression !== undefined &&
          data.FatigueDepression["@value"] &&
          data.FatigueDepression["@value"] === "true"
        )
          depressionSymptoms += "fatigue, ";

        if (
          data.ThoughtsOfDeath !== undefined &&
          data.ThoughtsOfDeath["@value"] &&
          data.ThoughtsOfDeath["@value"] === "true"
        )
          depressionSymptoms += "recurrent thoughts of death, ";

        if (
          data.OtherDepression !== undefined &&
          data.OtherDepressionDetails !== undefined &&
          data.OtherDepression["@value"] &&
          data.OtherDepression["@value"] === "true" &&
          data.OtherDepressionDetails["@value"] &&
          data.OtherDepressionDetails["@value"] !== ""
        )
          depressionSymptoms += `other Depression symptom(s) - ${data.OtherDepressionDetails["@value"]}, `;

        if (
          data.DepressionFrequency !== undefined &&
          data.DepressionFrequency["@value"] &&
          data.DepressionFrequency["@value"] !== ""
        )
          depressionSymptoms += `frequency - ${data.DepressionFrequency["@value"]}, `;

        if (
          data.DepressionIntensity !== undefined &&
          data.DepressionIntensity["@value"] &&
          data.DepressionIntensity["@value"] !== ""
        )
          depressionSymptoms += `intensity - ${data.DepressionIntensity["@value"]}, `;

        if (
          data.DepressionDuration !== undefined &&
          data.DepressionDuration["@value"] &&
          data.DepressionDuration["@value"] !== ""
        )
          depressionSymptoms += `duration - ${data.DepressionDuration["@value"]}, `;

        console.log("depressionSymptoms: ", depressionSymptoms);
        symptomSummary += `${depressionSymptoms.slice(0, -2)}..\r\n`;
      }

      if (
        data.AttentionHyperactivity !== undefined &&
        data.AttentionHyperactivity["@value"] &&
        data.AttentionHyperactivity["@value"] === "true"
      ) {
        let attentionSymptoms = "Symptoms of Attention and Hyperactivity: ";

        if (
          data.AttentionHyperactivityEasilyDistracted !== undefined &&
          data.AttentionHyperactivityEasilyDistracted["@value"] &&
          data.AttentionHyperactivityEasilyDistracted["@value"] === "true"
        )
          attentionSymptoms += "easily distracted, ";

        if (
          data.DifficultyFollowThrough !== undefined &&
          data.DifficultyFollowThrough["@value"] &&
          data.DifficultyFollowThrough["@value"] === "true"
        )
          attentionSymptoms += "difficulty following-through, ";

        if (
          data.Forgetful !== undefined &&
          data.Forgetful["@value"] &&
          data.Forgetful["@value"] === "true"
        )
          attentionSymptoms += "forgetful, ";

        if (
          data.DifficultyListening !== undefined &&
          data.DifficultyListening["@value"] &&
          data.DifficultyListening["@value"] === "true"
        )
          attentionSymptoms += "difficulty listening, ";

        if (
          data.DisorganizedAttention !== undefined &&
          data.DisorganizedAttention["@value"] &&
          data.DisorganizedAttention["@value"] === "true"
        )
          attentionSymptoms += "disorganized in tasks and activities, ";

        if (
          data.Fidgety !== undefined &&
          data.Fidgety["@value"] &&
          data.Fidgety["@value"] === "true"
        )
          attentionSymptoms += "fidgety, ";

        if (
          data.LackOfDetails !== undefined &&
          data.LackOfDetails["@value"] &&
          data.LackOfDetails["@value"] === "true"
        )
          attentionSymptoms += "lack of attention to detail, ";

        if (
          data.LosesThings !== undefined &&
          data.LosesThings["@value"] &&
          data.LosesThings["@value"] === "true"
        )
          attentionSymptoms += "loses things, ";

        if (
          data.InterruptsOthers !== undefined &&
          data.InterruptsOthers["@value"] &&
          data.InterruptsOthers["@value"] === "true"
        )
          attentionSymptoms += "interrupts others, ";

        if (
          data.UnableToSitStill !== undefined &&
          data.UnableToSitStill["@value"] &&
          data.UnableToSitStill["@value"] === "true"
        )
          attentionSymptoms += "unable to sit still, ";

        if (
          data.ExcessiveTalking !== undefined &&
          data.ExcessiveTalking["@value"] &&
          data.ExcessiveTalking["@value"] === "true"
        )
          attentionSymptoms += "excessive talking, ";

        if (
          data.DifficultyWaiting !== undefined &&
          data.DifficultyWaiting["@value"] &&
          data.DifficultyWaiting["@value"] === "true"
        )
          attentionSymptoms += "difficulty waiting for their turn, ";

        if (
          data.OtherAttention !== undefined &&
          data.OtherAttentionDetails !== undefined &&
          data.OtherAttention["@value"] &&
          data.OtherAttention["@value"] === "true" &&
          data.OtherAttentionDetails["@value"] &&
          data.OtherAttentionDetails["@value"] !== ""
        )
          attentionSymptoms += `other Attention/Hyperactivity symptom(s) - ${data.OtherAttentionDetails["@value"]}, `;

        if (
          data.AttentionHyperactivityFrequency !== undefined &&
          data.AttentionHyperactivityFrequency["@value"] &&
          data.AttentionHyperactivityFrequency["@value"] !== ""
        )
          attentionSymptoms += `frequency - ${data.AttentionHyperactivityFrequency["@value"]}, `;

        if (
          data.AttentionHyperactivityIntensity !== undefined &&
          data.AttentionHyperactivityIntensity["@value"] &&
          data.AttentionHyperactivityIntensity["@value"] !== ""
        )
          attentionSymptoms += `intensity - ${data.AttentionHyperactivityIntensity["@value"]}, `;

        if (
          data.AttentionHyperactivityDuration !== undefined &&
          data.AttentionHyperactivityDuration["@value"] &&
          data.AttentionHyperactivityDuration["@value"] !== ""
        )
          attentionSymptoms += `duration - ${data.AttentionHyperactivityDuration["@value"]}, `;

        console.log("attentionSymptoms: ", attentionSymptoms);
        symptomSummary += `${attentionSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.SU !== undefined &&
        data.SU["@value"] &&
        data.SU["@value"] === "true"
      ) {
        let suSymptoms = "Symptoms of Substance Use Disorders: ";

        if (
          data.PatternOfSubstanceUse !== undefined &&
          data.PatternOfSubstanceUse["@value"] &&
          data.PatternOfSubstanceUse["@value"] === "true"
        )
          suSymptoms +=
            "takes substances in large amounts and/or over long periods of time, ";

        if (
          data.DesiresCutDownUse !== undefined &&
          data.DesiresCutDownUse["@value"] &&
          data.DesiresCutDownUse["@value"] === "true"
        )
          suSymptoms += "persistent desire to cut down on substance use, ";

        if (
          data.TimeSpentObtaining !== undefined &&
          data.TimeSpentObtaining["@value"] &&
          data.TimeSpentObtaining["@value"] === "true"
        )
          suSymptoms +=
            "spends a significant amount of time obtaining substance(s), ";

        if (
          data.Cravings !== undefined &&
          data.Cravings["@value"] &&
          data.Cravings["@value"] === "true"
        )
          suSymptoms += "cravings for substances, ";

        if (
          data.FailureOfResponsibilities !== undefined &&
          data.FailureOfResponsibilities["@value"] &&
          data.FailureOfResponsibilities["@value"] === "true"
        )
          suSymptoms +=
            "failure to fulfill major roles at work, school, or home, ";

        if (
          data.NegativeImpact !== undefined &&
          data.NegativeImpact["@value"] &&
          data.NegativeImpact["@value"] === "true"
        )
          suSymptoms += "use consistently negatively impacts relationships, ";

        if (
          data.AbandonsActivities !== undefined &&
          data.AbandonsActivities["@value"] &&
          data.AbandonsActivities["@value"] === "true"
        )
          suSymptoms += "giving up/reducing previously enjoyed activities, ";

        if (data.Tolerance["@value"] && data.Tolerance["@value"] === "true")
          suSymptoms += "tolerance, ";

        if (
          data.SUWithdrawal !== undefined &&
          data.SUWithdrawal["@value"] &&
          data.SUWithdrawal["@value"] === "true"
        )
          suSymptoms += "withdrawal, ";

        if (
          data.SUOther !== undefined &&
          data.SUOther["@value"] &&
          data.SUOther["@value"] === "true" &&
          data.SUOtherDetails["@value"] &&
          data.SUOtherDetails["@value"] !== ""
        )
          suSymptoms += `other Substance Use Disorder symptom(s) - ${data.SUOtherDetails["@value"]}, `;

        if (
          data.SubstanceUseFrequency !== undefined &&
          data.SubstanceUseFrequency["@value"] &&
          data.SubstanceUseFrequency["@value"] !== ""
        )
          suSymptoms += `frequency - ${data.SubstanceUseFrequency["@value"]}, `;

        if (
          data.SubstanceUseIntensity !== undefined &&
          data.SubstanceUseIntensity["@value"] &&
          data.SubstanceUseIntensity["@value"] !== ""
        )
          suSymptoms += `intensity - ${data.SubstanceUseIntensity["@value"]}, `;

        if (
          data.SubstanceUseDuration !== undefined &&
          data.SubstanceUseDuration["@value"] &&
          data.SubstanceUseDuration["@value"] !== ""
        )
          suSymptoms += `duration - ${data.SubstanceUseDuration["@value"]}, `;

        console.log("suSymptoms: ", suSymptoms);
        symptomSummary += `${suSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.DMDD !== undefined &&
        data.DMDD["@value"] &&
        data.DMDD["@value"] === "true"
      ) {
        let dmddSymptoms =
          "Symptoms of Disruptive Mood Dysregulation Disorder: ";

        if (
          data.Temper !== undefined &&
          data.Temper["@value"] &&
          data.Temper["@value"] === "true"
        )
          dmddSymptoms += "severe and recurrent temper outbursts, ";

        if (
          data.OutsideOutbursts !== undefined &&
          data.OutsideOutbursts["@value"] &&
          data.OutsideOutbursts["@value"] === "true"
        )
          dmddSymptoms += "outbursts are outside of developmental level, ";

        if (
          data.FrequentOutbursts !== undefined &&
          data.FrequentOutbursts["@value"] &&
          data.FrequentOutbursts["@value"] === "true"
        )
          dmddSymptoms += "outbursts occur three or more times per week, ";

        if (
          data.DMDDIrritable !== undefined &&
          data.DMDDIrritable["@value"] &&
          data.DMDDIrritable["@value"] === "true"
        )
          dmddSymptoms += "irritable/angry mood most of the day, ";

        if (
          data.OutburstsAroundOthers !== undefined &&
          data.OutburstsAroundOthers["@value"] &&
          data.OutburstsAroundOthers["@value"] === "true"
        )
          dmddSymptoms += "symptoms occur in home, school and/or with peers, ";

        if (
          data.DMDDOther !== undefined &&
          data.DMDDOther["@value"] &&
          data.DMDDOther["@value"] === "true" &&
          data.DMDDOtherDetails["@value"] &&
          data.DMDDOtherDetails["@value"] !== ""
        )
          dmddSymptoms += `other DMDD symptom(s) - ${data.DMDDOtherDetails["@value"]}, `;

        if (
          data.DMDDFrequency !== undefined &&
          data.DMDDFrequency["@value"] &&
          data.DMDDFrequency["@value"] !== ""
        )
          dmddSymptoms += `frequency - ${data.DMDDFrequency["@value"]}, `;

        if (
          data.DMDDIntensity !== undefined &&
          data.DMDDIntensity["@value"] &&
          data.DMDDIntensity["@value"] !== ""
        )
          dmddSymptoms += `intensity - ${data.DMDDIntensity["@value"]}, `;

        if (
          data.DMDDDuration !== undefined &&
          data.DMDDDuration["@value"] &&
          data.DMDDDuration["@value"] !== ""
        )
          dmddSymptoms += `duration - ${data.DMDDDuration["@value"]}, `;

        console.log("dmddSymptoms: ", dmddSymptoms);
        symptomSummary += `${dmddSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.ConductDisorder !== undefined &&
        data.ConductDisorder["@value"] &&
        data.ConductDisorder["@value"] === "true"
      ) {
        let conductDisorderSymptoms = "Symptoms of Conduct Disorder: ";

        if (data.Aggression["@value"] && data.Aggression["@value"] === "true")
          conductDisorderSymptoms += "aggression to people and/or animals, ";

        if (
          data.DestructionOfProperty !== undefined &&
          data.DestructionOfProperty["@value"] &&
          data.DestructionOfProperty["@value"] === "true"
        )
          conductDisorderSymptoms += "destruction of Property, ";

        if (
          data.Theft !== undefined &&
          data.Theft["@value"] &&
          data.Theft["@value"] === "true"
        )
          conductDisorderSymptoms += "deceitfulness or theft, ";

        if (
          data.SeriousViolations !== undefined &&
          data.SeriousViolations["@value"] &&
          data.SeriousViolations["@value"] === "true"
        )
          conductDisorderSymptoms += "serious violations of rules, ";

        if (
          data.OtherConductDisorder !== undefined &&
          data.OtherConductDisorderDetails !== undefined &&
          data.OtherConductDisorder["@value"] &&
          data.OtherConductDisorder["@value"] === "true" &&
          data.OtherConductDisorderDetails["@value"] &&
          data.OtherConductDisorderDetails["@value"] !== ""
        )
          conductDisorderSymptoms += `other Conduct Disorder symptom(s) - ${data.OtherConductDisorderDetails["@value"]}, `;

        if (
          data.ConductDisorderFrequency !== undefined &&
          data.ConductDisorderFrequency["@value"] &&
          data.ConductDisorderFrequency["@value"] !== ""
        )
          conductDisorderSymptoms += `frequency - ${data.ConductDisorderFrequency["@value"]}, `;

        if (
          data.ConductDisorderIntensity !== undefined &&
          data.ConductDisorderIntensity["@value"] &&
          data.ConductDisorderIntensity["@value"] !== ""
        )
          conductDisorderSymptoms += `intensity - ${data.ConductDisorderIntensity["@value"]}, `;

        if (
          data.ConductDisorderDuration !== undefined &&
          data.ConductDisorderDuration["@value"] &&
          data.ConductDisorderDuration["@value"] !== ""
        )
          conductDisorderSymptoms += `duration - ${data.ConductDisorderDuration["@value"]}, `;

        console.log("conductDisorderSymptoms: ", conductDisorderSymptoms);
        symptomSummary += `${conductDisorderSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.ODD !== undefined &&
        data.ODD["@value"] &&
        data.ODD["@value"] === "true"
      ) {
        let oddSymptoms = "Symptoms of Oppositional Defiance Disorder (ODD): ";

        if (
          data.IrritableODD !== undefined &&
          data.IrritableODD["@value"] &&
          data.IrritableODD["@value"] === "true"
        )
          oddSymptoms += "angry/irritable mood, ";

        if (
          data.Argumentative !== undefined &&
          data.Argumentative["@value"] &&
          data.Argumentative["@value"] === "true"
        )
          oddSymptoms += "argumentative/defiant, ";

        if (
          data.Vindictive !== undefined &&
          data.Vindictive["@value"] &&
          data.Vindictive["@value"] === "true"
        )
          oddSymptoms += "vindictive, ";

        if (
          data.OtherODD !== undefined &&
          data.OtherODDDetails !== undefined &&
          data.OtherODD["@value"] &&
          data.OtherODD["@value"] === "true" &&
          data.OtherODDDetails["@value"] &&
          data.OtherODDDetails["@value"] !== ""
        )
          oddSymptoms += `other ODD symptom(s) - ${data.OtherODDDetails["@value"]}, `;

        if (
          data.ODDFrequency !== undefined &&
          data.ODDFrequency["@value"] &&
          data.ODDFrequency["@value"] !== ""
        )
          oddSymptoms += `frequency - ${data.ODDFrequency["@value"]}, `;

        if (
          data.ODDIntensity !== undefined &&
          data.ODDIntensity["@value"] &&
          data.ODDIntensity["@value"] !== ""
        )
          oddSymptoms += `intensity - ${data.ODDIntensity["@value"]}, `;

        if (
          data.ODDDuration !== undefined &&
          data.ODDDuration["@value"] &&
          data.ODDDuration["@value"] !== ""
        )
          oddSymptoms += `duration - ${data.ODDDuration["@value"]}, `;

        console.log("oddSymptoms: ", oddSymptoms);
        symptomSummary += `${oddSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.OCD !== undefined &&
        data.OCD["@value"] &&
        data.OCD["@value"] === "true"
      ) {
        let ocdSymptoms = "Symptoms of Obsessive-Compulsive Disorder (OCD): ";

        if (
          data.OCDPresence !== undefined &&
          data.OCDPresence["@value"] &&
          data.OCDPresence["@value"] === "true"
        )
          ocdSymptoms += "presence of obsessions, compulsions, or both, ";

        if (
          data.Obsessions !== undefined &&
          data.Obsessions["@value"] &&
          data.Obsessions["@value"] === "true"
        )
          ocdSymptoms += "obsessions, ";

        if (
          data.TimeConsuming !== undefined &&
          data.TimeConsuming["@value"] &&
          data.TimeConsuming["@value"] === "true"
        )
          ocdSymptoms +=
            "obsessions or compulsions are time consuming and cause distress/impairment, ";

        if (
          data.Compulsions !== undefined &&
          data.Compulsions["@value"] &&
          data.Compulsions["@value"] === "true"
        )
          ocdSymptoms += "compulsions/repetitive behaviors or mental acts, ";

        if (
          data.ReduceAnxiety !== undefined &&
          data.ReduceAnxiety["@value"] &&
          data.ReduceAnxiety["@value"] === "true"
        )
          ocdSymptoms +=
            "behaviors and mental acts done to prevent or reduce anxiety or distress, ";

        if (
          data.SuppressUrges !== undefined &&
          data.SuppressUrges["@value"] &&
          data.SuppressUrges["@value"] === "true"
        )
          ocdSymptoms +=
            "attempts to ignore or suppress thoughts, urges or images with other thought or action, ";

        if (
          data.OCDOther !== undefined &&
          data.OCDOther["@value"] &&
          data.OCDOther["@value"] === "true" &&
          data.OCDOtherDetails["@value"] &&
          data.OCDOtherDetails["@value"] !== ""
        )
          ocdSymptoms += `other OCD symptom(s) - ${data.OCDOtherDetails["@value"]}, `;

        if (
          data.OCDFrequency !== undefined &&
          data.OCDFrequency["@value"] &&
          data.OCDFrequency["@value"] !== ""
        )
          ocdSymptoms += `frequency - ${data.OCDFrequency["@value"]}, `;

        if (
          data.OCDIntensity !== undefined &&
          data.OCDIntensity["@value"] &&
          data.OCDIntensity["@value"] !== ""
        )
          ocdSymptoms += `intensity - ${data.OCDIntensity["@value"]}, `;

        if (
          data.OCDDuration !== undefined &&
          data.OCDDuration["@value"] &&
          data.OCDDuration["@value"] !== ""
        )
          ocdSymptoms += `duration - ${data.OCDDuration["@value"]}, `;

        console.log("ocdSymptoms: ", ocdSymptoms);
        symptomSummary += `${ocdSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.PanicDisorder !== undefined &&
        data.PanicDisorder["@value"] &&
        data.PanicDisorder["@value"] === "true"
      ) {
        let panicDisorderSymptoms = "Symptoms of Panic Disorder: ";

        if (
          data.PoundingHeart !== undefined &&
          data.PoundingHeart["@value"] &&
          data.PoundingHeart["@value"] === "true"
        )
          panicDisorderSymptoms +=
            "palpitations, pounding heart, accelerated heart rate, ";

        if (
          data.PanicDisorderSweating !== undefined &&
          data.PanicDisorderSweating["@value"] &&
          data.PanicDisorderSweating["@value"] === "true"
        )
          panicDisorderSymptoms += "sweating, ";

        if (
          data.Trembling !== undefined &&
          data.Trembling["@value"] &&
          data.Trembling["@value"] === "true"
        )
          panicDisorderSymptoms += "trembling/shaking, ";

        if (
          data.ShortnessOfBreath !== undefined &&
          data.ShortnessOfBreath["@value"] &&
          data.ShortnessOfBreath["@value"] === "true"
        )
          panicDisorderSymptoms += "shortness of breath, ";

        if (
          data.Choking !== undefined &&
          data.Choking["@value"] &&
          data.Choking["@value"] === "true"
        )
          panicDisorderSymptoms += "feelings of choking, ";

        if (
          data.ChestPain !== undefined &&
          data.ChestPain["@value"] &&
          data.ChestPain["@value"] === "true"
        )
          panicDisorderSymptoms += "chest pain/discomfort, ";

        if (
          data.PanicDisorderNausea !== undefined &&
          data.PanicDisorderNausea["@value"] &&
          data.PanicDisorderNausea["@value"] === "true"
        )
          panicDisorderSymptoms += "nausea/abdominal discomfort, ";

        if (
          data.Dizzy !== undefined &&
          data.Dizzy["@value"] &&
          data.Dizzy["@value"] === "true"
        )
          panicDisorderSymptoms += "dizzy/faint, ";

        if (
          data.Chills !== undefined &&
          data.Chills["@value"] &&
          data.Chills["@value"] === "true"
        )
          panicDisorderSymptoms += "chills, ";

        if (
          data.Derealization !== undefined &&
          data.Derealization["@value"] &&
          data.Derealization["@value"] === "true"
        )
          panicDisorderSymptoms += "derealization or depersonalization, ";

        if (
          data.FearOfCrazy !== undefined &&
          data.FearOfCrazy["@value"] &&
          data.FearOfCrazy["@value"] === "true"
        )
          panicDisorderSymptoms += "fear of losing control/going crazy, ";

        if (
          data.FearOfDying !== undefined &&
          data.FearOfDying["@value"] &&
          data.FearOfDying["@value"] === "true"
        )
          panicDisorderSymptoms += "fear of dying, ";

        if (
          data.PanicDisorderOther !== undefined &&
          data.PanicDisorderOther["@value"] &&
          data.PanicDisorderOther["@value"] === "true" &&
          data.PanicDisorderOtherDetails["@value"] &&
          data.PanicDisorderOtherDetails["@value"] !== ""
        )
          panicDisorderSymptoms += `other Panic Disorder symptom(s) - ${data.PanicDisorderOtherDetails["@value"]}, `;

        if (
          data.PanicDisorderFrequency !== undefined &&
          data.PanicDisorderFrequency["@value"] &&
          data.PanicDisorderFrequency["@value"] !== ""
        )
          panicDisorderSymptoms += `frequency - ${data.PanicDisorderFrequency["@value"]}, `;

        if (
          data.PanicDisorderIntensity !== undefined &&
          data.PanicDisorderIntensity["@value"] &&
          data.PanicDisorderIntensity["@value"] !== ""
        )
          panicDisorderSymptoms += `intensity - ${data.PanicDisorderIntensity["@value"]}, `;

        if (
          data.PanicDisorderDuration !== undefined &&
          data.PanicDisorderDuration["@value"] &&
          data.PanicDisorderDuration["@value"] !== ""
        )
          panicDisorderSymptoms += `duration - ${data.PanicDisorderDuration["@value"]}, `;

        console.log("panicDisorderSymptoms: ", panicDisorderSymptoms);
        symptomSummary += `${panicDisorderSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.Phobias !== undefined &&
        data.Phobias["@value"] &&
        data.Phobias["@value"] === "true"
      ) {
        let phobiaSymptoms = "Symptoms of Phobias: ";

        if (
          data.PhobiasSpecificFear !== undefined &&
          data.PhobiasSpecificFear["@value"] &&
          data.PhobiasSpecificFear["@value"] === "true"
        )
          phobiaSymptoms +=
            "marked fear/anxiety about a specific object or situation, ";

        if (
          data.ImmediateFear !== undefined &&
          data.ImmediateFear["@value"] &&
          data.ImmediateFear["@value"] === "true"
        )
          phobiaSymptoms +=
            "object or situation provokes immediate fear or anxiety, ";

        if (
          data.PhobiasAvoidance !== undefined &&
          data.PhobiasAvoidance["@value"] &&
          data.PhobiasAvoidance["@value"] === "true"
        )
          phobiaSymptoms += "avoids objective/situation, ";

        if (
          data.FearExaggerated !== undefined &&
          data.FearExaggerated["@value"] &&
          data.FearExaggerated["@value"] === "true"
        )
          phobiaSymptoms += "fear and anxiety is out of proportion, ";

        if (
          data.PhobiasOther !== undefined &&
          data.PhobiasOther["@value"] &&
          data.PhobiasOther["@value"] === "true" &&
          data.PhobiasOtherDetails["@value"] &&
          data.PhobiasOtherDetails["@value"] !== ""
        )
          phobiaSymptoms += `other Phobia symptom(s) - ${data.PhobiasOtherDetails["@value"]}, `;

        if (
          data.PhobiasFrequency !== undefined &&
          data.PhobiasFrequency["@value"] &&
          data.PhobiasFrequency["@value"] !== ""
        )
          phobiaSymptoms += `frequency - ${data.PhobiasFrequency["@value"]}, `;

        if (
          data.PhobiasIntensity !== undefined &&
          data.PhobiasIntensity["@value"] &&
          data.PhobiasIntensity["@value"] !== ""
        )
          phobiaSymptoms += `intensity - ${data.PhobiasIntensity["@value"]}, `;

        if (
          data.PhobiasDuration !== undefined &&
          data.PhobiasDuration["@value"] &&
          data.PhobiasDuration["@value"] !== ""
        )
          phobiaSymptoms += `duration - ${data.PhobiasDuration["@value"]}, `;

        console.log("phobiaSymptoms: ", phobiaSymptoms);
        symptomSummary += `${phobiaSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.DI !== undefined &&
        data.DI["@value"] &&
        data.DI["@value"] === "true"
      ) {
        let diSymptoms = "Developmental Impairments: ";

        if (
          data.DILowIQ !== undefined &&
          data.DILowIQ["@value"] &&
          data.DILowIQ["@value"] === "true"
        )
          diSymptoms += "low intelligence/IQ, ";

        if (
          data.DISensoryImpairments !== undefined &&
          data.DISensoryImpairments["@value"] &&
          data.DISensoryImpairments["@value"] === "true"
        )
          diSymptoms += "sensory impairments, ";

        if (
          data.DISpeechDelays !== undefined &&
          data.DISpeechDelays["@value"] &&
          data.DISpeechDelays["@value"] === "true"
        )
          diSymptoms += "language/speech delays, ";

        if (
          data.DIMotorCoordinationDelays !== undefined &&
          data.DIMotorCoordinationDelays["@value"] &&
          data.DIMotorCoordinationDelays["@value"] === "true"
        )
          diSymptoms += "motor coordination delays, ";

        if (
          data.DILearningDisorder !== undefined &&
          data.DILearningDisorder["@value"] &&
          data.DILearningDisorder["@value"] === "true"
        )
          diSymptoms += "specific learning disorder, ";

        if (
          data.DISpatialDelays !== undefined &&
          data.DISpatialDelays["@value"] &&
          data.DISpatialDelays["@value"] === "true"
        )
          diSymptoms += "visio-spatial skill delays, ";

        if (
          data.DevelopmentalImpairmentsOther !== undefined &&
          data.DevelopmentalImpairmentsOther["@value"] &&
          data.DevelopmentalImpairmentsOther["@value"] === "true" &&
          data.DevelopmentalImpairmentsOtherDetails["@value"] &&
          data.DevelopmentalImpairmentsOtherDetails["@value"] !== ""
        )
          diSymptoms += `other Developmental Impairment symptom(s) - ${data.DevelopmentalImpairmentsOtherDetails["@value"]}, `;

        if (
          data.DevelopmentalImpairmentsDetails !== undefined &&
          data.DevelopmentalImpairmentsDetails["@value"] &&
          data.DevelopmentalImpairmentsDetails["@value"] !== ""
        )
          diSymptoms += `Description of symptoms - ${data.DevelopmentalImpairmentsDetails["@value"]}, `;

        console.log("diSymptoms: ", diSymptoms);
        symptomSummary += `${diSymptoms.slice(0, -2)}.\r\n`;
      }

      if (
        data.OtherSymptoms !== undefined &&
        data.OtherSymptoms["@value"] &&
        data.OtherSymptoms["@value"] === "true"
      ) {
        let otherSymptoms = "Other Symptoms: ";

        if (
          data.OtherSymptomsDetails !== undefined &&
          data.OtherSymptomsDetails["@value"] &&
          data.OtherSymptomsDetails["@value"] !== ""
        )
          otherSymptoms += `${data.OtherSymptomsDetails["@value"]}, `;

        if (
          data.OtherSymptomsFrequency !== undefined &&
          data.OtherSymptomsFrequency["@value"] &&
          data.OtherSymptomsFrequency["@value"] !== ""
        )
          otherSymptoms += `frequency - ${data.OtherSymptomsFrequency["@value"]}, `;

        if (
          data.OtherSymptomsIntensity !== undefined &&
          data.OtherSymptomsIntensity["@value"] &&
          data.OtherSymptomsIntensity["@value"] !== ""
        )
          otherSymptoms += `intensity - ${data.OtherSymptomsIntensity["@value"]}, `;

        if (
          data.OtherSymptomsDuration !== undefined &&
          data.OtherSymptomsDuration["@value"] &&
          data.OtherSymptomsDuration["@value"] !== ""
        )
          otherSymptoms += `duration - ${data.OtherSymptomsDuration["@value"]}, `;

        console.log("otherSymptoms: ", otherSymptoms);
        symptomSummary += `${otherSymptoms.slice(0, -2)}.\r\n`;
      }
    }

    if (symptomSummary !== "")
      summary += `Symptom Checklist:\r\n${symptomSummary.trimEnd()}\r\n\r\n`;

    // OMHAS Review/Recommendations
    if (data.AgeGroup !== undefined) {
      let omhasSummary = "";
      if (
        data.LevelOfCareRecommendedLevel["@value"] &&
        data.LevelOfCareRecommendedLevel["@value"] !== ""
      ) {
        const levelOfCareRecommended = (
          data.AgeGroup["@value"] === "0"
            ? adolescentDimension1Options
            : adultDimension1Options
        ).filter(
          (option) => option._id === data.LevelOfCareRecommendedLevel["@value"]
        )[0].name;

        omhasSummary += `Level of Care Recommended: ${levelOfCareRecommended}; `;
      }

      if (
        data.LevelOfCarePlacedLevel["@value"] &&
        data.LevelOfCarePlacedLevel["@value"] !== ""
      ) {
        const levelOfCarePlaced = (
          data.AgeGroup["@value"] === "0"
            ? adolescentDimension1Options
            : adultDimension1Options
        ).filter(
          (option) => option._id === data.LevelOfCarePlacedLevel["@value"]
        )[0].name;

        omhasSummary += `Level of Care Placed: ${levelOfCarePlaced}; `;
      }

      if (
        data.NotPlacedReason["@value"] &&
        data.NotPlacedReason["@value"] !== ""
      ) {
        if (data.NotPlacedReason["@value"] !== "7") {
          const notPlacedReason = reasonNotPlacedOptions.filter(
            (option) => option._id === data.NotPlacedReason["@value"]
          )[0].name;

          omhasSummary += `Reason client was not placed in Recommended Level of Care: ${notPlacedReason}; `;
        } else if (
          data.TraumaSymptoms !== undefined &&
          data.NotPlacedReasonDetails["@value"] &&
          data.NotPlacedReasonDetails["@value"] !== ""
        )
          omhasSummary += `Reason client was not placed in Recommended Level of Care: ${data.NotPlacedReasonDetails["@value"]}; `;
      }

      if (data.OMHASComments["@value"] && data.OMHASComments["@value"] !== "")
        omhasSummary += `Comments/Recommendations: ${data.OMHASComments["@value"]}; `;

      if (omhasSummary !== "")
        summary += `OMHAS Review:\r\n${omhasSummary.trimEnd()}\r\n\r\n`;
    }

    // Diagnosis
    if (
      data.DiagnosisDetails["@value"] &&
      data.DiagnosisDetails["@value"] !== "" &&
      data.DiagnosisDetails["@value"] !== '[{"IsEditable": true}]'
    )
      summary += `Diagnosis:\r\n${JSON.parse(data.DiagnosisDetails["@value"])
        .map(
          (item) => `ICD-10 Code: ${item.Code}, Diagnosis: ${item.Diagnosis}`
        )
        .join("; ")};\r\n\r\n`;

    // Treatment Recommendations/Needs
    if (
      data.TreatmentRecommendations["@value"] &&
      data.TreatmentRecommendations["@value"] !== "" &&
      data.TreatmentRecommendations["@value"] !== '[{"IsEditable": true}]'
    )
      summary += `Treatment Recommendations:\r\n${JSON.parse(
        data.TreatmentRecommendations["@value"]
      )
        .map(
          (item) =>
            `Recommendation: ${item.Recommendation}, ${
              item.Deferred === "true"
                ? "Status: SAFY will be providing, "
                : item.Referred === "true"
                ? "Status: SAFY will be referring to another agency/individual, "
                : item.Declined === "true"
                ? "Status: Declined by Client, "
                : ""
            }Reason: ${item.Reason}`
        )
        .join("; ")}; `;

    // Clinical Narrative Summary Comments
    if (
      data.ClinicalNarrativeSummaryComments["@value"] &&
      data.ClinicalNarrativeSummaryComments["@value"] !== ""
    )
      summary += `Additional Comments:\r\n${data.ClinicalNarrativeSummaryComments["@value"]}`;

    summary = summary.trimEnd();

    const obj = { ...data };
    obj.ClinicalNarrativeSummary["@value"] = summary;
    setData(obj);
  };

  const getSelfHarmRiskFactors = () => {
    let riskFactors = "";

    if (data.PriorAttempt["@value"] === "true")
      riskFactors += "Prior suicide attempt(s), ";

    if (data.FamilyHistory["@value"] === "true")
      riskFactors += "Family history of suicide, ";

    if (data.AccessToMeans["@value"] === "true")
      riskFactors += "Access to lethal means, ";

    if (data.Exposure["@value"] === "true")
      riskFactors += "Exposure to another person's suicide, ";

    if (data.HealthCondition["@value"] === "true")
      riskFactors += "Serious or chronic health condition and/or pain, ";

    if (data.ElevatingBeliefs["@value"] === "true")
      riskFactors += "Cultural or religious beliefs elevating suicide, ";

    if (data.MentalHealthDisorder["@value"] === "true")
      riskFactors += "Mental health disorder, ";

    if (
      data.OtherRiskFactors !== undefined &&
      data.OtherRiskFactors["@value"] === "true"
    )
      riskFactors += `${
        data.OtherRiskFactorsDetails &&
        data.OtherRiskFactorsDetails["@value"] !== ""
          ? `${data.OtherRiskFactorsDetails["@value"]}, `
          : ""
      }`;

    if (
      data.NoRiskFactors !== undefined &&
      data.NoRiskFactors["@value"] === "true"
    )
      riskFactors += "None reported, ";

    return riskFactors.slice(0, -2);
  };
  const getSelfHarmProtectiveFactors = () => {
    let protectiveFactors = "";

    if (data.ClinicalCare["@value"] === "true")
      protectiveFactors +=
        "Effective clinical care for mental, physical, and substance abuse disorders, ";

    if (data.Connected["@value"] === "true")
      protectiveFactors +=
        "Connectedness to individuals, family, community, and social institutions, ";

    if (data.Skills["@value"] === "true")
      protectiveFactors +=
        "Skills in problem solving, conflict resolution, and nonviolent ways of handling disputes, ";

    if (data.CaregiverContacts["@value"] === "true")
      protectiveFactors += "Contacts with caregivers, ";

    if (data.DiscouragingBeliefs["@value"] === "true")
      protectiveFactors +=
        "Cultural and religious beliefs that discourage suicide, ";

    if (
      data.NoSelfHarmProtectiveFactors !== undefined &&
      data.NoSelfHarmProtectiveFactors["@value"] === "true"
    )
      protectiveFactors += "None reported, ";

    return protectiveFactors.slice(0, -2);
  };
  const getSelfHarmWarningSigns = () => {
    let warningSigns = "";

    if (data.Threatening["@value"] === "true")
      warningSigns += "Threatening to hurt or kill oneself, ";

    if (data.SayingGoodbye["@value"] === "true")
      warningSigns += "Visiting or calling people to say goodbye, ";

    if (data.SeekingMeans["@value"] === "true")
      warningSigns += "Seeking a means to kill oneself, ";

    if (data.SocialWithdrawal["@value"] === "true")
      warningSigns += "Social withdrawal/isolation, ";

    if (data.Hopelessness["@value"] === "true")
      warningSigns += "Hopelessness, ";

    if (data.RecklessBehavior["@value"] === "true")
      warningSigns += "Reckless behavior/increase in risky activities, ";

    if (data.IncreasedSubstanceUse["@value"] === "true")
      warningSigns += "Increasing alcohol or drug use, ";

    if (data.Feelings["@value"] === "true")
      warningSigns +=
        "Feelings of alienation, loneliness, guilt, shame, or inadequacy, ";

    if (data.DramaticMoodChanges["@value"] === "true")
      warningSigns += "Dramatic mood changes, ";

    if (data.GivingAwayPossessions["@value"] === "true")
      warningSigns += "Giving away prized possessions, ";

    if (
      data.NoWarningSigns !== undefined &&
      data.NoWarningSigns["@value"] === "true"
    )
      warningSigns += "None reported, ";

    return warningSigns.slice(0, -2);
  };

  const customValidate = (errs) => {
    // InformationSources
    console.log("Validating InformationSources");

    if (
      (!data.ISClient["@value"] || data.ISClient["@value"] === "false") &&
      (!data.ISParent["@value"] || data.ISParent["@value"] === "false") &&
      (!data.ISFamily["@value"] || data.ISFamily["@value"] === "false") &&
      (!data.ISGuardian["@value"] || data.ISGuardian["@value"] === "false") &&
      (!data.ISCaregiver["@value"] || data.ISCaregiver["@value"] === "false") &&
      (!data.ISPhysician["@value"] || data.ISPhysician["@value"] === "false") &&
      (!data.ISRecords["@value"] || data.ISRecords["@value"] === "false") &&
      (!data.ISServiceProvider["@value"] ||
        data.ISServiceProvider["@value"] === "false") &&
      (!data.ISSchool["@value"] || data.ISSchool["@value"] === "false") &&
      (!data.ISCourt["@value"] || data.ISCourt["@value"] === "false") &&
      (!data.ISCPS["@value"] || data.ISCPS["@value"] === "false") &&
      (!data.ISOther["@value"] || data.ISOther["@value"] === "false")
    ) {
      if (errs)
        errs.InformationSources =
          "At least one Information Source is required.";
      else
        errs = {
          InformationSources: "At least one Information Source is required.",
        };
    }

    console.log("Validating DomesticViolenceConcerns");

    if (
      (!data.CurrentConcernsOfDV["@value"] ||
        data.CurrentConcernsOfDV["@value"] === "false") &&
      (!data.PastConcernsOfDV["@value"] ||
        data.PastConcernsOfDV["@value"] === "false") &&
      (!data.UnknownConcernsOfDV["@value"] ||
        data.UnknownConcernsOfDV["@value"] === "false") &&
      (!data.NoReportedConcernsOfDV["@value"] ||
        data.NoReportedConcernsOfDV["@value"] === "false")
    ) {
      if (errs)
        errs.DomesticViolenceConcerns = "At least one value is required.";
      else
        errs = {
          DomesticViolenceConcerns: "At least one value is required.",
        };
    }

    console.log("Validating CouplesConflict");

    if (
      (!data.CurrentMaritalConflict["@value"] ||
        data.CurrentMaritalConflict["@value"] === "false") &&
      (!data.PastMaritalConflict["@value"] ||
        data.PastMaritalConflict["@value"] === "false") &&
      (!data.UnknownMaritalConflict["@value"] ||
        data.UnknownMaritalConflict["@value"] === "false") &&
      (!data.NoReportedMaritalConflict["@value"] ||
        data.NoReportedMaritalConflict["@value"] === "false")
    ) {
      if (errs) errs.CouplesConflict = "At least one value is required.";
      else
        errs = {
          CouplesConflict: "At least one value is required.",
        };
    }

    console.log("Validating DisciplinePractices");

    if (
      (!data.DisciplinePracticesEffective["@value"] ||
        data.DisciplinePracticesEffective["@value"] === "false") &&
      (!data.DisciplinePracticesInconsistent["@value"] ||
        data.DisciplinePracticesInconsistent["@value"] === "false") &&
      (!data.DisciplinePracticesIneffective["@value"] ||
        data.DisciplinePracticesIneffective["@value"] === "false") &&
      (data.DisciplinePracticesNA === undefined ||
        !data.DisciplinePracticesNA["@value"] ||
        data.DisciplinePracticesNA["@value"] === "false")
    ) {
      if (errs) errs.DisciplinePractices = "At least one value is required.";
      else
        errs = {
          DisciplinePractices: "At least one value is required.",
        };
    }

    console.log("Validating TraumaSymptomsResponse");

    if (data.TraumaNA["@value"] === "false") {
      if (
        (!data.DistressingMemories["@value"] ||
          data.DistressingMemories["@value"] === "false") &&
        (!data.LossOfMemories["@value"] ||
          data.LossOfMemories["@value"] === "false") &&
        (!data.Nightmares["@value"] || data.Nightmares["@value"] === "false") &&
        (!data.Flashbacks["@value"] || data.Flashbacks["@value"] === "false") &&
        (!data.RepetitivePlayOfEvents["@value"] ||
          data.RepetitivePlayOfEvents["@value"] === "false") &&
        (!data.Dissocation["@value"] ||
          data.Dissocation["@value"] === "false") &&
        (!data.BlockOut["@value"] || data.BlockOut["@value"] === "false") &&
        (!data.Hyperarousal["@value"] ||
          data.Hyperarousal["@value"] === "false") &&
        (!data.Avoidant["@value"] || data.Avoidant["@value"] === "false") &&
        (!data.Detachment["@value"] || data.Detachment["@value"] === "false") &&
        (!data.PsychologicalDistress["@value"] ||
          data.PsychologicalDistress["@value"] === "false") &&
        (!data.NegativeCognitions["@value"] ||
          data.NegativeCognitions["@value"] === "false") &&
        (!data.Hypervigilance["@value"] ||
          data.Hypervigilance["@value"] === "false") &&
        (!data.SleepDisturbances["@value"] ||
          data.SleepDisturbances["@value"] === "false") &&
        (!data.DifficultyConcentratingSymptom["@value"] ||
          data.DifficultyConcentratingSymptom["@value"] === "false") &&
        (!data.EmotionalDysregulation["@value"] ||
          data.EmotionalDysregulation["@value"] === "false") &&
        (!data.DiminishedInterests["@value"] ||
          data.DiminishedInterests["@value"] === "false") &&
        (!data.SocialWithdrawalSymptom["@value"] ||
          data.SocialWithdrawalSymptom["@value"] === "false") &&
        (!data.PhysiologicalResponses["@value"] ||
          data.PhysiologicalResponses["@value"] === "false") &&
        (!data.DevelopmentalRegression["@value"] ||
          data.DevelopmentalRegression["@value"] === "false") &&
        (!data.UnhealthyBoundaries["@value"] ||
          data.UnhealthyBoundaries["@value"] === "false") &&
        (!data.NoSymptoms["@value"] || data.NoSymptoms["@value"] === "false")
      ) {
        if (errs)
          errs.TraumaSymptomsResponse =
            "At least one Trauma Symptom/Response is required.";
        else
          errs = {
            TraumaSymptomsResponse:
              "At least one Trauma Symptom/Response is required.",
          };
      }

      console.log("Validating TraumaProtectiveFactors");

      if (
        (!data.SocialSupports["@value"] ||
          data.SocialSupports["@value"] === "false") &&
        (!data.InterpersonalSkills["@value"] ||
          data.InterpersonalSkills["@value"] === "false") &&
        (!data.ValidatingExperiences["@value"] ||
          data.ValidatingExperiences["@value"] === "false") &&
        (!data.Adaptability["@value"] ||
          data.Adaptability["@value"] === "false") &&
        (!data.SenseOfMastery["@value"] ||
          data.SenseOfMastery["@value"] === "false") &&
        (!data.PositiveOutlook["@value"] ||
          data.PositiveOutlook["@value"] === "false") &&
        (!data.Resources["@value"] || data.Resources["@value"] === "false") &&
        (!data.SecureAttachments["@value"] ||
          data.SecureAttachments["@value"] === "false") &&
        (!data.CaregiverStability["@value"] ||
          data.CaregiverStability["@value"] === "false") &&
        (!data.PlacementStability["@value"] ||
          data.PlacementStability["@value"] === "false") &&
        (!data.TiesToExtendedFamily["@value"] ||
          data.TiesToExtendedFamily["@value"] === "false") &&
        (!data.StrongCulturalIdentity["@value"] ||
          data.StrongCulturalIdentity["@value"] === "false") &&
        (!data.StrongSpiritualBeliefs["@value"] ||
          data.StrongSpiritualBeliefs["@value"] === "false") &&
        (!data.Pets["@value"] || data.Pets["@value"] === "false") &&
        (!data.NoTraumaProtectiveFactors ||
          !data.NoTraumaProtectiveFactors["@value"] ||
          data.NoTraumaProtectiveFactors["@value"] === "false")
      ) {
        if (errs)
          errs.TraumaProtectiveFactors =
            "At least one Protective Factor is required.";
        else
          errs = {
            TraumaProtectiveFactors:
              "At least one Protective Factor is required.",
          };
      }
    }

    console.log("Validating MSAppearance");

    if (
      (!data.AppropriateAppearance["@value"] ||
        data.AppropriateAppearance["@value"] === "false") &&
      (!data.Neat["@value"] || data.Neat["@value"] === "false") &&
      (!data.Disheveled["@value"] || data.Disheveled["@value"] === "false") &&
      (!data.BizarreAppearance["@value"] ||
        data.BizarreAppearance["@value"] === "false") &&
      (!data.OtherAppearance["@value"] ||
        data.OtherAppearance["@value"] === "false")
    ) {
      if (errs) errs.MSAppearance = "At least one value is required.";
      else
        errs = {
          MSAppearance: "At least one value is required.",
        };
    }

    console.log("Validating MSBehavior");

    if (
      (!data.Cooperative["@value"] || data.Cooperative["@value"] === "false") &&
      (!data.Guarded["@value"] || data.Guarded["@value"] === "false") &&
      (!data.Hyperactive["@value"] || data.Hyperactive["@value"] === "false") &&
      (!data.Agitated["@value"] || data.Agitated["@value"] === "false") &&
      (!data.Paranoid["@value"] || data.Paranoid["@value"] === "false") &&
      (!data.Tics["@value"] || data.Tics["@value"] === "false") &&
      (!data.Impulsive["@value"] || data.Impulsive["@value"] === "false") &&
      (!data.Aggressive["@value"] || data.Aggressive["@value"] === "false") &&
      (!data.Withdrawn["@value"] || data.Withdrawn["@value"] === "false") &&
      (!data.BizarreBehavior["@value"] ||
        data.BizarreBehavior["@value"] === "false") &&
      (!data.Rigidity["@value"] || data.Rigidity["@value"] === "false") &&
      (!data.OtherBehavior["@value"] ||
        data.OtherBehavior["@value"] === "false")
    ) {
      if (errs) errs.MSBehavior = "At least one value is required.";
      else
        errs = {
          MSBehavior: "At least one value is required.",
        };
    }

    console.log("Validating MSSpeech");

    if (
      (!data.Normal["@value"] || data.Normal["@value"] === "false") &&
      (!data.Slurred["@value"] || data.Slurred["@value"] === "false") &&
      (!data.Pressured["@value"] || data.Pressured["@value"] === "false") &&
      (!data.Tangential["@value"] || data.Tangential["@value"] === "false") &&
      (!data.Incoherent["@value"] || data.Incoherent["@value"] === "false") &&
      (!data.Stutters["@value"] || data.Stutters["@value"] === "false") &&
      (!data.OtherSpeech["@value"] || data.OtherSpeech["@value"] === "false")
    ) {
      if (errs) errs.MSSpeech = "At least one value is required.";
      else
        errs = {
          MSSpeech: "At least one value is required.",
        };
    }

    console.log("Validating MSMood");

    if (
      (!data.Euthymic["@value"] || data.Euthymic["@value"] === "false") &&
      (!data.Anxious["@value"] || data.Anxious["@value"] === "false") &&
      (!data.Angry["@value"] || data.Angry["@value"] === "false") &&
      (!data.Depressed["@value"] || data.Depressed["@value"] === "false") &&
      (!data.Euphoric["@value"] || data.Euphoric["@value"] === "false") &&
      (!data.Irritable["@value"] || data.Irritable["@value"] === "false") &&
      (!data.Fearful["@value"] || data.Fearful["@value"] === "false") &&
      (!data.OtherMood["@value"] || data.OtherMood["@value"] === "false")
    ) {
      if (errs) errs.MSMood = "At least one value is required.";
      else
        errs = {
          MSMood: "At least one value is required.",
        };
    }

    console.log("Validating MSAffect");

    if (
      (!data.FullRange["@value"] || data.FullRange["@value"] === "false") &&
      (!data.Restricted["@value"] || data.Restricted["@value"] === "false") &&
      (!data.Blunted["@value"] || data.Blunted["@value"] === "false") &&
      (!data.Flat["@value"] || data.Flat["@value"] === "false") &&
      (!data.Inappropriate["@value"] ||
        data.Inappropriate["@value"] === "false") &&
      (!data.OtherAffect["@value"] || data.OtherAffect["@value"] === "false")
    ) {
      if (errs) errs.MSAffect = "At least one value is required.";
      else
        errs = {
          MSAffect: "At least one value is required.",
        };
    }

    console.log("Validating MSPerception");

    if (
      (!data.Logical["@value"] || data.Logical["@value"] === "false") &&
      (!data.Preoccupations["@value"] ||
        data.Preoccupations["@value"] === "false") &&
      (!data.Delusions["@value"] || data.Delusions["@value"] === "false") &&
      (!data.Hallucinations["@value"] ||
        data.Hallucinations["@value"] === "false") &&
      (!data.Racing["@value"] || data.Racing["@value"] === "false") &&
      (!data.Disorganized["@value"] ||
        data.Disorganized["@value"] === "false") &&
      (!data.Concrete["@value"] || data.Concrete["@value"] === "false") &&
      (!data.SuicidalIdeation["@value"] ||
        data.SuicidalIdeation["@value"] === "false") &&
      (!data.HomicidalIdeation["@value"] ||
        data.HomicidalIdeation["@value"] === "false") &&
      (!data.OtherPerception["@value"] ||
        data.OtherPerception["@value"] === "false")
    ) {
      if (errs) errs.MSPerception = "At least one value is required.";
      else
        errs = {
          MSPerception: "At least one value is required.",
        };
    }

    console.log("Validating MSCognition");

    if (
      (!data.NoImpairment["@value"] ||
        data.NoImpairment["@value"] === "false") &&
      (!data.ShortAttentionSpan["@value"] ||
        data.ShortAttentionSpan["@value"] === "false") &&
      (!data.DifficultyConcentrating["@value"] ||
        data.DifficultyConcentrating["@value"] === "false") &&
      (!data.Distractibility["@value"] ||
        data.Distractibility["@value"] === "false") &&
      (!data.MemoryImpairment["@value"] ||
        data.MemoryImpairment["@value"] === "false") &&
      (!data.LowIntelligence["@value"] ||
        data.LowIntelligence["@value"] === "false") &&
      (!data.OtherCognition["@value"] ||
        data.OtherCognition["@value"] === "false")
    ) {
      if (errs) errs.MSCognition = "At least one value is required.";
      else
        errs = {
          MSCognition: "At least one value is required.",
        };
    }

    console.log("Validating MSInsight");

    if (
      (!data.AppropriateInsight["@value"] ||
        data.AppropriateInsight["@value"] === "false") &&
      (!data.Fair["@value"] || data.Fair["@value"] === "false") &&
      (!data.Impaired["@value"] || data.Impaired["@value"] === "false") &&
      (!data.OtherInsight["@value"] || data.OtherInsight["@value"] === "false")
    ) {
      if (errs) errs.MSInsight = "At least one value is required.";
      else
        errs = {
          MSInsight: "At least one value is required.",
        };
    }

    if (
      data.SelfAbusiveBehavior["@value"] === "1" ||
      data.SelfAbusiveBehavior["@value"] === "2" ||
      data.SelfAbusiveBehavior["@value"] === "3" ||
      data.SelfAbusiveBehavior["@value"] === "4" ||
      data.SuicidalThreats["@value"] === "1" ||
      data.SuicidalThreats["@value"] === "2" ||
      data.SuicidalThreats["@value"] === "3" ||
      data.SuicidalThreats["@value"] === "4" ||
      data.SuicideAttempt["@value"] === "1" ||
      data.SuicideAttempt["@value"] === "2" ||
      data.SuicideAttempt["@value"] === "3" ||
      data.SuicideAttempt["@value"] === "4"
    ) {
      console.log("Validating RiskFactors");

      if (
        (!data.PriorAttempt["@value"] ||
          data.PriorAttempt["@value"] === "false") &&
        (!data.FamilyHistory["@value"] ||
          data.FamilyHistory["@value"] === "false") &&
        (!data.AccessToMeans["@value"] ||
          data.AccessToMeans["@value"] === "false") &&
        (!data.Exposure["@value"] || data.Exposure["@value"] === "false") &&
        (!data.HealthCondition["@value"] ||
          data.HealthCondition["@value"] === "false") &&
        (!data.ElevatingBeliefs["@value"] ||
          data.ElevatingBeliefs["@value"] === "false") &&
        (!data.MentalHealthDisorder["@value"] ||
          data.MentalHealthDisorder["@value"] === "false") &&
        (!data.OtherRiskFactors ||
          !data.OtherRiskFactors["@value"] ||
          data.OtherRiskFactors["@value"] === "false") &&
        (!data.NoRiskFactors ||
          !data.NoRiskFactors["@value"] ||
          data.NoRiskFactors["@value"] === "false")
      ) {
        if (errs) errs.RiskFactors = "At least one Risk Factor is required.";
        else
          errs = {
            RiskFactors: "At least one Risk Factor is required.",
          };
      }

      console.log("Validating SelfHarmProtectiveFactors");

      if (
        (!data.ClinicalCare["@value"] ||
          data.ClinicalCare["@value"] === "false") &&
        (!data.Connected["@value"] || data.Connected["@value"] === "false") &&
        (!data.Skills["@value"] || data.Skills["@value"] === "false") &&
        (!data.CaregiverContacts["@value"] ||
          data.CaregiverContacts["@value"] === "false") &&
        (!data.DiscouragingBeliefs["@value"] ||
          data.DiscouragingBeliefs["@value"] === "false") &&
        (!data.NoSelfHarmProtectiveFactors ||
          !data.NoSelfHarmProtectiveFactors["@value"] ||
          data.NoSelfHarmProtectiveFactors["@value"] === "false")
      ) {
        if (errs)
          errs.SelfHarmProtectiveFactors =
            "At least one Protective Factor is required.";
        else
          errs = {
            SelfHarmProtectiveFactors:
              "At least one Protective Factor is required.",
          };
      }

      console.log("Validating WarningSigns");

      if (
        data.Threatening?.["@value"] !== "true" &&
        data.SeekingMeans?.["@value"] !== "true" &&
        data.Hopelessness?.["@value"] !== "true" &&
        data.IncreasedSubstanceUse?.["@value"] !== "true" &&
        data.DramaticMoodChanges?.["@value"] !== "true" &&
        data.GivingAwayPossessions?.["@value"] !== "true" &&
        data.SayingGoodbye?.["@value"] !== "true" &&
        data.SocialWithdrawal?.["@value"] !== "true" &&
        data.RecklessBehavior?.["@value"] !== "true" &&
        data.Feelings?.["@value"] !== "true" &&
        data.NoWarningSigns?.["@value"] !== "true"
      ) {
        if (errs)
          errs.WarningSigns = "At least one Current Warning Sign is required.";
        else
          errs = {
            WarningSigns: "At least one Current Warning Sign is required.",
          };
      }
    }

    console.log("Validating TraumaSymptoms");

    if (
      data.TraumaSymptoms !== undefined &&
      data.TraumaSymptoms["@value"] === "true" &&
      (data.TraumaticExposure === undefined ||
        !data.TraumaticExposure["@value"] ||
        data.TraumaticExposure["@value"] === "false") &&
      (data.AvoidantTrauma === undefined ||
        !data.AvoidantTrauma["@value"] ||
        data.AvoidantTrauma["@value"] === "false") &&
      (data.HypervigilanceTrauma === undefined ||
        !data.HypervigilanceTrauma["@value"] ||
        data.HypervigilanceTrauma["@value"] === "false") &&
      (data.IntrusiveDreams === undefined ||
        !data.IntrusiveDreams["@value"] ||
        data.IntrusiveDreams["@value"] === "false") &&
      (data.NegativeThoughts === undefined ||
        !data.NegativeThoughts["@value"] ||
        data.NegativeThoughts["@value"] === "false") &&
      (data.CocentrationProblems === undefined ||
        !data.CocentrationProblems["@value"] ||
        data.CocentrationProblems["@value"] === "false") &&
      (data.RecklessBehaviorTrauma === undefined ||
        !data.RecklessBehaviorTrauma["@value"] ||
        data.RecklessBehaviorTrauma["@value"] === "false") &&
      (data.SleepDisturbancesTrauma === undefined ||
        !data.SleepDisturbancesTrauma["@value"] ||
        data.SleepDisturbancesTrauma["@value"] === "false") &&
      (data.OtherTrauma === undefined ||
        !data.OtherTrauma["@value"] ||
        data.OtherTrauma["@value"] === "false")
    ) {
      if (errs)
        errs.TraumaSymptoms = "At least one Trauma Symptom is required.";
      else
        errs = {
          TraumaSymptoms: "At least one Trauma Symptom is required.",
        };
    }

    console.log("Validating AnxietyDisorder");

    if (
      data.AnxietyDisorder !== undefined &&
      data.AnxietyDisorder["@value"] === "true" &&
      (data.AnxietyDisorderExcessWorry === undefined ||
        !data.AnxietyDisorderExcessWorry["@value"] ||
        data.AnxietyDisorderExcessWorry["@value"] === "false") &&
      (data.AnxietyDisorderEasilyFatigued === undefined ||
        !data.AnxietyDisorderEasilyFatigued["@value"] ||
        data.AnxietyDisorderEasilyFatigued["@value"] === "false") &&
      (data.MuscleTension === undefined ||
        !data.MuscleTension["@value"] ||
        data.MuscleTension["@value"] === "false") &&
      (data.Overworried === undefined ||
        !data.Overworried["@value"] ||
        data.Overworried["@value"] === "false") &&
      (data.AnxietyDisorderDifficultyConcentrating === undefined ||
        !data.AnxietyDisorderDifficultyConcentrating["@value"] ||
        data.AnxietyDisorderDifficultyConcentrating["@value"] === "false") &&
      (data.SleepDisturbancesAnxiety === undefined ||
        !data.SleepDisturbancesAnxiety["@value"] ||
        data.SleepDisturbancesAnxiety["@value"] === "false") &&
      (data.RestlessAnxiety === undefined ||
        !data.RestlessAnxiety["@value"] ||
        data.RestlessAnxiety["@value"] === "false") &&
      (data.Irritability === undefined ||
        !data.Irritability["@value"] ||
        data.Irritability["@value"] === "false") &&
      (data.OtherAnxiety === undefined ||
        !data.OtherAnxiety["@value"] ||
        data.OtherAnxiety["@value"] === "false")
    ) {
      if (errs)
        errs.AnxietyDisorder = "At least one Anxiety Symptom is required.";
      else
        errs = {
          AnxietyDisorder: "At least one Anxiety Symptom is required.",
        };
    }

    console.log("Validating DepressionSymptoms");

    if (
      data.DepressionSymptoms !== undefined &&
      data.DepressionSymptoms["@value"] === "true" &&
      (data.DepressedMood === undefined ||
        !data.DepressedMood["@value"] ||
        data.DepressedMood["@value"] === "false") &&
      (data.SleepChanges === undefined ||
        !data.SleepChanges["@value"] ||
        data.SleepChanges["@value"] === "false") &&
      (data.Guilt === undefined ||
        !data.Guilt["@value"] ||
        data.Guilt["@value"] === "false") &&
      (data.DiminishedInterest === undefined ||
        !data.DiminishedInterest["@value"] ||
        data.DiminishedInterest["@value"] === "false") &&
      (data.PurposelessMovement === undefined ||
        !data.PurposelessMovement["@value"] ||
        data.PurposelessMovement["@value"] === "false") &&
      (data.WeightChanges === undefined ||
        !data.WeightChanges["@value"] ||
        data.WeightChanges["@value"] === "false") &&
      (data.FatigueDepression === undefined ||
        !data.FatigueDepression["@value"] ||
        data.FatigueDepression["@value"] === "false") &&
      (data.ThoughtsOfDeath === undefined ||
        !data.ThoughtsOfDeath["@value"] ||
        data.ThoughtsOfDeath["@value"] === "false") &&
      (data.OtherDepression === undefined ||
        !data.OtherDepression["@value"] ||
        data.OtherDepression["@value"] === "false")
    ) {
      if (errs)
        errs.DepressionSymptoms =
          "At least one Depression Symptom is required.";
      else
        errs = {
          DepressionSymptoms: "At least one Depression Symptom is required.",
        };
    }

    console.log("Validating AttentionHyperactivity");

    if (
      data.AttentionHyperactivity !== undefined &&
      data.AttentionHyperactivity["@value"] === "true" &&
      (data.AttentionHyperactivityEasilyDistracted === undefined ||
        !data.AttentionHyperactivityEasilyDistracted["@value"] ||
        data.AttentionHyperactivityEasilyDistracted["@value"] === "false") &&
      (data.DifficultyFollowThrough === undefined ||
        !data.DifficultyFollowThrough["@value"] ||
        data.DifficultyFollowThrough["@value"] === "false") &&
      (data.Forgetful === undefined ||
        !data.Forgetful["@value"] ||
        data.Forgetful["@value"] === "false") &&
      (data.DifficultyListening === undefined ||
        !data.DifficultyListening["@value"] ||
        data.DifficultyListening["@value"] === "false") &&
      (data.DisorganizedAttention === undefined ||
        !data.DisorganizedAttention["@value"] ||
        data.DisorganizedAttention["@value"] === "false") &&
      (data.Fidgety === undefined ||
        !data.Fidgety["@value"] ||
        data.Fidgety["@value"] === "false") &&
      (data.LackOfDetails === undefined ||
        !data.LackOfDetails["@value"] ||
        data.LackOfDetails["@value"] === "false") &&
      (data.LosesThings === undefined ||
        !data.LosesThings["@value"] ||
        data.LosesThings["@value"] === "false") &&
      (data.InterruptsOthers === undefined ||
        !data.InterruptsOthers["@value"] ||
        data.InterruptsOthers["@value"] === "false") &&
      (data.UnableToSitStill === undefined ||
        !data.UnableToSitStill["@value"] ||
        data.UnableToSitStill["@value"] === "false") &&
      (data.ExcessiveTalking === undefined ||
        !data.ExcessiveTalking["@value"] ||
        data.ExcessiveTalking["@value"] === "false") &&
      (data.DifficultyWaiting === undefined ||
        !data.DifficultyWaiting["@value"] ||
        data.DifficultyWaiting["@value"] === "false") &&
      (data.OtherAttention === undefined ||
        !data.OtherAttention["@value"] ||
        data.OtherAttention["@value"] === "false")
    ) {
      if (errs)
        errs.AttentionHyperactivity =
          "At least one Attention and Hyperactivity Symptom is required.";
      else
        errs = {
          AttentionHyperactivity:
            "At least one Attention and Hyperactivity Symptom is required.",
        };
    }

    console.log("Validating SubstanceUse");

    if (
      data.SU !== undefined &&
      data.SU["@value"] === "true" &&
      (data.PatternOfSubstanceUse === undefined ||
        !data.PatternOfSubstanceUse["@value"] ||
        data.PatternOfSubstanceUse["@value"] === "false") &&
      (data.DesiresCutDownUse === undefined ||
        !data.DesiresCutDownUse["@value"] ||
        data.DesiresCutDownUse["@value"] === "false") &&
      (data.TimeSpentObtaining === undefined ||
        !data.TimeSpentObtaining["@value"] ||
        data.TimeSpentObtaining["@value"] === "false") &&
      (data.Cravings === undefined ||
        !data.Cravings["@value"] ||
        data.Cravings["@value"] === "false") &&
      (data.FailureOfResponsibilities === undefined ||
        !data.FailureOfResponsibilities["@value"] ||
        data.FailureOfResponsibilities["@value"] === "false") &&
      (data.NegativeImpact === undefined ||
        !data.NegativeImpact["@value"] ||
        data.NegativeImpact["@value"] === "false") &&
      (data.AbandonsActivities === undefined ||
        !data.AbandonsActivities["@value"] ||
        data.AbandonsActivities["@value"] === "false") &&
      (data.Tolerance === undefined ||
        !data.Tolerance["@value"] ||
        data.Tolerance["@value"] === "false") &&
      (data.SUWithdrawal === undefined ||
        !data.SUWithdrawal["@value"] ||
        data.SUWithdrawal["@value"] === "false") &&
      (data.SUOther === undefined ||
        !data.SUOther["@value"] ||
        data.SUOther["@value"] === "false")
    ) {
      if (errs) errs.SU = "At least one Substance Use Symptom is required.";
      else
        errs = {
          SU: "At least one Substance Use Symptom is required.",
        };
    }

    console.log("Validating DMDD");

    if (
      data.DMDD !== undefined &&
      data.DMDD["@value"] === "true" &&
      (data.Temper === undefined ||
        !data.Temper["@value"] ||
        data.Temper["@value"] === "false") &&
      (data.OutsideOutbursts === undefined ||
        !data.OutsideOutbursts["@value"] ||
        data.OutsideOutbursts["@value"] === "false") &&
      (data.FrequentOutbursts === undefined ||
        !data.FrequentOutbursts["@value"] ||
        data.FrequentOutbursts["@value"] === "false") &&
      (data.DMDDIrritable === undefined ||
        !data.DMDDIrritable["@value"] ||
        data.DMDDIrritable["@value"] === "false") &&
      (data.OutburstsAroundOthers === undefined ||
        !data.OutburstsAroundOthers["@value"] ||
        data.OutburstsAroundOthers["@value"] === "false") &&
      (data.DMDDOther === undefined ||
        !data.DMDDOther["@value"] ||
        data.DMDDOther["@value"] === "false")
    ) {
      if (errs)
        errs.DMDD =
          "At least one Disruptive Mood Dysregulation Disorder Symptom is required.";
      else
        errs = {
          DMDD: "At least one Disruptive Mood Dysregulation Disorder Symptom is required.",
        };
    }

    console.log("Validating ConductDisorder");

    if (
      data.ConductDisorder !== undefined &&
      data.ConductDisorder["@value"] === "true" &&
      (data.Aggression === undefined ||
        !data.Aggression["@value"] ||
        data.Aggression["@value"] === "false") &&
      (data.DestructionOfProperty === undefined ||
        !data.DestructionOfProperty["@value"] ||
        data.DestructionOfProperty["@value"] === "false") &&
      (data.Theft === undefined ||
        !data.Theft["@value"] ||
        data.Theft["@value"] === "false") &&
      (data.SeriousViolations === undefined ||
        !data.SeriousViolations["@value"] ||
        data.SeriousViolations["@value"] === "false") &&
      (data.OtherConductDisorder === undefined ||
        !data.OtherConductDisorder["@value"] ||
        data.OtherConductDisorder["@value"] === "false")
    ) {
      if (errs)
        errs.ConductDisorder =
          "At least one Conduct Disorder Symptom is required.";
      else
        errs = {
          ConductDisorder: "At least one Conduct Disorder Symptom is required.",
        };
    }

    console.log("Validating ODD");

    if (
      data.ODD !== undefined &&
      data.ODD["@value"] === "true" &&
      (data.IrritableODD === undefined ||
        !data.IrritableODD["@value"] ||
        data.IrritableODD["@value"] === "false") &&
      (data.Argumentative === undefined ||
        !data.Argumentative["@value"] ||
        data.Argumentative["@value"] === "false") &&
      (data.Vindictive === undefined ||
        !data.Vindictive["@value"] ||
        data.Vindictive["@value"] === "false") &&
      (data.OtherODD === undefined ||
        !data.OtherODD["@value"] ||
        data.OtherODD["@value"] === "false")
    ) {
      if (errs)
        errs.ODD =
          "At least one Oppositional Defiance Disorder Symptom is required.";
      else
        errs = {
          ODD: "At least one Oppositional Defiance Disorder Symptom is required.",
        };
    }

    console.log("Validating OCD");

    if (
      data.OCD !== undefined &&
      data.OCD["@value"] === "true" &&
      (data.OCDPresence === undefined ||
        !data.OCDPresence["@value"] ||
        data.OCDPresence["@value"] === "false") &&
      (data.Obsessions === undefined ||
        !data.Obsessions["@value"] ||
        data.Obsessions["@value"] === "false") &&
      (data.TimeConsuming === undefined ||
        !data.TimeConsuming["@value"] ||
        data.TimeConsuming["@value"] === "false") &&
      (data.Compulsions === undefined ||
        !data.Compulsions["@value"] ||
        data.Compulsions["@value"] === "false") &&
      (data.ReduceAnxiety === undefined ||
        !data.ReduceAnxiety["@value"] ||
        data.ReduceAnxiety["@value"] === "false") &&
      (data.SuppressUrges === undefined ||
        !data.SuppressUrges["@value"] ||
        data.SuppressUrges["@value"] === "false") &&
      (data.OCDOther === undefined ||
        !data.OCDOther["@value"] ||
        data.OCDOther["@value"] === "false")
    ) {
      if (errs)
        errs.OCD =
          "At least one Obsessive-Compulsive Disorder Symptom is required.";
      else
        errs = {
          OCD: "At least one Obsessive-Compulsive Disorder Symptom is required.",
        };
    }

    console.log("Validating PanicDisorder");

    if (
      data.PanicDisorder !== undefined &&
      data.PanicDisorder["@value"] === "true" &&
      (data.PoundingHeart === undefined ||
        !data.PoundingHeart["@value"] ||
        data.PoundingHeart["@value"] === "false") &&
      (data.PanicDisorderSweating === undefined ||
        !data.PanicDisorderSweating["@value"] ||
        data.PanicDisorderSweating["@value"] === "false") &&
      (data.Trembling === undefined ||
        !data.Trembling["@value"] ||
        data.Trembling["@value"] === "false") &&
      (data.ShortnessOfBreath === undefined ||
        !data.ShortnessOfBreath["@value"] ||
        data.ShortnessOfBreath["@value"] === "false") &&
      (data.Choking === undefined ||
        !data.Choking["@value"] ||
        data.Choking["@value"] === "false") &&
      (data.ChestPain === undefined ||
        !data.ChestPain["@value"] ||
        data.ChestPain["@value"] === "false") &&
      (data.PanicDisorderNausea === undefined ||
        !data.PanicDisorderNausea["@value"] ||
        data.PanicDisorderNausea["@value"] === "false") &&
      (data.Dizzy === undefined ||
        !data.Dizzy["@value"] ||
        data.Dizzy["@value"] === "false") &&
      (data.Chills === undefined ||
        !data.Chills["@value"] ||
        data.Chills["@value"] === "false") &&
      (data.Derealization === undefined ||
        !data.Derealization["@value"] ||
        data.Derealization["@value"] === "false") &&
      (data.FearOfCrazy === undefined ||
        !data.FearOfCrazy["@value"] ||
        data.FearOfCrazy["@value"] === "false") &&
      (data.FearOfDying === undefined ||
        !data.FearOfDying["@value"] ||
        data.FearOfDying["@value"] === "false") &&
      (data.PanicDisorderOther === undefined ||
        !data.PanicDisorderOther["@value"] ||
        data.PanicDisorderOther["@value"] === "false")
    ) {
      if (errs)
        errs.PanicDisorder = "At least one Panic Disorder Symptom is required.";
      else
        errs = {
          PanicDisorder: "At least one Panic Disorder Symptom is required.",
        };
    }

    console.log("Validating Phobias");

    if (
      data.Phobias !== undefined &&
      data.Phobias["@value"] === "true" &&
      (data.PhobiasSpecificFear === undefined ||
        !data.PhobiasSpecificFear["@value"] ||
        data.PhobiasSpecificFear["@value"] === "false") &&
      (data.ImmediateFear === undefined ||
        !data.ImmediateFear["@value"] ||
        data.ImmediateFear["@value"] === "false") &&
      (data.PhobiasAvoidance === undefined ||
        !data.PhobiasAvoidance["@value"] ||
        data.PhobiasAvoidance["@value"] === "false") &&
      (data.FearExaggerated === undefined ||
        !data.FearExaggerated["@value"] ||
        data.FearExaggerated["@value"] === "false") &&
      (data.PhobiasOther === undefined ||
        !data.PhobiasOther["@value"] ||
        data.PhobiasOther["@value"] === "false")
    ) {
      if (errs) errs.Phobias = "At least one Phobia Symptom is required.";
      else
        errs = {
          Phobias: "At least one Phobia Symptom is required.",
        };
    }

    console.log("Validating DI");

    if (
      data.DI !== undefined &&
      data.DI["@value"] === "true" &&
      (data.DILowIQ === undefined ||
        !data.DILowIQ["@value"] ||
        data.DILowIQ["@value"] === "false") &&
      (data.DISensoryImpairments === undefined ||
        !data.DISensoryImpairments["@value"] ||
        data.DISensoryImpairments["@value"] === "false") &&
      (data.DISpeechDelays === undefined ||
        !data.DISpeechDelays["@value"] ||
        data.DISpeechDelays["@value"] === "false") &&
      (data.DIMotorCoordinationDelays === undefined ||
        !data.DIMotorCoordinationDelays["@value"] ||
        data.DIMotorCoordinationDelays["@value"] === "false") &&
      (data.DILearningDisorder === undefined ||
        !data.DILearningDisorder["@value"] ||
        data.DILearningDisorder["@value"] === "false") &&
      (data.DISpatialDelays === undefined ||
        !data.DISpatialDelays["@value"] ||
        data.DISpatialDelays["@value"] === "false") &&
      (data.DevelopmentalImpairmentsOther === undefined ||
        !data.DevelopmentalImpairmentsOther["@value"] ||
        data.DevelopmentalImpairmentsOther["@value"] === "false")
    ) {
      if (errs) errs.DI = "At least one Developmental Impairment is required.";
      else
        errs = {
          DI: "At least one Developmental Impairment is required.",
        };
    }

    console.log("Validating Bipolar");

    if (
      data.Bipolar["@value"] === "true" &&
      !moment(formsyDetails.CreatedOn).isBefore(moment("3/29/2022"))
    ) {
      if (
        data.ManicEpisodes?.["@value"] !== "true" &&
        data.DepressiveEpisodes?.["@value"] !== "true" &&
        data.SignificantMoodChanges?.["@value"] !== "true" &&
        data.BipolarOther?.["@value"] !== "true"
      ) {
        if (errs) errs.Bipolar = "At least one Bipolar Symptom is required.";
        else
          errs = {
            Bipolar: "At least one Bipolar Symptom is required.",
          };
      }

      if (!data.BipolarFrequency["@value"]) {
        if (errs) errs.BipolarFrequency = '"BipolarFrequency" is required.';
        else
          errs = {
            BipolarFrequency: '"BipolarFrequency" is required.',
          };
      }
      if (!data.BipolarIntensity["@value"]) {
        if (errs) errs.BipolarIntensity = '"BipolarIntensity" is required.';
        else
          errs = {
            BipolarIntensity: '"BipolarIntensity" is required.',
          };
      }
      if (!data.BipolarDuration["@value"]) {
        if (errs) errs.BipolarDuration = '"BipolarDuration" is required.';
        else
          errs = {
            BipolarDuration: '"BipolarDuration" is required.',
          };
      }
    }

    console.log("Validation Borderline");

    if (
      data.Borderline?.["@value"] === "true" &&
      data.AvoidAbandonment?.["@value"] !== "true" &&
      data.UnstableRelationships?.["@value"] !== "true" &&
      data.IdentityDisturbances?.["@value"] !== "true" &&
      data.Impulsivity?.["@value"] !== "true" &&
      data.RecurrentSelfHarm?.["@value"] !== "true" &&
      data.AffectiveInstability?.["@value"] !== "true" &&
      data.Emptiness?.["@value"] !== "true" &&
      data.PoorAngerManagement?.["@value"] !== "true" &&
      data.ParanoidIdeation?.["@value"] !== "true" &&
      data.BorderlineOther?.["@value"] !== "true"
    ) {
      if (errs)
        errs.Bipolar =
          "At least one Borderline Personality Disorder Symptom is required.";
      else
        errs = {
          Bipolar:
            "At least one Borderline Personality Disorder Symptom is required.",
        };
    }

    console.log("Validating NotPlacedReason");

    if (
      data.LevelOfCareRecommendedLevel["@value"] !== undefined &&
      data.LevelOfCarePlacedLevel["@value"] !== undefined &&
      data.LevelOfCareRecommendedLevel["@value"] !==
        data.LevelOfCarePlacedLevel["@value"] &&
      (data.NotPlacedReason["@value"] === undefined ||
        data.NotPlacedReason["@value"] === "")
    ) {
      if (errs) errs.NotPlacedReason = "Not Placed Reason is required.";
      else
        errs = {
          NotPlacedReason: "Not Placed Reason is required.",
        };
    }

    console.log("Validating AssessedNeeds");

    if (
      (!data.SkillsTraining["@value"] ||
        data.SkillsTraining["@value"] === "false") &&
      (!data.SymptomManagementSkills["@value"] ||
        data.SymptomManagementSkills["@value"] === "false") &&
      (!data.DecisionMaking["@value"] ||
        data.DecisionMaking["@value"] === "false") &&
      (!data.FinancialSupport["@value"] ||
        data.FinancialSupport["@value"] === "false") &&
      (!data.CommunitySupport["@value"] ||
        data.CommunitySupport["@value"] === "false") &&
      (!data.Mentoring["@value"] || data.Mentoring["@value"] === "false") &&
      (!data.FamilySupport["@value"] ||
        data.FamilySupport["@value"] === "false") &&
      (!data.FosterFamilySupport["@value"] ||
        data.FosterFamilySupport["@value"] === "false") &&
      (!data.Respite["@value"] || data.Respite["@value"] === "false") &&
      (!data.MedicationManagement["@value"] ||
        data.MedicationManagement["@value"] === "false") &&
      (!data.MedicalDentalVision["@value"] ||
        data.MedicalDentalVision["@value"] === "false") &&
      (!data.HealthyHabits["@value"] ||
        data.HealthyHabits["@value"] === "false") &&
      (!data.EducationSchool["@value"] ||
        data.EducationSchool["@value"] === "false") &&
      (!data.Vocational["@value"] || data.Vocational["@value"] === "false") &&
      (!data.Occupational["@value"] ||
        data.Occupational["@value"] === "false") &&
      (!data.ProsocialActivities["@value"] ||
        data.ProsocialActivities["@value"] === "false") &&
      (!data.CaseManagement["@value"] ||
        data.CaseManagement["@value"] === "false") &&
      (!data.SocialSkills["@value"] || data.SocialSkills["@value"] === "false")
    ) {
      if (errs) errs.AssessedNeeds = "At least one Assessed Need is required.";
      else
        errs = {
          AssessedNeeds: "At least one Assessed Need is required.",
        };
    }

    // if (
    //   (!data.MorePsychiatric["@value"] ||
    //     data.MorePsychiatric["@value"] === "false") &&
    //   (!data.MorePsychological["@value"] ||
    //     data.MorePsychological["@value"] === "false") &&
    //   (!data.MoreNeurological["@value"] ||
    //     data.MoreNeurological["@value"] === "false") &&
    //   (!data.MoreEducational["@value"] ||
    //     data.MoreEducational["@value"] === "false") &&
    //   (!data.MoreMedical["@value"] || data.MoreMedical["@value"] === "false") &&
    //   (!data.MoreDental["@value"] || data.MoreDental["@value"] === "false") &&
    //   (!data.MoreSubstanceUse["@value"] ||
    //     data.MoreSubstanceUse["@value"] === "false") &&
    //   (!data.MoreVocational["@value"] ||
    //     data.MoreVocational["@value"] === "false") &&
    //   (!data.MoreOccupational["@value"] ||
    //     data.MoreOccupational["@value"] === "false") &&
    //   (!data.MoreVisualAuditory["@value"] ||
    //     data.MoreVisualAuditory["@value"] === "false") &&
    //   (!data.MoreNutritional["@value"] ||
    //     data.MoreNutritional["@value"] === "false") &&
    //   (!data.MoreOther["@value"] || data.MoreOther["@value"] === "false")
    // ) {
    //   if (errs)
    //     errs.FurtherEvalution =
    //       "At least one Further Evaluation Need is required.";
    //   else
    //     errs = {
    //       FurtherEvalution: "At least one Further Evaluation Need is required.",
    //     };
    // }

    setErrors(errs);
    return errs;
  };

  const sectionsToNavigate = [
    { _id: "PresentingProblem", name: "Presenting Problem & Strengths" },
    { _id: "FamilyHistorySection", name: "Family Environment and History" },
    { _id: "Demographics", name: "Culture/Ethnicity/Religion/Spirituality" },
    { _id: "DevelopmentalHistory", name: "Developmental History" },
    { _id: "Education", name: "Education/Employment" },
    { _id: "Activities", name: "Activities of Daily Living" },
    {
      _id: "Friendships",
      name: "Friendship/Social Peer Support/Relationships",
    },
    { _id: "LegalHistory", name: "Legal History" },
    { _id: "MedicalHistory", name: "Medical History/Medications" },
    {
      _id: "SexualHistory",
      name: "Sexual History to Include Pertinent Sexual Issues/Concerns",
    },
    {
      _id: "MentalHealthTreatmentHistory",
      name: "Mental Health Treatment History",
    },
    { _id: "SubstanceUseHistory", name: "Alcohol/Drug Use History" },
    { _id: "TraumaHistory", name: "Trauma History" },
    { _id: "MentalStatus", name: "Mental Status" },
    { _id: "Lethality", name: "Lethality" },
    { _id: "SymptomChecklist", name: "Symptom Checklist" },
    { _id: "OMHAS", name: "OHIO MENTAL HEALTH & ADDICTION SERVICES" },
    { _id: "ClinicalNarrative", name: "Clinical Narrative Summary" },
    { _id: "Diagnosis", name: "Diagnosis" },
    {
      _id: "TreatmentRecommendations",
      name: "Treatment Recommendations/Needs",
    },
  ];

  ////UI
  return (
    <>
      <BackIcon />
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          customValidate={customValidate}
          sectionsToNavigate={sectionsToNavigate}
          beforePrintFunction={(callback) => {
            handleExpandStatusChange(true, callback);
          }}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              {!formDisabled ||
              (formDisabled &&
                data.SexAssignedAtBirth !== undefined &&
                data.Pronouns !== undefined &&
                data.GenderIdentity !== undefined) ? (
                <div className="row">
                  <div className="col">
                    <Select
                      name="SexAssignedAtBirth"
                      label="Sex Assigned at Birth:"
                      error={errors.SexAssignedAtBirth}
                      value={
                        data.SexAssignedAtBirth !== undefined &&
                        data.SexAssignedAtBirth["@value"]
                      }
                      options={sexAtBirthOptions}
                      onChange={handleChange}
                      identical={compare("SexAssignedAtBirth")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SexAssignedAtBirth !== undefined &&
                      data.SexAssignedAtBirth["@value"] ===
                        "Sex not listed" && (
                        <Input
                          name="SexAssignedAtBirthDetails"
                          label="Please specify:"
                          error={errors.SexAssignedAtBirthDetails}
                          value={
                            data.SexAssignedAtBirthDetails !== undefined
                              ? data.SexAssignedAtBirthDetails["@value"]
                              : ""
                          }
                          onChange={handleChange}
                          identical={compare("SexAssignedAtBirthDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                  </div>
                  <div className="col">
                    <Select
                      name="Pronouns"
                      label="Pronouns:"
                      error={errors.Pronouns}
                      value={
                        data.Pronouns !== undefined && data.Pronouns["@value"]
                      }
                      options={pronounOptions}
                      onChange={handleChange}
                      identical={compare("Pronouns")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.Pronouns !== undefined &&
                      data.Pronouns["@value"] === "Prefer to self-describe" && (
                        <Input
                          name="PronounsDescription"
                          label="Please describe:"
                          error={errors.PronounsDescription}
                          value={
                            data.PronounsDescription !== undefined
                              ? data.PronounsDescription["@value"]
                              : ""
                          }
                          onChange={handleChange}
                          identical={compare("PronounsDescription")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                  </div>
                  <div className="col">
                    <Select
                      name="GenderIdentity"
                      label="Gender Identity:"
                      error={errors.GenderIdentity}
                      value={
                        data.GenderIdentity !== undefined &&
                        data.GenderIdentity["@value"]
                      }
                      options={genderIdentityOptions}
                      onChange={handleChange}
                      identical={compare("GenderIdentity")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.GenderIdentity !== undefined &&
                      data.GenderIdentity["@value"] ===
                        "Identity not listed" && (
                        <Input
                          name="GenderIdentityDescription"
                          label="Please specify:"
                          error={errors.GenderIdentityDescription}
                          value={
                            data.GenderIdentityDescription !== undefined
                              ? data.GenderIdentityDescription["@value"]
                              : ""
                          }
                          onChange={handleChange}
                          identical={compare("GenderIdentityDescription")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                  </div>
                </div>
              ) : null}
              <div className="row">
                <div className="col">
                  <Input
                    name="DateCompleted"
                    label="Date Completed:"
                    type="date"
                    error={errors.DateCompleted}
                    value={data.DateCompleted["@value"]}
                    onChange={handleChange}
                    identical={compare("DateCompleted")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Select
                    name="Division"
                    label="Division:"
                    error={errors.Division}
                    value={data.Division["@value"]}
                    options={divisionOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <RadioButton
                name="PSDType"
                label="Type of PSD:"
                error={errors.PSDType}
                value={data.PSDType["@value"]}
                options={psdTypeOptions}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.PSDType["@value"] === "2" ? (
                <>
                  <Input
                    name="ReviewReason"
                    label="Reason:"
                    error={errors.ReviewReason}
                    value={getFieldValue(data.ReviewReason)}
                    onChange={handleChange}
                    identical={compare("ReviewReason")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <RadioButton
                    name="ChangeInDiagnosis"
                    label="Change in Diagnosis?"
                    error={errors.ChangeInDiagnosis}
                    value={
                      data.ChangeInDiagnosis !== undefined &&
                      data.ChangeInDiagnosis["@value"]
                    }
                    options={yesNoOptions}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </>
              ) : null}
              <h5>Information Sources:</h5>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISClient"
                      label="Client"
                      checked={data.ISClient["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISParent"
                      label="Parent/Other"
                      checked={data.ISParent["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISFamily"
                      label="Family"
                      checked={data.ISFamily["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISGuardian"
                      label="Guardian"
                      checked={data.ISGuardian["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISCaregiver"
                      label="Non-parent Caregiver"
                      checked={data.ISCaregiver["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISPhysician"
                      label="Physician"
                      checked={data.ISPhysician["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISRecords"
                      label="Records"
                      checked={data.ISRecords["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISServiceProvider"
                      label="Service Provider"
                      checked={data.ISServiceProvider["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <Checkbox
                      name="ISSchool"
                      label="School"
                      checked={data.ISSchool["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISCourt"
                      label="Court"
                      checked={data.ISCourt["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISCPS"
                      label="CPS"
                      checked={data.ISCPS["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="ISOther"
                      label="Other"
                      checked={data.ISOther["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              {data.ISOther["@value"] === "true" ? (
                <Input
                  name="ISOtherDetails"
                  error={errors.ISOtherDetails}
                  value={data.ISOtherDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("ISOtherDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              ) : null}
              {errors.InformationSources !== undefined ? (
                <div className="row">
                  <div className="col">
                    <div className="alert alert-danger">
                      {errors.InformationSources}
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="clearfix">
                <div className="float-start">
                  <button
                    className="btn btn-primary mb-2"
                    onClick={(e) => {
                      handleExpandStatusChange();
                    }}
                  >
                    {expandStatus === true ? "Collapse All" : "Expand All"}
                  </button>
                </div>
                <div className="float-end d-flex">
                  <div className="me-2">Clinical Narrative Summary:</div>
                  <ToggleSwitch
                    values={["Show", "Hide"]}
                    selected={showNarrative}
                    onChange={setShowNarrative}
                  />
                </div>
              </div>
              <ExpandDiv
                id="PresentingProblem"
                expandStatus={expandStatus}
                className="mt-2"
                title="Presenting Problem &amp; Strengths"
              >
                <Input
                  name="PresentingProblem"
                  label="Presenting Problem (according to the caregiver and child):"
                  error={errors.PresentingProblem}
                  value={data.PresentingProblem["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("PresentingProblem")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="ClientStrengths"
                  label="Client strengths, abilities, and skills (narrative):"
                  error={errors.ClientStrengths}
                  value={data.ClientStrengths["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("ClientStrengths")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <Input
                  name="Supports"
                  label="Supports for the client or caregivers:"
                  error={errors.Supports}
                  value={data.Supports["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("Supports")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              <ExpandDiv
                id="FamilyHistorySection"
                expandStatus={expandStatus}
                className="mt-2"
                title="Family Environment and History"
              >
                <TextArea
                  name="CurrentPlacement"
                  label="Current placement (including relevant family/environmental factors or alternate living situations):"
                  error={errors.CurrentPlacement}
                  value={data.CurrentPlacement["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("CurrentPlacement")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="PlacementHistory"
                  label="Placement History"
                  error={errors.PlacementHistory}
                  value={data.PlacementHistory["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("PlacementHistory")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="AdditionalAssessment"
                  label="Additional assessment is warranted (e.g., genogram):"
                  options={yesNoOptions}
                  error={errors.AdditionalAssessment}
                  value={data.AdditionalAssessment["@value"]}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <h5>Family History</h5>
                <Checkbox
                  name="FamilyHistoryUnknown"
                  label="Family History Unknown"
                  divClassName="ms-3"
                  checked={
                    data.FamilyHistoryUnknown &&
                    data.FamilyHistoryUnknown["@value"] === "true"
                  }
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                <FieldTable
                  name="FamilyHistoryDetails"
                  commentsHandler={handleComments}
                >
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col py-2 border-end">Family Member</div>
                      <div className="col py-2 border-end">Concern/Issue</div>
                      <div className="col py-2">Explanation/Comments</div>
                    </div>
                    {data.FamilyHistoryDetails["@value"] &&
                      JSON.parse(data.FamilyHistoryDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col py-2 border-end d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <Input
                                    name="Name"
                                    value={item.Name}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "FamilyHistoryDetails"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  {item.Name}
                                </>
                              )}
                            </div>
                            <div className="col border-end py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <div className="container">
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`MentalHealth-${index}`}
                                        label="Mental Health"
                                        checked={item.MentalHealth === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <Checkbox
                                        name={`Medical-${index}`}
                                        label="Medical"
                                        checked={item.Medical === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Disability-${index}`}
                                        label="Disability"
                                        checked={item.Disability === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <Checkbox
                                        name={`SubstanceAbuse-${index}`}
                                        label="Substance Abuse"
                                        checked={item.SubstanceAbuse === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Suicidal-${index}`}
                                        label="Suicidal"
                                        checked={item.Suicidal === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <Checkbox
                                        name={`Legal-${index}`}
                                        label="Legal"
                                        checked={item.Legal === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Trauma-${index}`}
                                        label="Trauma"
                                        checked={item.Trauma === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <Checkbox
                                        name={`Perpetration-${index}`}
                                        label="Perpetration"
                                        checked={item.Perpetration === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Other-${index}`}
                                        label="Other"
                                        checked={item.Other === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                    <div className="col">
                                      <Checkbox
                                        name={`NoConcerns-${index}`}
                                        label="No Concerns / Issues"
                                        checked={item.NoConcerns === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "FamilyHistoryDetails"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                getFamilyMemberConcerns(item)
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <TextArea
                                  name="Comments"
                                  value={item.Comments}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "FamilyHistoryDetails"
                                    );
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Comments
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("FamilyHistoryDetails", {
                          IsEditable: true,
                        });
                      }}
                    >
                      Add Family Member
                    </button>
                  ) : null}
                  {errors.FamilyHistoryDetails !== undefined ? (
                    <div className="alert alert-danger">
                      {errors.FamilyHistoryDetails}
                    </div>
                  ) : null}
                </FieldTable>
                <div className="container">
                  <div className="row pt-2 border-bottom">
                    <div className="col-3">Concerns of Domestic Violence:</div>
                    <div className="col-2">
                      <Checkbox
                        name="CurrentConcernsOfDV"
                        label="Current"
                        checked={data.CurrentConcernsOfDV["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <Checkbox
                        name="PastConcernsOfDV"
                        label="Past"
                        checked={data.PastConcernsOfDV["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <Checkbox
                        name="UnknownConcernsOfDV"
                        label="Unknown"
                        checked={data.UnknownConcernsOfDV["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-3">
                      <Checkbox
                        name="NoReportedConcernsOfDV"
                        label="None reported"
                        checked={
                          data.NoReportedConcernsOfDV["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {errors.DomesticViolenceConcerns !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.DomesticViolenceConcerns}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row pt-2 border-bottom">
                    <div className="col-3">Marital/Couples conflict:</div>
                    <div className="col-2">
                      <Checkbox
                        name="CurrentMaritalConflict"
                        label="Current"
                        checked={
                          data.CurrentMaritalConflict["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <Checkbox
                        name="PastMaritalConflict"
                        label="Past"
                        checked={data.PastMaritalConflict["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <Checkbox
                        name="UnknownMaritalConflict"
                        label="Unknown"
                        checked={
                          data.UnknownMaritalConflict["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-3">
                      <Checkbox
                        name="NoReportedMaritalConflict"
                        label="None reported"
                        checked={
                          data.NoReportedMaritalConflict["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {errors.CouplesConflict !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.CouplesConflict}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row pt-2 border-bottom">
                    <div className="col-3">
                      Cooperation between caregivers on discipline and care:
                    </div>
                    <div className="col-2">
                      <RadioButton
                        name="CaregiverCooperation"
                        value={data.CaregiverCooperation["@value"]}
                        options={[{ _id: "1", name: "Yes" }]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <RadioButton
                        name="CaregiverCooperation"
                        value={data.CaregiverCooperation["@value"]}
                        options={[{ _id: "0", name: "No" }]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <RadioButton
                        name="CaregiverCooperation"
                        value={data.CaregiverCooperation["@value"]}
                        options={[{ _id: "NA", name: "N/A" }]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-3"></div>
                  </div>
                  {errors.CaregiverCooperation !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.CaregiverCooperation}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row pt-2 border-bottom">
                    <div className="col-3">
                      Difficulties with the supervision/monitoring of child:
                    </div>
                    <div className="col-2">
                      <RadioButton
                        name="SupervisionDifficulties"
                        value={data.SupervisionDifficulties["@value"]}
                        options={[{ _id: "1", name: "Yes" }]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <RadioButton
                        name="SupervisionDifficulties"
                        value={data.SupervisionDifficulties["@value"]}
                        options={[{ _id: "0", name: "No" }]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <RadioButton
                        name="SupervisionDifficulties"
                        value={data.SupervisionDifficulties["@value"]}
                        options={[{ _id: "NA", name: "N/A" }]}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-3"></div>
                  </div>
                  {errors.SupervisionDifficulties !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.SupervisionDifficulties}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row pt-2 border-bottom">
                    <div className="col-3">Discipline Practices:</div>
                    <div className="col-2">
                      <Checkbox
                        name="DisciplinePracticesEffective"
                        label="Effective"
                        checked={
                          data.DisciplinePracticesEffective["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <Checkbox
                        name="DisciplinePracticesInconsistent"
                        label="Inconsistent"
                        checked={
                          data.DisciplinePracticesInconsistent["@value"] ===
                          "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-2">
                      <Checkbox
                        name="DisciplinePracticesIneffective"
                        label="Ineffective"
                        checked={
                          data.DisciplinePracticesIneffective["@value"] ===
                          "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col-3">
                      <Checkbox
                        name="DisciplinePracticesNA"
                        label="N/A"
                        checked={
                          data.DisciplinePracticesNA &&
                          data.DisciplinePracticesNA["@value"] === "true"
                        }
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {errors.DisciplinePractices !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.DisciplinePractices}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row pt-2">
                    <div className="col-3">Comments:</div>
                    <div className="col-9">
                      <TextArea
                        name="FamilyHistoryComments"
                        error={errors.FamilyHistoryComments}
                        value={data.FamilyHistoryComments["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("FamilyHistoryComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="Demographics"
                expandStatus={expandStatus}
                className="mt-2"
                title="Culture/Ethnicity/Religion/Spirituality"
              >
                <div className="container">
                  <div className="row">
                    <div className="col">Identified race and ethnicity:</div>
                    <div className="col">
                      <Input
                        name="RaceEthnicity"
                        error={errors.RaceEthnicity}
                        value={data.RaceEthnicity["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("RaceEthnicity")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Places lived:</div>
                    <div className="col">
                      <Input
                        name="PlacesLived"
                        error={errors.PlacesLived}
                        value={data.PlacesLived["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PlacesLived")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Socioeconomic Status:</div>
                    <div className="col">
                      <Input
                        name="SocioeconomicStatus"
                        error={errors.SocioeconomicStatus}
                        value={data.SocioeconomicStatus["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SocioeconomicStatus")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Identified religion/belief system and role of faith in
                      client's life:
                    </div>
                    <div className="col">
                      <Input
                        name="Religion"
                        error={errors.Religion}
                        value={data.Religion["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("Religion")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Special cultural traditions/celebrations/rituals:
                    </div>
                    <div className="col">
                      <RadioButton
                        name="Traditions"
                        error={errors.Traditions}
                        value={data.Traditions["@value"]}
                        options={yesNoNoneOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.Traditions["@value"] === "1" && (
                        <Input
                          name="TraditionsComments"
                          label="Comments"
                          error={errors.TraditionsComments}
                          value={data.TraditionsComments["@value"]}
                          onChange={handleChange}
                          identical={compare("TraditionsComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      Cultural or Spiritual beliefs that influence
                      medical/mental health decisions:
                    </div>
                    <div className="col">
                      <RadioButton
                        name="MedicalBeliefs"
                        error={errors.MedicalBeliefs}
                        value={data.MedicalBeliefs["@value"]}
                        options={yesNoNoneOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.MedicalBeliefs["@value"] === "1" && (
                        <Input
                          name="MedicalBeliefsComments"
                          label="Comments"
                          error={errors.MedicalBeliefsComments}
                          value={data.MedicalBeliefsComments["@value"]}
                          onChange={handleChange}
                          identical={compare("MedicalBeliefsComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">Is this youth in Foster Care?</div>
                    <div className="col">
                      <RadioButton
                        name="InFosterCare"
                        error={errors.InFosterCare}
                        value={data.InFosterCare["@value"]}
                        options={yesNoOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {data.InFosterCare["@value"] === "1" && (
                    <div className="row">
                      <div className="col">
                        Differences between youth's and foster/placement
                        family's cultures:
                      </div>
                      <div className="col">
                        <RadioButton
                          name="CulturalDifferences"
                          error={errors.CulturalDifferences}
                          value={data.CulturalDifferences["@value"]}
                          options={yesNoNAOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  )}
                  {data.CulturalDifferences["@value"] === "1" && (
                    <div className="row">
                      <div className="col">Comments:</div>
                      <div className="col">
                        <Input
                          name="CulturalDifferencesComments"
                          error={errors.CulturalDifferencesComments}
                          value={data.CulturalDifferencesComments["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("CulturalDifferencesComments")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="DevelopmentalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Developmental History"
              >
                <h5>Mother's Pregnancy History:</h5>
                <Checkbox
                  name="PHNoProblems"
                  label="No problems reported"
                  divClassName="ms-3"
                  checked={data.PHNoProblems["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {(data.PHNoProblems["@value"] === undefined ||
                  data.PHNoProblems["@value"] === "false") && (
                  <>
                    <div className="container">
                      <div className="row">
                        <div className="col">Prenatal Care:</div>
                        <div className="col">
                          <RadioButton
                            name="PHPrenatalCare"
                            error={errors.PHPrenatalCare}
                            value={data.PHPrenatalCare["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          Exposure to alcohol, tobacco, or other drugs:
                        </div>
                        <div className="col">
                          <RadioButton
                            name="PHExposure"
                            error={errors.PHExposure}
                            value={data.PHExposure["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          History of DV or significant trauma during pregnancy:
                        </div>
                        <div className="col">
                          <RadioButton
                            name="PHTrauma"
                            error={errors.PHTrauma}
                            value={data.PHTrauma["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Birth complications:</div>
                        <div className="col">
                          <RadioButton
                            name="PHComplications"
                            error={errors.PHComplications}
                            value={data.PHComplications["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Medical concerns:</div>
                        <div className="col">
                          <RadioButton
                            name="PHConcerns"
                            error={errors.PHConcerns}
                            value={data.PHConcerns["@value"]}
                            options={yesNoUnknownOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">Comments:</div>
                        <div className="col">
                          <TextArea
                            name="PHComments"
                            error={errors.PHComments}
                            value={data.PHComments["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PHComments")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <b>Developmental delays or special needs:</b>
                    </div>
                    <div className="col">
                      <RadioButton
                        name="SpecialNeeds"
                        error={errors.SpecialNeeds}
                        value={data.SpecialNeeds["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {data.SpecialNeeds["@value"] === "1" && (
                    <div className="row">
                      <div className="col">
                        Describe (include type/age observed/age resolved):
                      </div>
                      <div className="col">
                        <TextArea
                          name="SpecialNeedsDetails"
                          error={errors.SpecialNeedsDetails}
                          value={data.SpecialNeedsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SpecialNeedsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  )}
                  <div className="row">
                    <div className="col">
                      <b>Specialized Services</b> (Speech/Occupational/Physical
                      Therapy):
                    </div>
                    <div className="col">
                      <RadioButton
                        name="SpecializedServices"
                        error={errors.SpecializedServices}
                        value={data.SpecializedServices["@value"]}
                        options={yesNoUnknownOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  {data.SpecializedServices["@value"] === "1" && (
                    <div className="row">
                      <div className="col">Comments:</div>
                      <div className="col">
                        <TextArea
                          name="SpecializedServicesDetails"
                          error={errors.SpecializedServicesDetails}
                          value={data.SpecializedServicesDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SpecializedServicesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="Education"
                expandStatus={expandStatus}
                className="mt-2"
                title="Education/Employment"
              >
                <RadioButton
                  name="EducationStatus"
                  error={errors.EducationStatus}
                  value={data.EducationStatus["@value"]}
                  options={educationOptions}
                  onChange={handleEducationStatusChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.EducationStatus["@value"] !== "0" && (
                  <>
                    {(data.EducationStatus["@value"] === undefined ||
                      data.EducationStatus["@value"] === "1") && (
                      <h5>Education</h5>
                    )}
                    <div className="container">
                      {(data.EducationStatus["@value"] === undefined ||
                        data.EducationStatus["@value"] === "1") && (
                        <>
                          <div className="row">
                            <div className="col">School Name:</div>
                            <div className="col">
                              <Input
                                name="SchoolName"
                                error={errors.SchoolName}
                                value={data.SchoolName["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("SchoolName")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">Current Grade:</div>
                            <div className="col">
                              <Input
                                name="CurrentGrade"
                                error={errors.CurrentGrade}
                                value={data.CurrentGrade["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("CurrentGrade")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">Regular Classroom:</div>
                            <div className="col">
                              <RadioButton
                                name="RegularClassroom"
                                error={errors.RegularClassroom}
                                value={data.RegularClassroom["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <>Active 504 Plan:</>
                            </div>
                            <div className="col">
                              <RadioButton
                                name="Active504Plan"
                                error={errors.Active504Plan}
                                value={data.Active504Plan["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <>Active IEP:</>
                            </div>
                            <div className="col">
                              <RadioButton
                                name="ActiveIEP"
                                error={errors.ActiveIEP}
                                value={data.ActiveIEP["@value"]}
                                options={yesNoOptions}
                                onChange={handleChange}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.ActiveIEP["@value"] === "1" && (
                                <>What classification:</>
                              )}
                            </div>
                            <div className="col">
                              {data.ActiveIEP["@value"] === "1" && (
                                <Input
                                  name="IEPClassification"
                                  error={errors.IEPClassification}
                                  value={data.IEPClassification["@value"]}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("IEPClassification")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      <div className="row">
                        <div className="col">
                          Summary of Client's School Experience (e.g.,
                          achievements, discipline):
                        </div>
                        <div className="col">
                          <TextArea
                            name="SchoolExperience"
                            error={errors.SchoolExperience}
                            value={data.SchoolExperience["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SchoolExperience")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <h5>Employment</h5>
                    <RadioButton
                      name="CurrentlyEmployed"
                      label="Currently Employed:"
                      error={errors.CurrentlyEmployed}
                      value={data.CurrentlyEmployed["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.CurrentlyEmployed["@value"] === "1" && (
                      <>
                        <Input
                          name="EmployerAndTitle"
                          label="Name of employer &amp; title"
                          error={errors.EmployerAndTitle}
                          value={data.EmployerAndTitle["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("EmployerAndTitle")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                    {data.CurrentlyEmployed["@value"] !== "NA" && (
                      <Input
                        name="EmploymentInterests"
                        label="Employment Interests/Skills/Concerns:"
                        error={errors.EmploymentInterests}
                        value={data.EmploymentInterests["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("EmploymentInterests")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <RadioButton
                      name="MilitaryHistory"
                      label="Military History:"
                      error={errors.MilitaryHistory}
                      value={data.MilitaryHistory["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.MilitaryHistory["@value"] === undefined ||
                    data.MilitaryHistory["@value"] === "1" ? (
                      <TextArea
                        name="EmploymentComments"
                        label="Comments:"
                        error={errors.EmploymentComments}
                        value={data.EmploymentComments["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("EmploymentComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </>
                )}
              </ExpandDiv>
              <ExpandDiv
                id="Activities"
                expandStatus={expandStatus}
                className="mt-2"
                title="Activities of Daily Living"
              >
                <Checkbox
                  name="ADLNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.ADLNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.ADLNA["@value"] !== "true" ? (
                  <>
                    Does the client require additional support in the daily
                    functioning and daily activity skills listed below?
                    <div className="container">
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mb-2">
                        <div className="col">
                          <b>Skill</b>
                        </div>
                        <div className="col">
                          <b>Support needed?</b>
                        </div>
                        <div className="col">
                          <b>Explanation</b>
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Money management:</div>
                        <div className="col">
                          <RadioButton
                            name="MoneyManagement"
                            error={errors.MoneyManagement}
                            value={data.MoneyManagement["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.MoneyManagement["@value"] === "1" && (
                            <Input
                              name="MoneyManagementDetails"
                              error={errors.MoneyManagementDetails}
                              value={data.MoneyManagementDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("MoneyManagementDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Food management:</div>
                        <div className="col">
                          <RadioButton
                            name="FoodManagement"
                            error={errors.FoodManagement}
                            value={data.FoodManagement["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.FoodManagement["@value"] === "1" && (
                            <Input
                              name="FoodManagementDetails"
                              error={errors.FoodManagementDetails}
                              value={data.FoodManagementDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("FoodManagementDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">
                          Personal appearance and hygiene:
                        </div>
                        <div className="col">
                          <RadioButton
                            name="Hygiene"
                            error={errors.Hygiene}
                            value={data.Hygiene["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Hygiene["@value"] === "1" && (
                            <Input
                              name="HygieneDetails"
                              error={errors.HygieneDetails}
                              value={data.HygieneDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("HygieneDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">
                          Health awareness/Personal Health:
                        </div>
                        <div className="col">
                          <RadioButton
                            name="Health"
                            error={errors.Health}
                            value={data.Health["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Health["@value"] === "1" && (
                            <Input
                              name="HealthDetails"
                              error={errors.HealthDetails}
                              value={data.HealthDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("HealthDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Housekeeping:</div>
                        <div className="col">
                          <RadioButton
                            name="Housekeeping"
                            error={errors.Housekeeping}
                            value={data.Housekeeping["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Housekeeping["@value"] === "1" && (
                            <Input
                              name="HousekeepingDetails"
                              error={errors.HousekeepingDetails}
                              value={data.HousekeepingDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("HousekeepingDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Transportation:</div>
                        <div className="col">
                          <RadioButton
                            name="Transportation"
                            error={errors.Transportation}
                            value={data.Transportation["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Transportation["@value"] === "1" && (
                            <Input
                              name="TransportationDetails"
                              error={errors.TransportationDetails}
                              value={data.TransportationDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("TransportationDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Emergency and safety skills:</div>
                        <div className="col">
                          <RadioButton
                            name="SafetySkills"
                            error={errors.SafetySkills}
                            value={data.SafetySkills["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.SafetySkills["@value"] === "1" && (
                            <Input
                              name="SafetySkillsDetails"
                              error={errors.SafetySkillsDetails}
                              value={data.SafetySkillsDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("SafetySkillsDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">
                          Knowledge of community resources:
                        </div>
                        <div className="col">
                          <RadioButton
                            name="ResourceKnowledge"
                            error={errors.ResourceKnowledge}
                            value={data.ResourceKnowledge["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.ResourceKnowledge["@value"] === "1" && (
                            <Input
                              name="ResourceKnowledgeDetails"
                              error={errors.ResourceKnowledgeDetails}
                              value={data.ResourceKnowledgeDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("ResourceKnowledgeDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Time management:</div>
                        <div className="col">
                          <RadioButton
                            name="TimeManagement"
                            error={errors.TimeManagement}
                            value={data.TimeManagement["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.TimeManagement["@value"] === "1" && (
                            <Input
                              name="TimeManagementDetails"
                              error={errors.TimeManagementDetails}
                              value={data.TimeManagementDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("TimeManagementDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                      <div className="row row-cols-3 row-cols-sm-3 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 border-bottom mt-2">
                        <div className="col">Interactions with Others:</div>
                        <div className="col">
                          <RadioButton
                            name="Interaction"
                            error={errors.Interaction}
                            value={data.Interaction["@value"]}
                            options={yesNoOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          {data.Interaction["@value"] === "1" && (
                            <Input
                              name="InteractionDetails"
                              error={errors.InteractionDetails}
                              value={data.InteractionDetails["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("InteractionDetails")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="Friendships"
                expandStatus={expandStatus}
                className="mt-2"
                title="Friendship/ Social Peer Support/ Relationships"
              >
                <Checkbox
                  name="RelationshipsNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.RelationshipsNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.RelationshipsNA["@value"] !== "true" ? (
                  <>
                    <div className="container">
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom">
                        <div className="col">
                          <b>Skill</b>
                        </div>
                        <div className="col">
                          <b>Functioning Level</b>
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">Cooperative play skills:</div>
                        <div className="col">
                          <Select
                            name="CoopSkills"
                            error={errors.CoopSkills}
                            value={data.CoopSkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">
                          Communication and interaction skills:
                        </div>
                        <div className="col">
                          <Select
                            name="CommunicationSkills"
                            error={errors.CommunicationSkills}
                            value={data.CommunicationSkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">
                          Emotional understanding and self-regulation skills:
                        </div>
                        <div className="col">
                          <Select
                            name="EmotionalUnderstanding"
                            error={errors.EmotionalUnderstanding}
                            value={data.EmotionalUnderstanding["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">
                          Social problem-solving skills:
                        </div>
                        <div className="col">
                          <Select
                            name="ProblemSolving"
                            error={errors.ProblemSolving}
                            value={data.ProblemSolving["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">Conflict resolution skills:</div>
                        <div className="col">
                          <Select
                            name="ConflictResolution"
                            error={errors.ConflictResolution}
                            value={data.ConflictResolution["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row row-cols-2 row-cols-sm-2 row-cols-md-2 row-cols-lg-2 row-cols-xl-2 border-bottom mt-2">
                        <div className="col">Decision making skills:</div>
                        <div className="col">
                          <Select
                            name="DecisionMakingSkills"
                            error={errors.DecisionMakingSkills}
                            value={data.DecisionMakingSkills["@value"]}
                            options={functioningLevelOptions}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <RadioButton
                      name="Bullied"
                      label="History of being bullied:"
                      error={errors.Bullied}
                      value={data.Bullied["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <RadioButton
                      name="Bully"
                      label="History of bullying others:"
                      error={errors.Bully}
                      value={data.Bully["@value"]}
                      options={yesNoNoneNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <TextArea
                      name="RelationshipsSummary"
                      label="Friendship/Social Support/Relationships Summary Narrative:"
                      error={errors.RelationshipsSummary}
                      value={data.RelationshipsSummary["@value"]}
                      options={yesNoOptions}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("RelationshipsSummary")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="LegalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Legal History"
              >
                <Checkbox
                  name="LegalNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.LegalNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.LegalNA["@value"] !== "true" ? (
                  <>
                    <RadioButton
                      name="LegalInvolvement"
                      label="Current involvement with the Legal System:"
                      error={errors.LegalInvolvement}
                      value={data.LegalInvolvement["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.LegalInvolvement["@value"] === "1" && (
                      <TextArea
                        name="LegalInvolvementDetails"
                        label="Describe:"
                        error={errors.LegalInvolvementDetails}
                        value={data.LegalInvolvementDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("LegalInvolvementDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <RadioButton
                      name="CPSInvolvement"
                      label="Current Children's Protective Services (CPS) Involvement with Family:"
                      error={errors.CPSInvolvement}
                      value={data.CPSInvolvement["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.CPSInvolvement["@value"] === "1" && (
                      <TextArea
                        name="CPSInvolvementDetails"
                        label="Describe:"
                        error={errors.CPSInvolvementDetails}
                        value={data.CPSInvolvementDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("CPSInvolvementDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <div className="row">
                      <div className="col">
                        <RadioButton
                          name="CPSCaseworker"
                          label="CPS Caseworker:"
                          error={errors.CPSCaseworker}
                          value={data.CPSCaseworker["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      {data.CPSCaseworker["@value"] === "1" && (
                        <>
                          <div className="col clearfix">
                            <div className="float-start">Name:</div>
                            <div className="float-start ms-2">
                              <Input
                                name="CPSCaseworkerName"
                                error={errors.CPSCaseworkerName}
                                value={data.CPSCaseworkerName["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("CPSCaseworkerName")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="row">
                      <div className="col">
                        <RadioButton
                          name="ProbationOfficer"
                          label="Probation/Parole Officer:"
                          error={errors.ProbationOfficer}
                          value={data.ProbationOfficer["@value"]}
                          options={yesNoOptions}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      {data.ProbationOfficer["@value"] === "1" && (
                        <>
                          <div className="col clearfix">
                            <div className="float-start">Name:</div>
                            <div className="float-start ms-2">
                              <Input
                                name="ProbationOfficerName"
                                error={errors.ProbationOfficerName}
                                value={data.ProbationOfficerName["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("ProbationOfficerName")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    <RadioButton
                      name="GALCASA"
                      label="Guardian ad Litem (GAL) or Court Appointed Special Advocate (CASA):"
                      error={errors.GALCASA}
                      value={data.GALCASA["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Input
                      name="OtherLegalDetails"
                      label="Other Court/Custody Related Issues:"
                      error={errors.OtherLegalDetails}
                      value={data.OtherLegalDetails["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("OtherLegalDetails")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="MedicalHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Medical History/Medications"
              >
                <b>Medical</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <Input
                        name="PCP"
                        label="Primary Care Physician:"
                        error={errors.PCP}
                        value={data.PCP["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("PCP")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <div className="row">
                        <div className="col">
                          <PhoneInput
                            name="PCPPhone"
                            label="Phone:"
                            error={errors.PCPPhone}
                            value={data.PCPPhone["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PCPPhone")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="PCPPhoneUnknown"
                            label="None Reported"
                            checked={
                              data.PCPPhoneUnknown &&
                              data.PCPPhoneUnknown["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            divStyle={{ marginTop: "30px" }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Input
                        name="LastMedicalExamDate"
                        label="Date of last medical exam:"
                        error={errors.LastMedicalExamDate}
                        value={data.LastMedicalExamDate["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("LastMedicalExamDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Input
                        name="LastDentalExamDate"
                        label="Date of last dental exam:"
                        error={errors.LastDentalExamDate}
                        value={data.LastDentalExamDate["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("LastDentalExamDate")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="Allergies"
                        label="Allergies"
                        divClassName="ms-3"
                        checked={data.Allergies["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.Allergies["@value"] === "true" ? (
                        <Input
                          name="AllergiesDetails"
                          label="List:"
                          error={errors.AllergiesDetails}
                          value={data.AllergiesDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("AllergiesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="MedicalConditions"
                        label="Medical conditions/concerns"
                        divClassName="ms-3"
                        checked={data.MedicalConditions["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.MedicalConditions["@value"] === "true" ? (
                        <Input
                          name="MedicalConditionsDetails"
                          label="List:"
                          error={errors.MedicalConditionsDetails}
                          value={data.MedicalConditionsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("MedicalConditionsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="NutritionalConcerns"
                        label="Nutritional concerns"
                        divClassName="ms-3"
                        checked={data.NutritionalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.NutritionalConcerns["@value"] === "true" ? (
                        <Input
                          name="NutritionalConcernsDetails"
                          label="List:"
                          error={errors.NutritionalConcernsDetails}
                          value={data.NutritionalConcernsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("NutritionalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="DentalConcerns"
                        label="Dental concerns"
                        divClassName="ms-3"
                        checked={data.DentalConcerns["@value"] === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      {data.DentalConcerns["@value"] === "true" ? (
                        <Input
                          name="DentalConcernsDetails"
                          label="List:"
                          error={errors.DentalConcernsDetails}
                          value={data.DentalConcernsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("DentalConcernsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                <RadioButton
                  name="Undiagnosed"
                  className="mt-2"
                  label="Client has symptoms that suggest an undiagnosed medical illness that may be causing or contributing to mental health symptoms:"
                  error={errors.Undiagnosed}
                  value={data.Undiagnosed["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="MedicalComments"
                  label="Comments:"
                  error={errors.MedicalComments}
                  value={data.MedicalComments["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("MedicalComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <br />
                <FieldTable
                  name="CurrentMedicationDetails"
                  commentsHandler={handleComments}
                >
                  <b>Current Medications</b>
                  <Checkbox
                    name="NoMedications"
                    label="Not currently on medications"
                    divClassName="ms-3"
                    checked={data.NoMedications["@value"] === "true"}
                    commentsHandler={handleComments}
                    onChange={handleChange}
                    disabled={formDisabled}
                  />
                  {data.NoMedications["@value"] !== "true" ? (
                    <>
                      <div className="container border mb-2">
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">Medication</div>
                          <div className="col py-2 border-end">Dosage/Use</div>
                          <div className="col py-2 border-end">
                            Prescribed By/Date
                          </div>
                          <div className="col py-2">Purpose</div>
                        </div>
                        {data.CurrentMedicationDetails["@value"]
                          ? JSON.parse(
                              data.CurrentMedicationDetails["@value"]
                            ).map((item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col py-2 border-end d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "CurrentMedicationDetails"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "CurrentMedicationDetails"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      <Input
                                        name="Name"
                                        value={item.Name}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "CurrentMedicationDetails"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "CurrentMedicationDetails"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      {item.Name}
                                    </>
                                  )}
                                </div>
                                <div className="col py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Dosage"
                                      value={item.Dosage}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Dosage
                                  )}
                                </div>
                                <div className="col py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="PrescribedBy"
                                      value={item.PrescribedBy}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.PrescribedBy
                                  )}
                                </div>
                                <div className="col py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Purpose"
                                      value={item.Purpose}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "CurrentMedicationDetails"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Purpose
                                  )}
                                </div>
                              </div>
                            ))
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("CurrentMedicationDetails", {
                                IsEditable: true,
                              });
                              buildClinicalNarrativeSummary();
                            }}
                          >
                            Add Medication
                          </button>
                        </>
                      ) : null}
                      {errors.CurrentMedicationDetails !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.CurrentMedicationDetails}
                            </div>
                          </div>
                        </div>
                      ) : null}
                      <Select
                        name="MedicationCompliance"
                        label="Compliance with medication:"
                        error={errors.MedicationCompliance}
                        value={data.MedicationCompliance["@value"]}
                        options={complianceOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <RadioButton
                        name="SideEffects"
                        label="Side effects from medications:"
                        error={errors.SideEffects}
                        value={data.SideEffects["@value"]}
                        options={yesNoNoneNAOptions}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      {data.SideEffects["@value"] === "1" ? (
                        <Input
                          name="SideEffectsDetails"
                          label="Describe:"
                          error={errors.SideEffectsDetails}
                          value={data.SideEffectsDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SideEffectsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </>
                  ) : null}
                </FieldTable>
                <RadioButton
                  name="PsychotropicMeds"
                  label="Past psychotropic medications:"
                  error={errors.PsychotropicMeds}
                  value={data.PsychotropicMeds["@value"]}
                  options={yesNoNoneNAOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.PsychotropicMeds["@value"] === "1" ? (
                  <Input
                    name="PsychotropicMedsDetails"
                    label="List:"
                    error={errors.PsychotropicMedsDetails}
                    value={data.PsychotropicMedsDetails["@value"]}
                    onChange={(e) => {
                      handleChange(e);
                      buildClinicalNarrativeSummary();
                    }}
                    identical={compare("PsychotropicMedsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
                <TextArea
                  name="MedicationComments"
                  label="Comments:"
                  error={errors.MedicationComments}
                  value={data.MedicationComments["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("MedicationComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  placeholder="Include compliance with past medications and any known side effects from past medications..."
                />
              </ExpandDiv>
              <ExpandDiv
                id="SexualHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Sexual History to Include Pertinent Sexual Issues/Concerns"
              >
                <Checkbox
                  name="SexualHistoryNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.SexualHistoryNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.SexualHistoryNA["@value"] !== "true" ? (
                  <>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <Select
                            name="SexualOrientation"
                            label="Sexual orientation:"
                            error={errors.SexualOrientation}
                            value={data.SexualOrientation["@value"]}
                            options={sexualOrientationOptions}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SexualOrientation")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                            title="who someone is physically, romantically, or sexually attracted to"
                          />
                        </div>
                        <div className="col">
                          {data.SexualOrientation !== undefined &&
                            data.SexualOrientation["@value"] ===
                              "Orientation not listed" && (
                              <Input
                                name="SexualOrientationDetails"
                                label="Please specify:"
                                error={errors.SexualOrientationDetails}
                                value={
                                  data.SexualOrientationDetails !== undefined
                                    ? data.SexualOrientationDetails["@value"]
                                    : ""
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                }}
                                identical={compare("SexualOrientationDetails")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            )}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Input
                            name="SexualBehavior"
                            label="Current or historical sexual behavior:"
                            error={errors.SexualBehavior}
                            value={data.SexualBehavior["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SexualBehavior")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Input
                            name="SexualAbuse"
                            label="Current or history of sexual abuse or perpetration:"
                            error={errors.SexualAbuse}
                            value={data.SexualAbuse["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SexualAbuse")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </div>
                    <RadioButton
                      name="SexualRelationshipsEducation"
                      label="Education provided on healthy sexual behavior/relationships:"
                      error={errors.SexualRelationshipsEducation}
                      value={data.SexualRelationshipsEducation["@value"]}
                      options={yesNoNAOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <TextArea
                      name="SexualHistoryComments"
                      label="Comments:"
                      error={errors.SexualHistoryComments}
                      value={data.SexualHistoryComments["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("SexualHistoryComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="MentalHealthTreatmentHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Health Treatment History"
              >
                <Checkbox
                  name="MentalHealthNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.MentalHealthNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.MentalHealthNA["@value"] !== "true" ? (
                  <>
                    <RadioButton
                      name="PastMentalHealthTreatment"
                      label="Past mental health counseling/treatment:"
                      error={errors.PastMentalHealthTreatment}
                      value={data.PastMentalHealthTreatment["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <RadioButton
                      name="PastMentalHealthPlacement"
                      label="Past placement in a group home, facility, or psychiatric hospital:"
                      error={errors.PastMentalHealthPlacement}
                      value={data.PastMentalHealthPlacement["@value"]}
                      options={yesNoOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Input
                      name="PastMentalHealthDiagnosis"
                      label="Previous mental health diagnosis:"
                      error={errors.PastMentalHealthDiagnosis}
                      value={data.PastMentalHealthDiagnosis["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("PastMentalHealthDiagnosis")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <TextArea
                      name="MentalHealthComments"
                      label="Comments:"
                      error={errors.MentalHealthComments}
                      value={data.MentalHealthComments["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("PastMentalHealthDiagnosis")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FieldTable
                      name="MentalHealthHistory"
                      commentsHandler={handleComments}
                    >
                      <br />
                      <div className="container border mb-2">
                        <div className="row border-bottom">
                          <div className="col py-2 border-end">
                            Agency/Facility
                          </div>
                          <div className="col py-2 border-end">Dates</div>
                          <div className="col-6 py-2">
                            Summary of Services/Treatment
                          </div>
                        </div>
                        {data.MentalHealthHistory["@value"] &&
                          JSON.parse(data.MentalHealthHistory["@value"]).map(
                            (item, index) => (
                              <div key={item._id} className="row border-bottom">
                                <div className="col py-2 border-end d-flex">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <>
                                      <i
                                        className="fas fa-save me-2 d-print-none"
                                        onClick={() => {
                                          handleSaveRow(
                                            index,
                                            "MentalHealthHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      <i
                                        className="far fa-trash-alt me-2 d-print-none"
                                        onClick={() => {
                                          handleDeleteRow(
                                            index,
                                            "MentalHealthHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      <Input
                                        name="Agency"
                                        value={item.Agency}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "MentalHealthHistory"
                                          );
                                        }}
                                        disabled={formDisabled}
                                      />
                                    </>
                                  ) : (
                                    <>
                                      <i
                                        className="fas fa-pen me-2 d-print-none"
                                        onClick={() => {
                                          handleEditRow(
                                            index,
                                            "MentalHealthHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      ></i>
                                      {item.Agency}
                                    </>
                                  )}
                                </div>
                                <div className="col py-2 border-end">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Dates"
                                      value={item.Dates}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthHistory"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Dates
                                  )}
                                </div>
                                <div className="col-6 py-2">
                                  {item.IsEditable === undefined ||
                                  item.IsEditable ? (
                                    <Input
                                      name="Summary"
                                      value={item.Summary}
                                      onChange={(e) => {
                                        handleRowChange(
                                          e,
                                          index,
                                          "MentalHealthHistory"
                                        );
                                        buildClinicalNarrativeSummary();
                                      }}
                                      disabled={formDisabled}
                                    />
                                  ) : (
                                    item.Summary
                                  )}
                                </div>
                              </div>
                            )
                          )}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary my-2"
                            onClick={() => {
                              handleAddRow("MentalHealthHistory", {
                                IsEditable: true,
                              });
                              buildClinicalNarrativeSummary();
                            }}
                          >
                            Add Agency/Facility
                          </button>
                        </>
                      ) : null}
                      {errors.MentalHealthHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.MentalHealthHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                    <TextArea
                      name="PerspectiveOfTreatment"
                      label="Client and caregiver perspective of treatment/counseling:"
                      error={errors.PerspectiveOfTreatment}
                      value={data.PerspectiveOfTreatment["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("PerspectiveOfTreatment")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="SubstanceUseHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Alcohol/Drug Use History"
              >
                <Checkbox
                  name="SubstanceUseNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.SubstanceUseNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleSubstanceUseNAChange}
                  disabled={formDisabled}
                />
                {data.SubstanceUseNA["@value"] !== "true" ? (
                  <>
                    <Input
                      name="PastSubstanceUse"
                      label="History of past drug or alcohol use:"
                      error={errors.PastSubstanceUse}
                      value={data.PastSubstanceUse["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("PastSubstanceUse")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <FieldTable
                      name="SubstanceUseHistory"
                      commentsHandler={handleComments}
                    >
                      Current drug or alcohol use:
                      <div className="container border mb-2">
                        <div className="row border-bottom ">
                          <div className="col py-2 border-end">Substance</div>
                          <div className="col py-2 border-end">First Use</div>
                          <div className="col py-2 border-end">Last Use</div>
                          <div className="col py-2 border-end">Frequency</div>
                          <div className="col py-2 border-end">Amount</div>
                          <div className="col py-2">Method</div>
                        </div>
                        {data.SubstanceUseHistory["@value"]
                          ? JSON.parse(data.SubstanceUseHistory["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col py-2 border-end d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <Input
                                          name="Substance"
                                          value={item.Substance}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "SubstanceUseHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        {item.Substance}
                                      </>
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="FirstUse"
                                        value={item.FirstUse}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.FirstUse
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="LastUse"
                                        value={item.LastUse}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.LastUse
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Frequency"
                                        value={item.Frequency}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Frequency
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Amount"
                                        value={item.Amount}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Amount
                                    )}
                                  </div>
                                  <div className="col py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Method"
                                        value={item.Method}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "SubstanceUseHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Method
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("SubstanceUseHistory", {
                                IsEditable: true,
                              });
                              buildClinicalNarrativeSummary();
                            }}
                          >
                            Add Substance
                          </button>
                        </>
                      ) : null}
                      {errors.SubstanceUseHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.SubstanceUseHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                    <Input
                      name="PerspectiveOfSubstanceUse"
                      label="Client and caregiver perception of substance use:"
                      error={errors.PerspectiveOfSubstanceUse}
                      value={data.PerspectiveOfSubstanceUse["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("PerspectiveOfSubstanceUse")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <Input
                      name="SubstanceUseComments"
                      label="Comments (including impact to current functioning):"
                      error={errors.SubstanceUseComments}
                      value={data.SubstanceUseComments["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("SubstanceUseComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <br />
                    <b>Alcohol/Drug Treatment History</b>
                    <Checkbox
                      name="NoPastTreatment"
                      label="No past treatment"
                      divClassName="ms-3"
                      checked={data.NoPastTreatment["@value"] === "true"}
                      commentsHandler={handleComments}
                      onChange={handleChange}
                      disabled={formDisabled}
                    />
                    <FieldTable
                      name="SubstanceUseTreatmentHistory"
                      commentsHandler={handleComments}
                    >
                      {data.NoPastTreatment["@value"] !== "true" ? (
                        <>
                          <br />
                          <div className="container border mb-2">
                            <div className="row border-bottom">
                              <div className="col py-2 border-end">
                                Agency/Facility
                              </div>
                              <div className="col py-2 border-end">Dates</div>
                              <div className="col-6 py-2">
                                Summary of Services/Treatment
                              </div>
                            </div>
                            {data.SubstanceUseTreatmentHistory["@value"]
                              ? JSON.parse(
                                  data.SubstanceUseTreatmentHistory["@value"]
                                ).map((item, index) => (
                                  <div
                                    key={item._id}
                                    className="row border-bottom"
                                  >
                                    <div className="col py-2 border-end d-flex">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <>
                                          <i
                                            className="fas fa-save me-2 d-print-none"
                                            onClick={() => {
                                              handleSaveRow(
                                                index,
                                                "SubstanceUseTreatmentHistory"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          <i
                                            className="far fa-trash-alt me-2 d-print-none"
                                            onClick={() => {
                                              handleDeleteRow(
                                                index,
                                                "SubstanceUseTreatmentHistory"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          <Input
                                            name="Agency"
                                            value={item.Agency}
                                            onChange={(e) => {
                                              handleRowChange(
                                                e,
                                                index,
                                                "SubstanceUseTreatmentHistory"
                                              );
                                            }}
                                            disabled={formDisabled}
                                          />
                                        </>
                                      ) : (
                                        <>
                                          <i
                                            className="fas fa-pen me-2 d-print-none"
                                            onClick={() => {
                                              handleEditRow(
                                                index,
                                                "SubstanceUseTreatmentHistory"
                                              );
                                              buildClinicalNarrativeSummary();
                                            }}
                                          ></i>
                                          {item.Agency}
                                        </>
                                      )}
                                    </div>
                                    <div className="col py-2 border-end">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <Input
                                          name="Dates"
                                          value={item.Dates}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SubstanceUseTreatmentHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : (
                                        item.Dates
                                      )}
                                    </div>
                                    <div className="col-6 py-2 border-end">
                                      {item.IsEditable === undefined ||
                                      item.IsEditable ? (
                                        <Input
                                          name="Summary"
                                          value={item.Summary}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "SubstanceUseTreatmentHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                          disabled={formDisabled}
                                        />
                                      ) : (
                                        item.Summary
                                      )}
                                    </div>
                                  </div>
                                ))
                              : null}
                          </div>
                          {!formDisabled ? (
                            <>
                              <button
                                className="btn btn-primary mb-2"
                                onClick={() => {
                                  handleAddRow("SubstanceUseTreatmentHistory", {
                                    IsEditable: true,
                                  });
                                  buildClinicalNarrativeSummary();
                                }}
                              >
                                Add Agency/Facility
                              </button>
                            </>
                          ) : null}
                          {errors.SubstanceUseTreatmentHistory !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.SubstanceUseTreatmentHistory}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="PerspectiveOfSubstanceUseTreatment"
                            label="Client and caregiver perspective of treatment:"
                            error={errors.PerspectiveOfSubstanceUseTreatment}
                            value={
                              data.PerspectiveOfSubstanceUseTreatment["@value"]
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare(
                              "PerspectiveOfSubstanceUseTreatment"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="SubstanceUseTreatmentComments"
                            label="Comments:"
                            error={errors.SubstanceUseTreatmentComments}
                            value={data.SubstanceUseTreatmentComments["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SubstanceUseTreatmentComments")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      ) : null}
                    </FieldTable>
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="TraumaHistory"
                expandStatus={expandStatus}
                className="mt-2"
                title="Trauma History"
              >
                <Checkbox
                  name="TraumaNA"
                  label="No history of abuse or trauma (Skip Section)"
                  divClassName="ms-3"
                  checked={data.TraumaNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.TraumaNA["@value"] !== "true" ? (
                  <>
                    <FieldTable
                      name="TraumaHistory"
                      commentsHandler={handleComments}
                    >
                      <br />
                      <div className="container border mb-2">
                        <div className="row border-bottom">
                          <div className="col-3 py-2 border-end">Trauma</div>
                          <div className="col-3 py-2 border-end">Status</div>
                          <div className="col-6 py-2">Comments</div>
                        </div>
                        {data.TraumaHistory["@value"]
                          ? JSON.parse(data.TraumaHistory["@value"]).map(
                              (item, index) => (
                                <div
                                  key={item._id}
                                  className="row border-bottom"
                                >
                                  <div className="col py-2 border-end d-flex">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <>
                                        <i
                                          className="fas fa-save me-2 d-print-none"
                                          onClick={() => {
                                            handleSaveRow(
                                              index,
                                              "TraumaHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <i
                                          className="far fa-trash-alt me-2 d-print-none"
                                          onClick={() => {
                                            handleDeleteRow(
                                              index,
                                              "TraumaHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        <Select
                                          name="Trauma"
                                          value={item.Trauma}
                                          options={traumaOptions}
                                          onChange={(e) => {
                                            handleRowChange(
                                              e,
                                              index,
                                              "TraumaHistory"
                                            );
                                          }}
                                          disabled={formDisabled}
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <i
                                          className="fas fa-pen me-2 d-print-none"
                                          onClick={() => {
                                            handleEditRow(
                                              index,
                                              "TraumaHistory"
                                            );
                                            buildClinicalNarrativeSummary();
                                          }}
                                        ></i>
                                        {item.Trauma}
                                      </>
                                    )}
                                  </div>
                                  <div className="col py-2 border-end">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Select
                                        name="Status"
                                        value={item.Status}
                                        options={statusOptions}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "TraumaHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Status
                                    )}
                                  </div>
                                  <div className="col-6 py-2">
                                    {item.IsEditable === undefined ||
                                    item.IsEditable ? (
                                      <Input
                                        name="Comments"
                                        value={item.Comments}
                                        onChange={(e) => {
                                          handleRowChange(
                                            e,
                                            index,
                                            "TraumaHistory"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                        disabled={formDisabled}
                                      />
                                    ) : (
                                      item.Comments
                                    )}
                                  </div>
                                </div>
                              )
                            )
                          : null}
                      </div>
                      {!formDisabled ? (
                        <>
                          <button
                            className="btn btn-primary mb-2"
                            onClick={() => {
                              handleAddRow("TraumaHistory", {
                                IsEditable: true,
                              });
                              buildClinicalNarrativeSummary();
                            }}
                          >
                            Add Trauma
                          </button>
                        </>
                      ) : null}
                      {errors.TraumaHistory !== undefined ? (
                        <div className="row">
                          <div className="col">
                            <div className="alert alert-danger">
                              {errors.TraumaHistory}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </FieldTable>
                    <br />
                    <b>Trauma Symptoms/Responses:</b>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="DistressingMemories"
                            label="Distressing memories"
                            divClassName="ms-3"
                            checked={
                              data.DistressingMemories["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="BlockOut"
                            label="Blocking out events"
                            divClassName="ms-3"
                            checked={data.BlockOut["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Hypervigilance"
                            label="Hypervigilance"
                            divClassName="ms-3"
                            checked={data.Hypervigilance["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="PhysiologicalResponses"
                            label="Physiological responses"
                            divClassName="ms-3"
                            checked={
                              data.PhysiologicalResponses["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="LossOfMemories"
                            label="Loss of memories"
                            divClassName="ms-3"
                            checked={data.LossOfMemories["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Hyperarousal"
                            label="Hyperarousal"
                            divClassName="ms-3"
                            checked={data.Hyperarousal["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SleepDisturbances"
                            label="Sleep disturbances"
                            divClassName="ms-3"
                            checked={
                              data.SleepDisturbances["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="DevelopmentalRegression"
                            label="Developmental regression"
                            divClassName="ms-3"
                            checked={
                              data.DevelopmentalRegression["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Nightmares"
                            label="Frightening dreams"
                            divClassName="ms-3"
                            checked={data.Nightmares["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Avoidant"
                            label="Avoidant behaviors"
                            divClassName="ms-3"
                            checked={data.Avoidant["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="DifficultyConcentratingSymptom"
                            label="Difficulty concentrating"
                            divClassName="ms-3"
                            checked={
                              data.DifficultyConcentratingSymptom["@value"] ===
                              "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="UnhealthyBoundaries"
                            label="Unhealthy boundaries"
                            divClassName="ms-3"
                            checked={
                              data.UnhealthyBoundaries["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="Flashbacks"
                            label="Flashbacks"
                            divClassName="ms-3"
                            checked={data.Flashbacks["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Detachment"
                            label="Detachment"
                            divClassName="ms-3"
                            checked={data.Detachment["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="EmotionalDysregulation"
                            label="Emotional dysregulation"
                            divClassName="ms-3"
                            checked={
                              data.EmotionalDysregulation["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="RepetitivePlayOfEvents"
                            label="Repetitive play of events"
                            divClassName="ms-3"
                            checked={
                              data.RepetitivePlayOfEvents["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="PsychologicalDistress"
                            label="Psychological distress"
                            divClassName="ms-3"
                            checked={
                              data.PsychologicalDistress["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="DiminishedInterests"
                            label="Diminished interests"
                            divClassName="ms-3"
                            checked={
                              data.DiminishedInterests["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Dissocation"
                            label="Dissocation"
                            divClassName="ms-3"
                            checked={data.Dissocation["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="NegativeCognitions"
                            label="Negative cognitions"
                            divClassName="ms-3"
                            checked={
                              data.NegativeCognitions["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SocialWithdrawalSymptom"
                            label="Social withdrawal"
                            divClassName="ms-3"
                            checked={
                              data.SocialWithdrawalSymptom["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="NoSymptoms"
                            label="None reported"
                            divClassName="ms-3"
                            checked={
                              data.NoSymptoms &&
                              data.NoSymptoms["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col"></div>
                      </div>
                    </div>
                    {errors.TraumaSymptomsResponse !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.TraumaSymptomsResponse}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <br />
                    <b>Protective Factors:</b>
                    <div className="container">
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SocialSupports"
                            label="Social supports"
                            divClassName="ms-3"
                            checked={data.SocialSupports["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="SenseOfMastery"
                            label="Sense of mastery"
                            divClassName="ms-3"
                            checked={data.SenseOfMastery["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="CaregiverStability"
                            label="Caregiver stability"
                            divClassName="ms-3"
                            checked={
                              data.CaregiverStability["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="StrongSpiritualBeliefs"
                            label="Strong spiritual beliefs"
                            divClassName="ms-3"
                            checked={
                              data.StrongSpiritualBeliefs["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="InterpersonalSkills"
                            label="Interpersonal skills"
                            divClassName="ms-3"
                            checked={
                              data.InterpersonalSkills["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="PositiveOutlook"
                            label="Positive outlook"
                            divClassName="ms-3"
                            checked={data.PositiveOutlook["@value"] === "true"}
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="PlacementStability"
                            label="Placement stability"
                            divClassName="ms-3"
                            checked={
                              data.PlacementStability["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Pets"
                            label="Pets"
                            divClassName="ms-3"
                            checked={data.Pets["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="ValidatingExperiences"
                            label="Validating experiences"
                            divClassName="ms-3"
                            checked={
                              data.ValidatingExperiences["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Resources"
                            label="Resources"
                            divClassName="ms-3"
                            checked={data.Resources["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="TiesToExtendedFamily"
                            label="Ties to extended family"
                            divClassName="ms-3"
                            checked={
                              data.TiesToExtendedFamily["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="Adaptability"
                            label="Adaptability"
                            divClassName="ms-3"
                            checked={data.Adaptability["@value"] === "true"}
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                      <div className="row">
                        <div className="col">
                          <Checkbox
                            name="SecureAttachments"
                            label="Secure attachments"
                            divClassName="ms-3"
                            checked={
                              data.SecureAttachments["@value"] === "true"
                            }
                            commentsHandler={handleComments}
                            onChange={handleChange}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="StrongCulturalIdentity"
                            label="Strong cultural identity"
                            divClassName="ms-3"
                            checked={
                              data.StrongCulturalIdentity["@value"] === "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col">
                          <Checkbox
                            name="NoTraumaProtectiveFactors"
                            label="None reported"
                            divClassName="ms-3"
                            checked={
                              data.NoTraumaProtectiveFactors &&
                              data.NoTraumaProtectiveFactors["@value"] ===
                                "true"
                            }
                            onChange={handleChange}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div className="col"></div>
                      </div>
                    </div>
                    {errors.TraumaProtectiveFactors !== undefined ? (
                      <div className="row">
                        <div className="col">
                          <div className="alert alert-danger">
                            {errors.TraumaProtectiveFactors}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <TextArea
                      name="CopingStrategies"
                      label="Coping strategies:"
                      error={errors.CopingStrategies}
                      value={data.CopingStrategies["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CopingStrategies")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <TextArea
                      name="TraumaSummary"
                      label="Trauma Impact Summary (include recollection, perspective, and impact):"
                      error={errors.TraumaSummary}
                      value={data.TraumaSummary["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("TraumaSummary")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="MentalStatus"
                expandStatus={expandStatus}
                className="mt-2"
                title="Mental Status"
              >
                <div className="container">
                  <div className="row border-bottom">
                    <div className="col-3">Appearance</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="AppropriateAppearance"
                        label="Appropriate"
                        divClassName="ms-3"
                        checked={
                          data.AppropriateAppearance["@value"] === "true"
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Neat"
                        label="Neat"
                        divClassName="ms-3"
                        checked={data.Neat["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Disheveled"
                        label="Disheveled"
                        divClassName="ms-3"
                        checked={data.Disheveled["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="BizarreAppearance"
                        label="Bizarre"
                        divClassName="ms-3"
                        checked={data.BizarreAppearance["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherAppearance"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherAppearance["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherAppearance["@value"] === "true" ? (
                        <Input
                          name="OtherAppearanceDetails"
                          label="Comments:"
                          error={errors.OtherAppearanceDetails}
                          value={data.OtherAppearanceDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherAppearanceDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSAppearance !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSAppearance}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Behavior</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Cooperative"
                        label="Cooperative"
                        divClassName="ms-3"
                        checked={data.Cooperative["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Guarded"
                        label="Guarded"
                        divClassName="ms-3"
                        checked={data.Guarded["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Hyperactive"
                        label="Hyperactive"
                        divClassName="ms-3"
                        checked={data.Hyperactive["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Agitated"
                        label="Agitated"
                        divClassName="ms-3"
                        checked={data.Agitated["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Paranoid"
                        label="Paranoid"
                        divClassName="ms-3"
                        checked={data.Paranoid["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Tics"
                        label="Tics"
                        divClassName="ms-3"
                        checked={data.Tics["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Impulsive"
                        label="Impulsive"
                        divClassName="ms-3"
                        checked={data.Impulsive["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Aggressive"
                        label="Aggressive"
                        divClassName="ms-3"
                        checked={data.Aggressive["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Withdrawn"
                        label="Withdrawn"
                        divClassName="ms-3"
                        checked={data.Withdrawn["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="BizarreBehavior"
                        label="Bizarre"
                        divClassName="ms-3"
                        checked={data.BizarreBehavior["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Rigidity"
                        label="Rigidity"
                        divClassName="ms-3"
                        checked={data.Rigidity["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherBehavior"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherBehavior["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherBehavior["@value"] === "true" ? (
                        <Input
                          name="OtherBehaviorDetails"
                          label="Comments:"
                          error={errors.OtherBehaviorDetails}
                          value={data.OtherBehaviorDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherBehaviorDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSBehavior !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSBehavior}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Speech</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Normal"
                        label="Normal"
                        divClassName="ms-3"
                        checked={data.Normal["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Slurred"
                        label="Slurred"
                        divClassName="ms-3"
                        checked={data.Slurred["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Pressured"
                        label="Pressured"
                        divClassName="ms-3"
                        checked={data.Pressured["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Tangential"
                        label="Tangential"
                        divClassName="ms-3"
                        checked={data.Tangential["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Incoherent"
                        label="Incoherent"
                        divClassName="ms-3"
                        checked={data.Incoherent["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Stutters"
                        label="Stutters"
                        divClassName="ms-3"
                        checked={data.Stutters["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherSpeech"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherSpeech["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherSpeech["@value"] === "true" ? (
                        <Input
                          name="OtherSpeechDetails"
                          label="Comments:"
                          error={errors.OtherSpeechDetails}
                          value={data.OtherSpeechDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherSpeechDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSSpeech !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSSpeech}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Mood</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Euthymic"
                        label="Euthymic"
                        divClassName="ms-3"
                        checked={data.Euthymic["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Anxious"
                        label="Anxious"
                        divClassName="ms-3"
                        checked={data.Anxious["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Angry"
                        label="Angry"
                        divClassName="ms-3"
                        checked={data.Angry["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Depressed"
                        label="Depressed"
                        divClassName="ms-3"
                        checked={data.Depressed["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Euphoric"
                        label="Euphoric"
                        divClassName="ms-3"
                        checked={data.Euphoric["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Irritable"
                        label="Irritable"
                        divClassName="ms-3"
                        checked={data.Irritable["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Fearful"
                        label="Fearful"
                        divClassName="ms-3"
                        checked={data.Fearful["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherMood"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherMood["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherMood["@value"] === "true" ? (
                        <Input
                          name="OtherMoodDetails"
                          label="Comments:"
                          error={errors.OtherMoodDetails}
                          value={data.OtherMoodDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherMoodDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSMood !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSMood}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Affect</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="FullRange"
                        label="Full range"
                        divClassName="ms-3"
                        checked={data.FullRange["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Restricted"
                        label="Restricted"
                        divClassName="ms-3"
                        checked={data.Restricted["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Blunted"
                        label="Blunted"
                        divClassName="ms-3"
                        checked={data.Blunted["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Flat"
                        label="Flat"
                        divClassName="ms-3"
                        checked={data.Flat["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Inappropriate"
                        label="Inappropriate"
                        divClassName="ms-3"
                        checked={data.Inappropriate["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherAffect"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherAffect["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherAffect["@value"] === "true" ? (
                        <Input
                          name="OtherAffectDetails"
                          label="Comments:"
                          error={errors.OtherAffectDetails}
                          value={data.OtherAffectDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherAffectDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSAffect !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSAffect}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Thought Process/Perception</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="Logical"
                        label="Logical"
                        divClassName="ms-3"
                        checked={data.Logical["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Preoccupations"
                        label="Preoccupations"
                        divClassName="ms-3"
                        checked={data.Preoccupations["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Delusions"
                        label="Delusions"
                        divClassName="ms-3"
                        checked={data.Delusions["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Hallucinations"
                        label="Hallucinations"
                        divClassName="ms-3"
                        checked={data.Hallucinations["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Racing"
                        label="Racing"
                        divClassName="ms-3"
                        checked={data.Racing["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Disorganized"
                        label="Disorganized"
                        divClassName="ms-3"
                        checked={data.Disorganized["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Concrete"
                        label="Concrete"
                        divClassName="ms-3"
                        checked={data.Concrete["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="SuicidalIdeation"
                        label="Suicidal Ideation"
                        divClassName="ms-3"
                        checked={data.SuicidalIdeation["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="HomicidalIdeation"
                        label="Homicidal Ideation"
                        divClassName="ms-3"
                        checked={data.HomicidalIdeation["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherPerception"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherPerception["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherPerception["@value"] === "true" ? (
                        <Input
                          name="OtherPerceptionDetails"
                          label="Comments:"
                          error={errors.OtherPerceptionDetails}
                          value={data.OtherPerceptionDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherPerceptionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSPerception !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSPerception}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">
                      Cognition/Intellectual Functioning
                    </div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="NoImpairment"
                        label="No Impairment"
                        divClassName="ms-3"
                        checked={data.NoImpairment["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="ShortAttentionSpan"
                        label="Short Attention Span"
                        divClassName="ms-3"
                        checked={data.ShortAttentionSpan["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="DifficultyConcentrating"
                        label="Difficulty Concentrating"
                        divClassName="ms-3"
                        checked={
                          data.DifficultyConcentrating["@value"] === "true"
                        }
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Distractibility"
                        label="Distractibility"
                        divClassName="ms-3"
                        checked={data.Distractibility["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="MemoryImpairment"
                        label="Memory Impairment"
                        divClassName="ms-3"
                        checked={data.MemoryImpairment["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="LowIntelligence"
                        label="Low Intelligence"
                        divClassName="ms-3"
                        checked={data.LowIntelligence["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherCognition"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherCognition["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherCognition["@value"] === "true" ? (
                        <Input
                          name="OtherCognitionDetails"
                          label="Comments:"
                          error={errors.OtherCognitionDetails}
                          value={data.OtherCognitionDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherCognitionDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSCognition !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSCognition}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2 border-bottom">
                    <div className="col-3">Insight/Judgment</div>
                    <div className="col-9 d-flex flex-wrap">
                      <Checkbox
                        name="AppropriateInsight"
                        label="Appropriate"
                        divClassName="ms-3"
                        checked={data.AppropriateInsight["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Fair"
                        label="Fair"
                        divClassName="ms-3"
                        checked={data.Fair["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="Impaired"
                        label="Impaired"
                        divClassName="ms-3"
                        checked={data.Impaired["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      <Checkbox
                        name="OtherInsight"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.OtherInsight["@value"] === "true"}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divStyle={{ width: "200px" }}
                      />
                      {data.OtherInsight["@value"] === "true" ? (
                        <Input
                          name="OtherInsightDetails"
                          label="Comments:"
                          error={errors.OtherInsightDetails}
                          value={data.OtherInsightDetails["@value"]}
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("OtherInsightDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.MSInsight !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.MSInsight}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="row mt-2">
                    <div className="col-3">Comments/Observations</div>
                    <div className="col-9">
                      <TextArea
                        name="MentalStatusComments"
                        error={errors.MentalStatusComments}
                        value={data.MentalStatusComments["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("MentalStatusComments")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
              </ExpandDiv>
              <ExpandDiv
                id="Lethality"
                expandStatus={expandStatus}
                className="mt-2"
                title="Lethality"
              >
                <Checkbox
                  name="LethalityNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={data.LethalityNA["@value"] === "true"}
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.LethalityNA["@value"] !== "true" ? (
                  <>
                    <b>Suicide/Harm to Self</b>
                    <RadioButton
                      name="SelfAbusiveBehavior"
                      label="Self-injurious or self-abusive behavior:"
                      error={errors.SelfAbusiveBehavior}
                      value={data.SelfAbusiveBehavior["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SelfAbusiveBehavior["@value"] !== "0" ? (
                      <Input
                        name="SelfAbusiveBehaviorDetails"
                        label="Describe:"
                        error={errors.SelfAbusiveBehaviorDetails}
                        value={data.SelfAbusiveBehaviorDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SelfAbusiveBehaviorDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <RadioButton
                      name="SuicidalThreats"
                      label="Suicidal threats/gestures:"
                      error={errors.SuicidalThreats}
                      value={data.SuicidalThreats["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SuicidalThreats["@value"] !== "0" ? (
                      <Input
                        name="SuicidalThreatsDetails"
                        label="Describe:"
                        error={errors.SuicidalThreatsDetails}
                        value={data.SuicidalThreatsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SuicidalThreatsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <RadioButton
                      name="SuicideAttempt"
                      label="Suicide attempt:"
                      error={errors.SuicideAttempt}
                      value={data.SuicideAttempt["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.SuicideAttempt["@value"] !== "0" ? (
                      <Input
                        name="SuicideAttemptDetails"
                        label="Describe:"
                        error={errors.SuicideAttemptDetails}
                        value={data.SuicideAttemptDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("SuicideAttemptDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    {(data.SelfAbusiveBehavior["@value"] !== undefined &&
                      data.SelfAbusiveBehavior["@value"] !== "0") ||
                    (data.SuicidalThreats["@value"] !== undefined &&
                      data.SuicidalThreats["@value"] !== "0") ||
                    (data.SuicideAttempt["@value"] !== undefined &&
                      data.SuicideAttempt["@value"] !== "0") ? (
                      <>
                        <b>Risk Factors</b>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="PriorAttempt"
                                label="Prior suicide attempt(s)"
                                divClassName="ms-3"
                                checked={data.PriorAttempt["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="FamilyHistory"
                                label="Family history of suicide"
                                divClassName="ms-3"
                                checked={
                                  data.FamilyHistory["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="AccessToMeans"
                                label="Access to lethal means"
                                divClassName="ms-3"
                                checked={
                                  data.AccessToMeans["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="Exposure"
                                label="Exposure to another person's suicide"
                                divClassName="ms-3"
                                checked={data.Exposure["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="HealthCondition"
                                label="Serious or chronic health condition and/or pain"
                                divClassName="ms-3"
                                checked={
                                  data.HealthCondition["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="ElevatingBeliefs"
                                label="Cultural or religious beliefs elevating suicide"
                                divClassName="ms-3"
                                checked={
                                  data.ElevatingBeliefs["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="MentalHealthDisorder"
                                label="Mental health disorder"
                                divClassName="ms-3"
                                checked={
                                  data.MentalHealthDisorder["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="OtherRiskFactors"
                                label="Other"
                                divClassName="ms-3"
                                checked={
                                  data.OtherRiskFactors &&
                                  data.OtherRiskFactors["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="NoRiskFactors"
                                label="None reported"
                                divClassName="ms-3"
                                checked={
                                  data.NoRiskFactors &&
                                  data.NoRiskFactors["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              {data.OtherRiskFactors &&
                              data.OtherRiskFactors["@value"] === "true" ? (
                                <Input
                                  name="OtherRiskFactorsDetails"
                                  label="Describe risk factors:"
                                  error={errors.OtherRiskFactorsDetails}
                                  value={
                                    data.OtherRiskFactorsDetails !== undefined
                                      ? data.OtherRiskFactorsDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("OtherRiskFactorsDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                          {errors.RiskFactors !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.RiskFactors}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className="row">
                            <div className="col">
                              <TextArea
                                name="RiskFactorsComments"
                                label="Comments:"
                                error={errors.RiskFactorsComments}
                                value={data.RiskFactorsComments["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("RiskFactorsComments")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </div>
                        <br />
                        <b>Protective Factors</b>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="ClinicalCare"
                                label="Effective clinical care for mental, physical, and substance abuse disorders"
                                divClassName="ms-3"
                                checked={data.ClinicalCare["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="Connected"
                                label="Connectedness to individuals, family, community, and social institutions"
                                divClassName="ms-3"
                                checked={data.Connected["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="Skills"
                                label="Skills in problem solving, conflict resolution, and nonviolent ways of handling disputes"
                                divClassName="ms-3"
                                checked={data.Skills["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="CaregiverContacts"
                                label="Contacts with caregivers"
                                divClassName="ms-3"
                                checked={
                                  data.CaregiverContacts["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="DiscouragingBeliefs"
                                label="Cultural and religious beliefs that discourage suicide"
                                divClassName="ms-3"
                                checked={
                                  data.DiscouragingBeliefs["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="NoSelfHarmProtectiveFactors"
                                label="None reported"
                                divClassName="ms-3"
                                checked={
                                  data.NoSelfHarmProtectiveFactors &&
                                  data.NoSelfHarmProtectiveFactors["@value"] ===
                                    "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          {errors.SelfHarmProtectiveFactors !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.SelfHarmProtectiveFactors}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className="row">
                            <div className="col">
                              <TextArea
                                name="ProtectiveFactorsComments"
                                label="Comments:"
                                error={errors.ProtectiveFactorsComments}
                                value={data.ProtectiveFactorsComments["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("ProtectiveFactorsComments")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </div>
                        <br />
                        <b>Current Warning Signs</b>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="Threatening"
                                label="Threatening to hurt or kill oneself"
                                divClassName="ms-3"
                                checked={data.Threatening["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="SayingGoodbye"
                                label="Visiting or calling people to say goodbye"
                                divClassName="ms-3"
                                checked={
                                  data.SayingGoodbye["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="SeekingMeans"
                                label="Seeking a means to kill oneself"
                                divClassName="ms-3"
                                checked={data.SeekingMeans["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="SocialWithdrawal"
                                label="Social withdrawal/isolation"
                                divClassName="ms-3"
                                checked={
                                  data.SocialWithdrawal["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="Hopelessness"
                                label="Hopelessness"
                                divClassName="ms-3"
                                checked={data.Hopelessness["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="RecklessBehavior"
                                label="Reckless behavior/increase in risky activities"
                                divClassName="ms-3"
                                checked={
                                  data.RecklessBehavior["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="IncreasedSubstanceUse"
                                label="Increasing alcohol or drug use"
                                divClassName="ms-3"
                                checked={
                                  data.IncreasedSubstanceUse["@value"] ===
                                  "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="Feelings"
                                label="Feelings of alienation, loneliness, guilt, shame, or inadequacy"
                                divClassName="ms-3"
                                checked={data.Feelings["@value"] === "true"}
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="DramaticMoodChanges"
                                label="Dramatic mood changes"
                                divClassName="ms-3"
                                checked={
                                  data.DramaticMoodChanges["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="GivingAwayPossessions"
                                label="Giving away prized possessions"
                                divClassName="ms-3"
                                checked={
                                  data.GivingAwayPossessions["@value"] ===
                                  "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="NoWarningSigns"
                                label="None reported"
                                divClassName="ms-3"
                                checked={
                                  data.NoWarningSigns &&
                                  data.NoWarningSigns["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          {errors.WarningSigns !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.WarningSigns}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className="row">
                            <div className="col">
                              <TextArea
                                name="WarningSignsComments"
                                label="Comments:"
                                error={errors.WarningSignsComments}
                                value={data.WarningSignsComments["@value"]}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                identical={compare("WarningSignsComments")}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                        </div>
                      </>
                    ) : null}
                    <RadioButton
                      name="CaregiverConcernsOfSelfHarm"
                      label="Parent/Caregiver concerns of suicide/self-harm:"
                      error={errors.CaregiverConcernsOfSelfHarm}
                      value={data.CaregiverConcernsOfSelfHarm["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <RadioButton
                      name="OtherConcernsOfSelfHarm"
                      label="Other expressed concerns of suicide/self-harm:"
                      error={errors.OtherConcernsOfSelfHarm}
                      value={data.OtherConcernsOfSelfHarm["@value"]}
                      options={yesNoNoneOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <RadioButton
                      name="SelfHarmSafetyPlanStatus"
                      label="Safety plan status:"
                      error={errors.SelfHarmSafetyPlanStatus}
                      value={data.SelfHarmSafetyPlanStatus["@value"]}
                      options={safetyPlanStatusOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <b>Current Risk of Harm to Self:</b>
                    <Select
                      name="CurrentSelfHarmRisk"
                      error={errors.CurrentSelfHarmRisk}
                      value={data.CurrentSelfHarmRisk["@value"]}
                      options={riskOptions}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <TextArea
                      name="CurrentSelfHarmRiskComments"
                      label="Comments/Plan:"
                      error={errors.CurrentSelfHarmRiskComments}
                      value={data.CurrentSelfHarmRiskComments["@value"]}
                      onChange={(e) => {
                        handleChange(e);
                        buildClinicalNarrativeSummary();
                      }}
                      identical={compare("CurrentSelfHarmRiskComments")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <br />
                    <b>Harm to Others/Endangerment</b>
                    <RadioButton
                      name="ThreatsToHarmOthers"
                      label="Threats to harm/endanger others:"
                      error={errors.ThreatsToHarmOthers}
                      value={data.ThreatsToHarmOthers["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ThreatsToHarmOthers["@value"] !== "0" && (
                      <Input
                        name="ThreatsToHarmOthersDetails"
                        label="Describe:"
                        error={errors.ThreatsToHarmOthersDetails}
                        value={data.ThreatsToHarmOthersDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ThreatsToHarmOthersDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <RadioButton
                      name="ThoughtsOfHarm"
                      label="Thoughts of harming/endangering others:"
                      error={errors.ThoughtsOfHarm}
                      value={data.ThoughtsOfHarm["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ThoughtsOfHarm["@value"] !== "0" ? (
                      <Input
                        name="ThoughtsOfHarmDetails"
                        label="Describe:"
                        error={errors.ThoughtsOfHarmDetails}
                        value={data.ThoughtsOfHarmDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ThoughtsOfHarmDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                    <RadioButton
                      name="ActsOfHarm"
                      label="Acts of harm/endangering others:"
                      error={errors.ActsOfHarm}
                      value={data.ActsOfHarm["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ActsOfHarm["@value"] !== "0" && (
                      <Input
                        name="ActsOfHarmDetails"
                        label="Describe:"
                        error={errors.ActsOfHarmDetails}
                        value={data.ActsOfHarmDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ActsOfHarmDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <RadioButton
                      name="ActsOfHarmAnimals"
                      label="Acts of harm/endangering animals:"
                      error={errors.ActsOfHarmAnimals}
                      value={data.ActsOfHarmAnimals["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ActsOfHarmAnimals["@value"] !== "0" && (
                      <Input
                        name="ActsOfHarmAnimalsDetails"
                        label="Describe:"
                        error={errors.ActsOfHarmAnimalsDetails}
                        value={data.ActsOfHarmAnimalsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ActsOfHarmAnimalsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                    <RadioButton
                      name="ActsInvolvingWeapons"
                      label="Acts involving weapons:"
                      error={errors.ActsInvolvingWeapons}
                      value={data.ActsInvolvingWeapons["@value"]}
                      options={timeframeOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {data.ActsInvolvingWeapons["@value"] !== "0" && (
                      <Input
                        name="ActsInvolvingWeaponsDetails"
                        label="Describe:"
                        error={errors.ActsInvolvingWeaponsDetails}
                        value={data.ActsInvolvingWeaponsDetails["@value"]}
                        onChange={(e) => {
                          handleChange(e);
                          buildClinicalNarrativeSummary();
                        }}
                        identical={compare("ActsInvolvingWeaponsDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    )}
                  </>
                ) : null}
                <RadioButton
                  name="CaregiverConcernOfOtherHarm"
                  label="Parent/Caregiver concerns of harm/endangering others:"
                  error={errors.CaregiverConcernOfOtherHarm}
                  value={data.CaregiverConcernOfOtherHarm["@value"]}
                  options={yesNoNoneOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="OtherConcernsOfOtherHarm"
                  label="Other expressed concerns of harm/endangering others:"
                  error={errors.OtherConcernsOfOtherHarm}
                  value={data.OtherConcernsOfOtherHarm["@value"]}
                  options={yesNoNoneOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="OtherHarmSafetyPlanStatus"
                  label="Safety plan status:"
                  error={errors.OtherHarmSafetyPlanStatus}
                  value={data.OtherHarmSafetyPlanStatus["@value"]}
                  options={safetyPlanStatusOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <b>Current Risk of Harm to Others:</b>
                <Select
                  name="CurrentOtherHarmRisk"
                  error={errors.CurrentOtherHarmRisk}
                  value={data.CurrentOtherHarmRisk["@value"]}
                  options={riskOptions}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <TextArea
                  name="CurrentOtherHarmRiskDetails"
                  label="Comments/Plan:"
                  error={errors.CurrentOtherHarmRiskDetails}
                  value={data.CurrentOtherHarmRiskDetails["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("CurrentOtherHarmRiskDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              {moment(formsyDetails.CreatedOn).isBefore(
                moment("3/29/2022")
              ) && (
                <ExpandDiv
                  id="SymptomChecklist"
                  expandStatus={expandStatus}
                  className="mt-2"
                  title="Symptom Checklist"
                >
                  Mark if symptoms of diagnostic category are present. Describe
                  symptom <b>frequency, intensity and duration</b> if present to
                  assist in formulating diagnosis.
                  <div className="container">
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Depression"
                          label="Depression"
                          divClassName="ms-3"
                          checked={data.Depression["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Depression["@value"] === "true" ? (
                          <Input
                            name="DepressionDetails"
                            label="Describe:"
                            error={errors.DepressionDetails}
                            value={data.DepressionDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("DepressionDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Mania"
                          label="Mania"
                          divClassName="ms-3"
                          checked={data.Mania["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Mania["@value"] === "true" ? (
                          <Input
                            name="ManiaDetails"
                            label="Describe:"
                            error={errors.ManiaDetails}
                            value={data.ManiaDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("ManiaDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Bipolar"
                          label="Bipolar"
                          divClassName="ms-3"
                          checked={data.Bipolar["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Bipolar["@value"] === "true" ? (
                          <Input
                            name="BipolarDetails"
                            label="Describe:"
                            error={errors.BipolarDetails}
                            value={data.BipolarDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("BipolarDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="ObsessiveCompulsive"
                          label="Obsessive Compulsive"
                          divClassName="ms-3"
                          checked={
                            data.ObsessiveCompulsive["@value"] === "true"
                          }
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.ObsessiveCompulsive["@value"] === "true" ? (
                          <Input
                            name="ObsessiveCompulsiveDetails"
                            label="Describe:"
                            error={errors.ObsessiveCompulsiveDetails}
                            value={data.ObsessiveCompulsiveDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("ObsessiveCompulsiveDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Psychosis"
                          label="Psychosis"
                          divClassName="ms-3"
                          checked={data.Psychosis["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Psychosis["@value"] === "true" ? (
                          <Input
                            name="PsychosisDetails"
                            label="Describe:"
                            error={errors.PsychosisDetails}
                            value={data.PsychosisDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PsychosisDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="PTSD"
                          label="Post-Traumatic Stress (PTSD)"
                          divClassName="ms-3"
                          checked={data.PTSD["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.PTSD["@value"] === "true" ? (
                          <Input
                            name="PTSDDetails"
                            label="Describe:"
                            error={errors.PTSDDetails}
                            value={data.PTSDDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PTSDDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="PanicAttacks"
                          label="Panic Attacks"
                          divClassName="ms-3"
                          checked={data.PanicAttacks["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.PanicAttacks["@value"] === "true" ? (
                          <Input
                            name="PanicAttacksDetails"
                            label="Describe:"
                            error={errors.PanicAttacksDetails}
                            value={data.PanicAttacksDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PanicAttacksDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Anxiety"
                          label="Anxiety"
                          divClassName="ms-3"
                          checked={data.Anxiety["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Anxiety["@value"] === "true" ? (
                          <Input
                            name="AnxietyDetails"
                            label="Describe:"
                            error={errors.AnxietyDetails}
                            value={data.AnxietyDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("AnxietyDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Phobia"
                          label="Phobia"
                          divClassName="ms-3"
                          checked={data.Phobia["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Phobia["@value"] === "true" ? (
                          <Input
                            name="PhobiaDetails"
                            label="Describe:"
                            error={errors.PhobiaDetails}
                            value={data.PhobiaDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PhobiaDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="EatingDisorder"
                          label="Eating Disorder"
                          divClassName="ms-3"
                          checked={data.EatingDisorder["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.EatingDisorder["@value"] === "true" ? (
                          <Input
                            name="EatingDisorderDetails"
                            label="Describe:"
                            error={errors.EatingDisorderDetails}
                            value={data.EatingDisorderDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("EatingDisorderDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Elimination"
                          label="Elimination"
                          divClassName="ms-3"
                          checked={data.Elimination["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Elimination["@value"] === "true" ? (
                          <Input
                            name="EliminationDetails"
                            label="Describe:"
                            error={errors.EliminationDetails}
                            value={data.EliminationDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("EliminationDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Conduct"
                          label="Conduct"
                          divClassName="ms-3"
                          checked={data.Conduct["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Conduct["@value"] === "true" ? (
                          <Input
                            name="ConductDetails"
                            label="Describe:"
                            error={errors.ConductDetails}
                            value={data.ConductDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("ConductDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Oppositional"
                          label="Oppositional Defiance"
                          divClassName="ms-3"
                          checked={data.Oppositional["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Oppositional["@value"] === "true" ? (
                          <Input
                            name="OppositionalDetails"
                            label="Describe:"
                            error={errors.OppositionalDetails}
                            value={data.OppositionalDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("OppositionalDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Attention"
                          label="Attention"
                          divClassName="ms-3"
                          checked={data.Attention["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Attention["@value"] === "true" ? (
                          <Input
                            name="AttentionDetails"
                            label="Describe:"
                            error={errors.AttentionDetails}
                            value={data.AttentionDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("AttentionDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Adjustment"
                          label="Adjustment"
                          divClassName="ms-3"
                          checked={data.Adjustment["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Adjustment["@value"] === "true" ? (
                          <Input
                            name="AdjustmentDetails"
                            label="Describe:"
                            error={errors.AdjustmentDetails}
                            value={data.AdjustmentDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("AdjustmentDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="Attachment"
                          label="Attachment"
                          divClassName="ms-3"
                          checked={data.Attachment["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Attachment["@value"] === "true" ? (
                          <Input
                            name="AttachmentDetails"
                            label="Describe:"
                            error={errors.AttachmentDetails}
                            value={data.AttachmentDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("AttachmentDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="DevelopmentalImpairments"
                          label="Developmental Impairments"
                          divClassName="ms-3"
                          checked={
                            data.DevelopmentalImpairments["@value"] === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.DevelopmentalImpairments["@value"] === "true" ? (
                          <Input
                            name="DevelopmentalImpairmentsDetails"
                            label="Describe:"
                            error={errors.DevelopmentalImpairmentsDetails}
                            value={
                              data.DevelopmentalImpairmentsDetails["@value"]
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare(
                              "DevelopmentalImpairmentsDetails"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="SubstanceUse"
                          label="Substance Use"
                          divClassName="ms-3"
                          checked={data.SubstanceUse["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.SubstanceUse["@value"] === "true" ? (
                          <Input
                            name="SubstanceUseDetails"
                            label="Describe:"
                            error={errors.SubstanceUseDetails}
                            value={data.SubstanceUseDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("SubstanceUseDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                    <div className="row mt-2 border-bottom">
                      <div className="col">
                        <Checkbox
                          name="OtherSymptom"
                          label="Other"
                          divClassName="ms-3"
                          checked={data.OtherSymptom["@value"] === "true"}
                          commentsHandler={handleComments}
                          onChange={handleChange}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.OtherSymptom["@value"] === "true" ? (
                          <Input
                            name="OtherSymptomDetails"
                            label="Describe:"
                            error={errors.OtherSymptomDetails}
                            value={data.OtherSymptomDetails["@value"]}
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("OtherSymptomDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </ExpandDiv>
              )}
              {!moment(formsyDetails.CreatedOn).isBefore(
                moment("3/29/2022")
              ) && (
                <ExpandDiv
                  id="SymptomChecklist"
                  expandStatus={expandStatus}
                  className="mt-2"
                  title="Symptom Checklist"
                >
                  Mark if symptoms of diagnostic category are present. Describe
                  symptom <b>frequency, intensity and duration</b> if present to
                  assist in formulating diagnosis.
                  <br />
                  <div className="border rounded mb-2 px-2 pt-2 mt-2">
                    <Checkbox
                      name="TraumaSymptoms"
                      label="Symptoms of Trauma and Other Stressor-Related Disorders"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.TraumaSymptoms !== undefined &&
                        data.TraumaSymptoms["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleTraumaSymptomsChange}
                      disabled={formDisabled}
                    />
                    {data.TraumaSymptoms !== undefined &&
                      data.TraumaSymptoms["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="TraumaticExposure"
                                  label="Exposure to a traumatic event"
                                  divClassName="ms-3"
                                  checked={
                                    data.TraumaticExposure !== undefined &&
                                    data.TraumaticExposure["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="AvoidantTrauma"
                                  label="Persistent avoidant behavior"
                                  divClassName="ms-3"
                                  checked={
                                    data.AvoidantTrauma !== undefined &&
                                    data.AvoidantTrauma["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="HypervigilanceTrauma"
                                  label="Hypervigilance"
                                  divClassName="ms-3"
                                  checked={
                                    data.HypervigilanceTrauma !== undefined &&
                                    data.HypervigilanceTrauma["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="IntrusiveDreams"
                                  label="Intrusive dreams/memories"
                                  divClassName="ms-3"
                                  checked={
                                    data.IntrusiveDreams !== undefined &&
                                    data.IntrusiveDreams["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="NegativeThoughts"
                                  label="Negative thoughts and mood"
                                  divClassName="ms-3"
                                  checked={
                                    data.NegativeThoughts !== undefined &&
                                    data.NegativeThoughts["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="CocentrationProblems"
                                  label="Problems with concentration"
                                  divClassName="ms-3"
                                  checked={
                                    data.CocentrationProblems !== undefined &&
                                    data.CocentrationProblems["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="RecklessBehaviorTrauma"
                                  label="Irritable or reckless behavior"
                                  divClassName="ms-3"
                                  checked={
                                    data.RecklessBehaviorTrauma !== undefined &&
                                    data.RecklessBehaviorTrauma["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="SleepDisturbancesTrauma"
                                  label="Sleep disturbances"
                                  divClassName="ms-3"
                                  checked={
                                    data.SleepDisturbancesTrauma !==
                                      undefined &&
                                    data.SleepDisturbancesTrauma["@value"] ===
                                      "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="OtherTrauma"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherTrauma !== undefined &&
                                    data.OtherTrauma["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OtherTrauma !== undefined &&
                                data.OtherTrauma["@value"] === "true" ? (
                                  <Input
                                    name="OtherTraumaDetails"
                                    error={errors.OtherTraumaDetails}
                                    value={
                                      data.OtherTraumaDetails !== undefined
                                        ? data.OtherTraumaDetails["@value"]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare("OtherTraumaDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.TraumaSymptoms !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.TraumaSymptoms}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="TraumaSymptomsFrequency"
                            label="Frequency:"
                            error={errors.TraumaSymptomsFrequency}
                            value={
                              data.TraumaSymptomsFrequency !== undefined
                                ? data.TraumaSymptomsFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("TraumaSymptomsFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="TraumaSymptomsIntensity"
                            label="Intensity:"
                            error={errors.TraumaSymptomsIntensity}
                            value={
                              data.TraumaSymptomsIntensity !== undefined
                                ? data.TraumaSymptomsIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("TraumaSymptomsIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="TraumaSymptomsDuration"
                            label="Duration:"
                            error={errors.TraumaSymptomsDuration}
                            value={
                              data.TraumaSymptomsDuration !== undefined
                                ? data.TraumaSymptomsDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("TraumaSymptomsDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="TraumaSymptomsOnset"
                            label="Onset:"
                            error={errors.TraumaSymptomsOnset}
                            value={
                              data.TraumaSymptomsOnset !== undefined
                                ? data.TraumaSymptomsOnset["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("TraumaSymptomsOnset")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnoses to Consider: Post-traumatic Stress Disorder
                          (PTSD), Acute Stress Disorder, Adjustment Disorder,
                          Other Specified Trauma-and Stressor-Related Disorder
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="AnxietyDisorder"
                      label="Symptoms of Anxiety"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.AnxietyDisorder !== undefined &&
                        data.AnxietyDisorder["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleAnxietyDisorderChange}
                      disabled={formDisabled}
                    />
                    {data.AnxietyDisorder !== undefined &&
                      data.AnxietyDisorder["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="AnxietyDisorderExcessWorry"
                                  label="Excess worry"
                                  divClassName="ms-3"
                                  checked={
                                    data.AnxietyDisorderExcessWorry !==
                                      undefined &&
                                    data.AnxietyDisorderExcessWorry[
                                      "@value"
                                    ] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="AnxietyDisorderEasilyFatigued"
                                  label="Easily fatigued"
                                  divClassName="ms-3"
                                  checked={
                                    data.AnxietyDisorderEasilyFatigued !==
                                      undefined &&
                                    data.AnxietyDisorderEasilyFatigued[
                                      "@value"
                                    ] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="MuscleTension"
                                  label="Muscle tension"
                                  divClassName="ms-3"
                                  checked={
                                    data.MuscleTension !== undefined &&
                                    data.MuscleTension["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Overworried"
                                  label="Difficulty controlling worry"
                                  divClassName="ms-3"
                                  checked={
                                    data.Overworried !== undefined &&
                                    data.Overworried["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="AnxietyDisorderDifficultyConcentrating"
                                  label="Difficulty concentrating"
                                  divClassName="ms-3"
                                  checked={
                                    data.AnxietyDisorderDifficultyConcentrating !==
                                      undefined &&
                                    data.AnxietyDisorderDifficultyConcentrating[
                                      "@value"
                                    ] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="SleepDisturbancesAnxiety"
                                  label="Sleep difficulties"
                                  divClassName="ms-3"
                                  checked={
                                    data.SleepDisturbancesAnxiety !==
                                      undefined &&
                                    data.SleepDisturbancesAnxiety["@value"] ===
                                      "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="RestlessAnxiety"
                                  label="Restless"
                                  divClassName="ms-3"
                                  checked={
                                    data.RestlessAnxiety !== undefined &&
                                    data.RestlessAnxiety["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Irritability"
                                  label="Irritability"
                                  divClassName="ms-3"
                                  checked={
                                    data.Irritability !== undefined &&
                                    data.Irritability["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="OtherAnxiety"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherAnxiety !== undefined &&
                                    data.OtherAnxiety["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                                {data.OtherAnxiety !== undefined &&
                                data.OtherAnxiety["@value"] === "true" ? (
                                  <Input
                                    name="OtherAnxietyDetails"
                                    error={errors.OtherAnxietyDetails}
                                    value={
                                      data.OtherAnxietyDetails !== undefined
                                        ? data.OtherAnxietyDetails["@value"]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare("OtherAnxietyDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.AnxietyDisorder !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.AnxietyDisorder}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="AnxietyDisorderFrequency"
                            label="Frequency:"
                            error={errors.AnxietyDisorderFrequency}
                            value={
                              data.AnxietyDisorderFrequency !== undefined
                                ? data.AnxietyDisorderFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("AnxietyDisorderFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="AnxietyDisorderIntensity"
                            label="Intensity:"
                            error={errors.AnxietyDisorderIntensity}
                            value={
                              data.AnxietyDisorderIntensity !== undefined
                                ? data.AnxietyDisorderIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("AnxietyDisorderIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="AnxietyDisorderDuration"
                            label="Duration:"
                            error={errors.AnxietyDisorderDuration}
                            value={
                              data.AnxietyDisorderDuration !== undefined
                                ? data.AnxietyDisorderDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("AnxietyDisorderDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Generalized Anxiety Disorder
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="DepressionSymptoms"
                      label="Symptoms of Depression"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.DepressionSymptoms !== undefined &&
                        data.DepressionSymptoms["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleDepressionChange}
                      disabled={formDisabled}
                    />
                    {data.DepressionSymptoms !== undefined &&
                      data.DepressionSymptoms["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DepressedMood"
                                  label="Depressed mood most of the day, nearly every day"
                                  divClassName="ms-3"
                                  checked={
                                    data.DepressedMood !== undefined &&
                                    data.DepressedMood["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="SleepChanges"
                                  label="Insomnia or hypersomnia"
                                  divClassName="ms-3"
                                  checked={
                                    data.SleepChanges !== undefined &&
                                    data.SleepChanges["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Guilt"
                                  label="Feelings of worthlessness/guilt"
                                  divClassName="ms-3"
                                  checked={
                                    data.Guilt !== undefined &&
                                    data.Guilt["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DiminishedInterest"
                                  label="Diminished interest or pleasure in most daily activities"
                                  divClassName="ms-3"
                                  checked={
                                    data.DiminishedInterest !== undefined &&
                                    data.DiminishedInterest["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="PurposelessMovement"
                                  label="Purposeless movement"
                                  divClassName="ms-3"
                                  checked={
                                    data.PurposelessMovement !== undefined &&
                                    data.PurposelessMovement["@value"] ===
                                      "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DifficultyConcentratingDepression"
                                  label="Inability to think/concentrate"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyConcentratingDepression !==
                                      undefined &&
                                    data.DifficultyConcentratingDepression[
                                      "@value"
                                    ] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="WeightChanges"
                                  label="Significant weight loss or weight gain"
                                  divClassName="ms-3"
                                  checked={
                                    data.WeightChanges !== undefined &&
                                    data.WeightChanges["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FatigueDepression"
                                  label="Fatigue"
                                  divClassName="ms-3"
                                  checked={
                                    data.FatigueDepression !== undefined &&
                                    data.FatigueDepression["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ThoughtsOfDeath"
                                  label="Recurrent thoughts of death"
                                  divClassName="ms-3"
                                  checked={
                                    data.ThoughtsOfDeath !== undefined &&
                                    data.ThoughtsOfDeath["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="OtherDepression"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherDepression !== undefined &&
                                    data.OtherDepression["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OtherDepression !== undefined &&
                                data.OtherDepression["@value"] === "true" ? (
                                  <Input
                                    name="OtherDepressionDetails"
                                    error={errors.OtherDepressionDetails}
                                    value={
                                      data.OtherDepressionDetails !== undefined
                                        ? data.OtherDepressionDetails["@value"]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare(
                                      "OtherDepressionDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.DepressionSymptoms !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.DepressionSymptoms}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="DepressionFrequency"
                            label="Frequency:"
                            error={errors.DepressionFrequency}
                            value={
                              data.DepressionFrequency !== undefined
                                ? data.DepressionFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("DepressionFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="DepressionIntensity"
                            label="Intensity:"
                            error={errors.DepressionIntensity}
                            value={
                              data.DepressionIntensity !== undefined
                                ? data.DepressionIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("DepressionIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="DepressionDuration"
                            label="Duration:"
                            error={errors.DepressionDuration}
                            value={
                              data.DepressionDuration !== undefined
                                ? data.DepressionDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("DepressionDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Major Depressive Disorder,
                          Other Specified Depressive Disorder, Unspecified
                          Depressive Disorder
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="AttentionHyperactivity"
                      label="Symptoms of Attention and Hyperactivity"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.AttentionHyperactivity !== undefined &&
                        data.AttentionHyperactivity["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleAttentionHyperactivityChange}
                      disabled={formDisabled}
                    />
                    {data.AttentionHyperactivity !== undefined &&
                      data.AttentionHyperactivity["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="AttentionHyperactivityEasilyDistracted"
                                  label="Easily distracted"
                                  divClassName="ms-3"
                                  checked={
                                    data.AttentionHyperactivityEasilyDistracted !==
                                      undefined &&
                                    data.AttentionHyperactivityEasilyDistracted[
                                      "@value"
                                    ] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DifficultyFollowThrough"
                                  label="Difficulty following-through"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyFollowThrough !==
                                      undefined &&
                                    data.DifficultyFollowThrough["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Forgetful"
                                  label="Forgetful"
                                  divClassName="ms-3"
                                  checked={
                                    data.Forgetful !== undefined &&
                                    data.Forgetful["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DifficultyListening"
                                  label="Difficulty listening"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyListening !== undefined &&
                                    data.DifficultyListening["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DisorganizedAttention"
                                  label="Disorganized in tasks and activities"
                                  divClassName="ms-3"
                                  checked={
                                    data.DisorganizedAttention !== undefined &&
                                    data.DisorganizedAttention["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Fidgety"
                                  label="Fidgety"
                                  divClassName="ms-3"
                                  checked={
                                    data.Fidgety !== undefined &&
                                    data.Fidgety["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="LackOfDetails"
                                  label="Lack of attention to detail"
                                  divClassName="ms-3"
                                  checked={
                                    data.LackOfDetails !== undefined &&
                                    data.LackOfDetails["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="LosesThings"
                                  label="Loses things"
                                  divClassName="ms-3"
                                  checked={
                                    data.LosesThings !== undefined &&
                                    data.LosesThings["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="InterruptsOthers"
                                  label="Interrupts others"
                                  divClassName="ms-3"
                                  checked={
                                    data.InterruptsOthers !== undefined &&
                                    data.InterruptsOthers["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="UnableToSitStill"
                                  label="Unable to sit still"
                                  divClassName="ms-3"
                                  checked={
                                    data.UnableToSitStill !== undefined &&
                                    data.UnableToSitStill["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ExcessiveTalking"
                                  label="Excessive talking"
                                  divClassName="ms-3"
                                  checked={
                                    data.ExcessiveTalking !== undefined &&
                                    data.ExcessiveTalking["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DifficultyWaiting"
                                  label="Difficulty waiting for their turn"
                                  divClassName="ms-3"
                                  checked={
                                    data.DifficultyWaiting !== undefined &&
                                    data.DifficultyWaiting["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="OtherAttention"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherAttention !== undefined &&
                                    data.OtherAttention["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                                {data.OtherAttention !== undefined &&
                                data.OtherAttention["@value"] === "true" ? (
                                  <Input
                                    name="OtherAttentionDetails"
                                    error={errors.OtherAttentionDetails}
                                    value={
                                      data.OtherAttentionDetails !== undefined
                                        ? data.OtherAttentionDetails["@value"]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare("OtherAttentionDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.AttentionHyperactivity !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.AttentionHyperactivity}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="AttentionHyperactivityFrequency"
                            label="Frequency:"
                            error={errors.AttentionHyperactivityFrequency}
                            value={
                              data.AttentionHyperactivityFrequency !== undefined
                                ? data.AttentionHyperactivityFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare(
                              "AttentionHyperactivityFrequency"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="AttentionHyperactivityIntensity"
                            label="Intensity:"
                            error={errors.AttentionHyperactivityIntensity}
                            value={
                              data.AttentionHyperactivityIntensity !== undefined
                                ? data.AttentionHyperactivityIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare(
                              "AttentionHyperactivityIntensity"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="AttentionHyperactivityDuration"
                            label="Duration:"
                            error={errors.AttentionHyperactivityDuration}
                            value={
                              data.AttentionHyperactivityDuration !== undefined
                                ? data.AttentionHyperactivityDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare(
                              "AttentionHyperactivityDuration"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Attention-Deficit/Hyperactivity
                          Disorder
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="SU"
                      label="Symptoms of Substance Use Disorders"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.SU !== undefined && data.SU["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleSubstanceUseSymptomsChange}
                      disabled={formDisabled}
                    />
                    {data.SU !== undefined && data.SU["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="PatternOfSubstanceUse"
                                label="Takes substances in large amounts and/or over long periods of time"
                                divClassName="ms-3"
                                checked={
                                  data.PatternOfSubstanceUse !== undefined &&
                                  data.PatternOfSubstanceUse["@value"] ===
                                    "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="DesiresCutDownUse"
                                label="Persistent desire to cut down on substance use"
                                divClassName="ms-3"
                                checked={
                                  data.DesiresCutDownUse !== undefined &&
                                  data.DesiresCutDownUse["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="TimeSpentObtaining"
                                label="Spends a significant amount of time obtaining substance(s)"
                                divClassName="ms-3"
                                checked={
                                  data.TimeSpentObtaining !== undefined &&
                                  data.TimeSpentObtaining["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="Cravings"
                                label="Cravings for substances"
                                divClassName="ms-3"
                                checked={
                                  data.Cravings !== undefined &&
                                  data.Cravings["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="FailureOfResponsibilities"
                                label="Failure to fulfill major roles at work, school, or home"
                                divClassName="ms-3"
                                checked={
                                  data.FailureOfResponsibilities !==
                                    undefined &&
                                  data.FailureOfResponsibilities["@value"] ===
                                    "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="NegativeImpact"
                                label="Use consistently negatively impacts relationships"
                                divClassName="ms-3"
                                checked={
                                  data.NegativeImpact !== undefined &&
                                  data.NegativeImpact["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="AbandonsActivities"
                                label="Giving up/reducing previously enjoyed activities"
                                divClassName="ms-3"
                                checked={
                                  data.AbandonsActivities !== undefined &&
                                  data.AbandonsActivities["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="Tolerance"
                                label="Tolerance"
                                divClassName="ms-3"
                                checked={
                                  data.Tolerance !== undefined &&
                                  data.Tolerance["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="SUWithdrawal"
                                label="Withdrawal"
                                divClassName="ms-3"
                                checked={
                                  data.SUWithdrawal !== undefined &&
                                  data.SUWithdrawal["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <Checkbox
                                name="SUOther"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.SUOther !== undefined &&
                                  data.SUOther["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                              {data.SUOther !== undefined &&
                              data.SUOther["@value"] === "true" ? (
                                <Input
                                  name="SUOtherDetails"
                                  error={errors.SUOtherDetails}
                                  value={
                                    data.SUOtherDetails !== undefined
                                      ? data.SUOtherDetails["@value"]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare("SUOtherDetails")}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.SU !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.SU}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        <Input
                          name="SubstanceUseFrequency"
                          label="Frequency:"
                          error={errors.SubstanceUseFrequency}
                          value={
                            data.SubstanceUseFrequency !== undefined
                              ? data.SubstanceUseFrequency["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SubstanceUseFrequency")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <Input
                          name="SubstanceUseIntensity"
                          label="Intensity:"
                          error={errors.SubstanceUseIntensity}
                          value={
                            data.SubstanceUseIntensity !== undefined
                              ? data.SubstanceUseIntensity["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SubstanceUseIntensity")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        <Input
                          name="SubstanceUseDuration"
                          label="Duration:"
                          error={errors.SubstanceUseDuration}
                          value={
                            data.SubstanceUseDuration !== undefined
                              ? data.SubstanceUseDuration["@value"]
                              : ""
                          }
                          onChange={(e) => {
                            handleChange(e);
                            buildClinicalNarrativeSummary();
                          }}
                          identical={compare("SubstanceUseDuration")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </>
                    )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="DMDD"
                      label="Symptoms of Disruptive Mood Dysregulation Disorder"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.DMDD !== undefined &&
                        data.DMDD["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleDMDDChange}
                      disabled={formDisabled}
                    />
                    {data.DMDD !== undefined &&
                      data.DMDD["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Temper"
                                  label="Severe and recurrent temper outbursts (verbally and/or behaviorally)"
                                  divClassName="ms-3"
                                  checked={
                                    data.Temper !== undefined &&
                                    data.Temper["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="OutsideOutbursts"
                                  label="Outbursts are outside of developmental level"
                                  divClassName="ms-3"
                                  checked={
                                    data.OutsideOutbursts !== undefined &&
                                    data.OutsideOutbursts["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FrequentOutbursts"
                                  label="Outbursts occur three or more times per week"
                                  divClassName="ms-3"
                                  checked={
                                    data.FrequentOutbursts !== undefined &&
                                    data.FrequentOutbursts["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="DMDDIrritable"
                                  label="Irritable/angry mood most of the day"
                                  divClassName="ms-3"
                                  checked={
                                    data.DMDDIrritable !== undefined &&
                                    data.DMDDIrritable["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="OutburstsAroundOthers"
                                  label="Symptoms occur in home, school and/or with peers"
                                  divClassName="ms-3"
                                  checked={
                                    data.OutburstsAroundOthers !== undefined &&
                                    data.OutburstsAroundOthers["@value"] ===
                                      "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DMDDOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.DMDDOther !== undefined &&
                                    data.DMDDOther["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.DMDDOther !== undefined &&
                                data.DMDDOther["@value"] === "true" ? (
                                  <Input
                                    name="DMDDOtherDetails"
                                    error={errors.DMDDOtherDetails}
                                    value={
                                      data.DMDDOtherDetails !== undefined
                                        ? data.DMDDOtherDetails["@value"]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare("DMDDOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.DMDD !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.DMDD}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="DMDDFrequency"
                            label="Frequency:"
                            error={errors.DMDDFrequency}
                            value={
                              data.DMDDFrequency !== undefined
                                ? data.DMDDFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("DMDDFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="DMDDIntensity"
                            label="Intensity:"
                            error={errors.DMDDIntensity}
                            value={
                              data.DMDDIntensity !== undefined
                                ? data.DMDDIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("DMDDIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="DMDDDuration"
                            label="Duration:"
                            error={errors.DMDDDuration}
                            value={
                              data.DMDDDuration !== undefined
                                ? data.DMDDDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("DMDDDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="ConductDisorder"
                      label="Symptoms of Conduct Disorder"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.ConductDisorder !== undefined &&
                        data.ConductDisorder["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleConductDisorderChange}
                      disabled={formDisabled}
                    />
                    {data.ConductDisorder !== undefined &&
                      data.ConductDisorder["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Aggression"
                                  label="Aggression to people and/or animals"
                                  divClassName="ms-3"
                                  checked={
                                    data.Aggression !== undefined &&
                                    data.Aggression["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DestructionOfProperty"
                                  label="Destruction of Property"
                                  divClassName="ms-3"
                                  checked={
                                    data.DestructionOfProperty !== undefined &&
                                    data.DestructionOfProperty["@value"] ===
                                      "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Theft"
                                  label="Deceitfulness or theft"
                                  divClassName="ms-3"
                                  checked={
                                    data.Theft !== undefined &&
                                    data.Theft["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="SeriousViolations"
                                  label="Serious violations of rules"
                                  divClassName="ms-3"
                                  checked={
                                    data.SeriousViolations !== undefined &&
                                    data.SeriousViolations["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col-4">
                                <Checkbox
                                  name="OtherConductDisorder"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherConductDisorder !== undefined &&
                                    data.OtherConductDisorder["@value"] ===
                                      "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OtherConductDisorder !== undefined &&
                                data.OtherConductDisorder["@value"] ===
                                  "true" ? (
                                  <Input
                                    name="OtherConductDisorderDetails"
                                    error={errors.OtherConductDisorderDetails}
                                    value={
                                      data.OtherConductDisorderDetails !==
                                      undefined
                                        ? data.OtherConductDisorderDetails[
                                            "@value"
                                          ]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare(
                                      "OtherConductDisorderDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.ConductDisorder !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.ConductDisorder}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="ConductDisorderFrequency"
                            label="Frequency:"
                            error={errors.ConductDisorderFrequency}
                            value={
                              data.ConductDisorderFrequency !== undefined
                                ? data.ConductDisorderFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("ConductDisorderFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="ConductDisorderIntensity"
                            label="Intensity:"
                            error={errors.ConductDisorderIntensity}
                            value={
                              data.ConductDisorderIntensity !== undefined
                                ? data.ConductDisorderIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("ConductDisorderIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="ConductDisorderDuration"
                            label="Duration:"
                            error={errors.ConductDisorderDuration}
                            value={
                              data.ConductDisorderDuration !== undefined
                                ? data.ConductDisorderDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("ConductDisorderDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="ODD"
                      label="Symptoms of Oppositional Defiance Disorder (ODD)"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.ODD !== undefined && data.ODD["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleODDChange}
                      disabled={formDisabled}
                    />
                    {data.ODD !== undefined &&
                      data.ODD["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="IrritableODD"
                                  label="Angry/irritable mood"
                                  divClassName="ms-3"
                                  checked={
                                    data.IrritableODD !== undefined &&
                                    data.IrritableODD["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Argumentative"
                                  label="Argumentative/defiant"
                                  divClassName="ms-3"
                                  checked={
                                    data.Argumentative !== undefined &&
                                    data.Argumentative["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Vindictive"
                                  label="Vindictive"
                                  divClassName="ms-3"
                                  checked={
                                    data.Vindictive !== undefined &&
                                    data.Vindictive["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="OtherODD"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OtherODD !== undefined &&
                                    data.OtherODD["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                                {data.OtherODD !== undefined &&
                                data.OtherODD["@value"] === "true" ? (
                                  <Input
                                    name="OtherODDDetails"
                                    error={errors.OtherODDDetails}
                                    value={
                                      data.OtherODDDetails !== undefined
                                        ? data.OtherODDDetails["@value"]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare("OtherODDDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.ODD !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.ODD}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="ODDFrequency"
                            label="Frequency:"
                            error={errors.ODDFrequency}
                            value={
                              data.ODDFrequency !== undefined
                                ? data.ODDFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("ODDFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="ODDIntensity"
                            label="Intensity:"
                            error={errors.ODDIntensity}
                            value={
                              data.ODDIntensity !== undefined
                                ? data.ODDIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("ODDIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="ODDDuration"
                            label="Duration:"
                            error={errors.ODDDuration}
                            value={
                              data.ODDDuration !== undefined
                                ? data.ODDDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("ODDDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="OCD"
                      label="Symptoms of Obsessive-Compulsive Disorder (OCD)"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.OCD !== undefined && data.OCD["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleOCDChange}
                      disabled={formDisabled}
                    />
                    {data.OCD !== undefined &&
                      data.OCD["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="OCDPresence"
                                  label="Presence of obsessions, compulsions, or both"
                                  divClassName="ms-3"
                                  checked={
                                    data.OCDPresence !== undefined &&
                                    data.OCDPresence["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Obsessions"
                                  label="Obsessions (recurrent and persistent thoughts, urges, or images)"
                                  divClassName="ms-3"
                                  checked={
                                    data.Obsessions !== undefined &&
                                    data.Obsessions["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="TimeConsuming"
                                  label="Obsessions or compulsions are time consuming and cause distress/impairment"
                                  divClassName="ms-3"
                                  checked={
                                    data.TimeConsuming !== undefined &&
                                    data.TimeConsuming["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Compulsions"
                                  label="Compulsions/repetitive behaviors (hand washing, ordering, checking,) or mental acts (praying, counting, repeating words silently)"
                                  divClassName="ms-3"
                                  checked={
                                    data.Compulsions !== undefined &&
                                    data.Compulsions["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ReduceAnxiety"
                                  label="Behaviors and mental acts done to prevent or reduce anxiety or distress"
                                  divClassName="ms-3"
                                  checked={
                                    data.ReduceAnxiety !== undefined &&
                                    data.ReduceAnxiety["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="SuppressUrges"
                                  label="Attempts to ignore or suppress thoughts, urges or images with other thought or action (compulsion)"
                                  divClassName="ms-3"
                                  checked={
                                    data.SuppressUrges !== undefined &&
                                    data.SuppressUrges["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="OCDOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.OCDOther !== undefined &&
                                    data.OCDOther["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.OCDOther !== undefined &&
                                data.OCDOther["@value"] === "true" ? (
                                  <Input
                                    name="OCDOtherDetails"
                                    error={errors.OCDOtherDetails}
                                    value={
                                      data.OCDOtherDetails !== undefined
                                        ? data.OCDOtherDetails["@value"]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare("OCDOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.OCD !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.OCD}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="OCDFrequency"
                            label="Frequency:"
                            error={errors.OCDFrequency}
                            value={
                              data.OCDFrequency !== undefined
                                ? data.OCDFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("OCDFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OCDIntensity"
                            label="Intensity:"
                            error={errors.OCDIntensity}
                            value={
                              data.OCDIntensity !== undefined
                                ? data.OCDIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("OCDIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OCDDuration"
                            label="Duration:"
                            error={errors.OCDDuration}
                            value={
                              data.OCDDuration !== undefined
                                ? data.OCDDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("OCDDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="PanicDisorder"
                      label="Symptoms of Panic Disorder"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.PanicDisorder !== undefined &&
                        data.PanicDisorder["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handlePanicDisorderChange}
                      disabled={formDisabled}
                    />
                    {data.PanicDisorder !== undefined &&
                      data.PanicDisorder["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="PoundingHeart"
                                  label="Palpitations, pounding heart, accelerated heart rate"
                                  divClassName="ms-3"
                                  checked={
                                    data.PoundingHeart !== undefined &&
                                    data.PoundingHeart["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="PanicDisorderSweating"
                                  label="Sweating"
                                  divClassName="ms-3"
                                  checked={
                                    data.PanicDisorderSweating !== undefined &&
                                    data.PanicDisorderSweating["@value"] ===
                                      "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Trembling"
                                  label="Trembling/shaking"
                                  divClassName="ms-3"
                                  checked={
                                    data.Trembling !== undefined &&
                                    data.Trembling["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="ShortnessOfBreath"
                                  label="Shortness of breath"
                                  divClassName="ms-3"
                                  checked={
                                    data.ShortnessOfBreath !== undefined &&
                                    data.ShortnessOfBreath["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Choking"
                                  label="Feelings of choking"
                                  divClassName="ms-3"
                                  checked={
                                    data.Choking !== undefined &&
                                    data.Choking["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ChestPain"
                                  label="Chest pain/discomfort"
                                  divClassName="ms-3"
                                  checked={
                                    data.ChestPain !== undefined &&
                                    data.ChestPain["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="PanicDisorderNausea"
                                  label="Nausea/abdominal discomfort"
                                  divClassName="ms-3"
                                  checked={
                                    data.PanicDisorderNausea !== undefined &&
                                    data.PanicDisorderNausea["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Dizzy"
                                  label="Dizzy/faint"
                                  divClassName="ms-3"
                                  checked={
                                    data.Dizzy !== undefined &&
                                    data.Dizzy["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="Chills"
                                  label="Chills"
                                  divClassName="ms-3"
                                  checked={
                                    data.Chills !== undefined &&
                                    data.Chills["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Derealization"
                                  label="Derealization or depersonalization"
                                  divClassName="ms-3"
                                  checked={
                                    data.Derealization !== undefined &&
                                    data.Derealization["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FearOfCrazy"
                                  label="Fear of losing control/going crazy"
                                  divClassName="ms-3"
                                  checked={
                                    data.FearOfCrazy !== undefined &&
                                    data.FearOfCrazy["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="FearOfDying"
                                  label="Fear of dying"
                                  divClassName="ms-3"
                                  checked={
                                    data.FearOfDying !== undefined &&
                                    data.FearOfDying["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="PanicDisorderOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.PanicDisorderOther !== undefined &&
                                    data.PanicDisorderOther["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                                {data.PanicDisorderOther !== undefined &&
                                data.PanicDisorderOther["@value"] === "true" ? (
                                  <Input
                                    name="PanicDisorderOtherDetails"
                                    error={errors.PanicDisorderOtherDetails}
                                    value={
                                      data.PanicDisorderOtherDetails !==
                                      undefined
                                        ? data.PanicDisorderOtherDetails[
                                            "@value"
                                          ]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare(
                                      "PanicDisorderOtherDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.PanicDisorder !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.PanicDisorder}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="PanicDisorderFrequency"
                            label="Frequency:"
                            error={errors.PanicDisorderFrequency}
                            value={
                              data.PanicDisorderFrequency !== undefined
                                ? data.PanicDisorderFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PanicDisorderFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="PanicDisorderIntensity"
                            label="Intensity:"
                            error={errors.PanicDisorderIntensity}
                            value={
                              data.PanicDisorderIntensity !== undefined
                                ? data.PanicDisorderIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PanicDisorderIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="PanicDisorderDuration"
                            label="Duration:"
                            error={errors.PanicDisorderDuration}
                            value={
                              data.PanicDisorderDuration !== undefined
                                ? data.PanicDisorderDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PanicDisorderDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="Phobias"
                      label="Symptoms of Phobias"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.Phobias !== undefined &&
                        data.Phobias["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handlePhobiasChange}
                      disabled={formDisabled}
                    />
                    {data.Phobias !== undefined &&
                      data.Phobias["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="PhobiasSpecificFear"
                                  label="Marked fear/anxiety about a specific object or situation"
                                  divClassName="ms-3"
                                  checked={
                                    data.PhobiasSpecificFear !== undefined &&
                                    data.PhobiasSpecificFear["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ImmediateFear"
                                  label="Object or situation provokes immediate fear or anxiety"
                                  divClassName="ms-3"
                                  checked={
                                    data.ImmediateFear !== undefined &&
                                    data.ImmediateFear["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="PhobiasAvoidance"
                                  label="Avoids objective/situation"
                                  divClassName="ms-3"
                                  checked={
                                    data.PhobiasAvoidance !== undefined &&
                                    data.PhobiasAvoidance["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="FearExaggerated"
                                  label="Fear and anxiety is out of proportion"
                                  divClassName="ms-3"
                                  checked={
                                    data.FearExaggerated !== undefined &&
                                    data.FearExaggerated["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col-4">
                                <Checkbox
                                  name="PhobiasOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.PhobiasOther !== undefined &&
                                    data.PhobiasOther["@value"] === "true"
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.PhobiasOther !== undefined &&
                                data.PhobiasOther["@value"] === "true" ? (
                                  <Input
                                    name="PhobiasOtherDetails"
                                    error={errors.PhobiasOtherDetails}
                                    value={
                                      data.PhobiasOtherDetails !== undefined
                                        ? data.PhobiasOtherDetails["@value"]
                                        : ""
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare("PhobiasOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.Phobias !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.Phobias}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="PhobiasFrequency"
                            label="Frequency:"
                            error={errors.PhobiasFrequency}
                            value={
                              data.PhobiasFrequency !== undefined
                                ? data.PhobiasFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PhobiasFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="PhobiasIntensity"
                            label="Intensity:"
                            error={errors.PhobiasIntensity}
                            value={
                              data.PhobiasIntensity !== undefined
                                ? data.PhobiasIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PhobiasIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="PhobiasDuration"
                            label="Duration:"
                            error={errors.PhobiasDuration}
                            value={
                              data.PhobiasDuration !== undefined
                                ? data.PhobiasDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PhobiasDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Specific Phobia, Social Anxiety
                          Disorder (Social Phobia), Agoraphobia
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="DI"
                      label="Developmental Impairments"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.DI !== undefined && data.DI["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleDIChange}
                      disabled={formDisabled}
                    />
                    {data.DI !== undefined && data.DI["@value"] === "true" && (
                      <>
                        <div className="container">
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="DILowIQ"
                                label="Low intelligence/IQ"
                                divClassName="ms-3"
                                checked={
                                  data.DILowIQ !== undefined &&
                                  data.DILowIQ["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="DISensoryImpairments"
                                label="Sensory impairments"
                                divClassName="ms-3"
                                checked={
                                  data.DISensoryImpairments !== undefined &&
                                  data.DISensoryImpairments["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="DISpeechDelays"
                                label="Language/speech delays"
                                divClassName="ms-3"
                                checked={
                                  data.DISpeechDelays !== undefined &&
                                  data.DISpeechDelays["@value"] === "true"
                                }
                                commentsHandler={handleComments}
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col">
                              <Checkbox
                                name="DIMotorCoordinationDelays"
                                label="Motor coordination delays"
                                divClassName="ms-3"
                                checked={
                                  data.DIMotorCoordinationDelays !==
                                    undefined &&
                                  data.DIMotorCoordinationDelays["@value"] ===
                                    "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="DILearningDisorder"
                                label="Specific learning disorder"
                                divClassName="ms-3"
                                checked={
                                  data.DILearningDisorder !== undefined &&
                                  data.DILearningDisorder["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                            <div className="col">
                              <Checkbox
                                name="DISpatialDelays"
                                label="Visio-spatial skill delays"
                                divClassName="ms-3"
                                checked={
                                  data.DISpatialDelays !== undefined &&
                                  data.DISpatialDelays["@value"] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-4">
                              <Checkbox
                                name="DevelopmentalImpairmentsOther"
                                label="Other:"
                                divClassName="ms-3"
                                checked={
                                  data.DevelopmentalImpairmentsOther !==
                                    undefined &&
                                  data.DevelopmentalImpairmentsOther[
                                    "@value"
                                  ] === "true"
                                }
                                onChange={(e) => {
                                  handleChange(e);
                                  buildClinicalNarrativeSummary();
                                }}
                                commentsHandler={handleComments}
                                disabled={formDisabled}
                              />
                              {data.DevelopmentalImpairmentsOther !==
                                undefined &&
                              data.DevelopmentalImpairmentsOther["@value"] ===
                                "true" ? (
                                <Input
                                  name="DevelopmentalImpairmentsOtherDetails"
                                  error={
                                    errors.DevelopmentalImpairmentsOtherDetails
                                  }
                                  value={
                                    data.DevelopmentalImpairmentsOtherDetails !==
                                    undefined
                                      ? data
                                          .DevelopmentalImpairmentsOtherDetails[
                                          "@value"
                                        ]
                                      : ""
                                  }
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  identical={compare(
                                    "DevelopmentalImpairmentsOtherDetails"
                                  )}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              ) : null}
                            </div>
                          </div>
                        </div>
                        {errors.DI !== undefined ? (
                          <div className="row">
                            <div className="col">
                              <div className="alert alert-danger">
                                {errors.DI}
                              </div>
                            </div>
                          </div>
                        ) : null}
                        {(data.DILowIQ !== undefined &&
                          data.DILowIQ["@value"] === "true") ||
                        (data.DISensoryImpairments !== undefined &&
                          data.DISensoryImpairments["@value"] === "true") ||
                        (data.DISpeechDelays !== undefined &&
                          data.DISpeechDelays["@value"] === "true") ||
                        (data.DIMotorCoordinationDelays !== undefined &&
                          data.DIMotorCoordinationDelays["@value"] ===
                            "true") ||
                        (data.DILearningDisorder !== undefined &&
                          data.DILearningDisorder["@value"] === "true") ||
                        (data.DISpatialDelays !== undefined &&
                          data.DISpatialDelays["@value"] === "true") ? (
                          <Input
                            name="DevelopmentalImpairmentsDetails"
                            label="Please describe:"
                            error={errors.DevelopmentalImpairmentsDetails}
                            value={
                              data.DevelopmentalImpairmentsDetails !== undefined
                                ? data.DevelopmentalImpairmentsDetails["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare(
                              "DevelopmentalImpairmentsDetails"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </>
                    )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="Bipolar"
                      label="Symptoms of Bipolar and Related Disorders"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.Bipolar !== undefined &&
                        data.Bipolar["@value"] === "true"
                      }
                      onChange={handleBipolarChange}
                      disabled={formDisabled}
                    />
                    {data.Bipolar !== undefined &&
                      data.Bipolar["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="ManicEpisodes"
                                  label="Manic (or hypomanic) Episodes"
                                  divClassName="ms-3"
                                  checked={
                                    data.ManicEpisodes !== undefined &&
                                    data.ManicEpisodes["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="DepressiveEpisodes"
                                  label="Depressive Episodes"
                                  divClassName="ms-3"
                                  checked={
                                    data.DepressiveEpisodes !== undefined &&
                                    data.DepressiveEpisodes["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="SignificantMoodChanges"
                                  label="Significant changes in mood and activity level that impact functioning"
                                  divClassName="ms-3"
                                  checked={
                                    data.SignificantMoodChanges !== undefined &&
                                    data.SignificantMoodChanges["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="BipolarOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.BipolarOther !== undefined &&
                                    data.BipolarOther["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.BipolarOther !== undefined &&
                                data.BipolarOther["@value"] === "true" ? (
                                  <Input
                                    name="BipolarOtherDetails"
                                    error={errors.BipolarOtherDetails}
                                    value={
                                      data.BipolarOtherDetails !== undefined &&
                                      data.BipolarOtherDetails["@value"]
                                    }
                                    onChange={handleChange}
                                    identical={compare("BipolarOtherDetails")}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.Bipolar !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.Bipolar}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="BipolarFrequency"
                            label="Frequency:"
                            error={errors.BipolarFrequency}
                            value={
                              data.BipolarFrequency !== undefined
                                ? data.BipolarFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              setData({
                                ...data,
                                BipolarFrequency: {
                                  "@value": e.currentTarget.value,
                                },
                              });
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("BipolarFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="BipolarIntensity"
                            label="Intensity:"
                            error={errors.BipolarIntensity}
                            value={
                              data.BipolarIntensity !== undefined
                                ? data.BipolarIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("BipolarIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="BipolarDuration"
                            label="Duration:"
                            error={errors.BipolarDuration}
                            value={
                              data.BipolarDuration !== undefined
                                ? data.BipolarDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("AnxietyDisorderDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          Diagnosis to consider: Bipolar I, Bipolar II,
                          Cyclothymic Disorder
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="Borderline"
                      label="Symptoms of Borderline Personality Disorder"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.Borderline !== undefined &&
                        data.Borderline["@value"] === "true"
                      }
                      onChange={handleBorderlineChange}
                      disabled={formDisabled}
                    />
                    {data.Borderline !== undefined &&
                      data.Borderline["@value"] === "true" && (
                        <>
                          <div className="container">
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="AvoidAbandonment"
                                  label="Frantic efforts to avoid real or imagined abandonment"
                                  divClassName="ms-3"
                                  checked={
                                    data.AvoidAbandonment !== undefined &&
                                    data.AvoidAbandonment["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="UnstableRelationships"
                                  label="A pattern of unstable and intense interpersonal relationships (Alternating between extremes of idealization and devaluation)"
                                  divClassName="ms-3"
                                  checked={
                                    data.UnstableRelationships !== undefined &&
                                    data.UnstableRelationships["@value"] ===
                                      "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="IdentityDisturbances"
                                  label="Identity Disturbances: Markedly and persistently unstable self-image or sense of self"
                                  divClassName="ms-3"
                                  checked={
                                    data.IdentityDisturbances !== undefined &&
                                    data.IdentityDisturbances["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Impulsivity"
                                  label="Impulsivity in at least 2: Spending, Sex, Substance Use, Reckless Driving, Binge Eating"
                                  divClassName="ms-3"
                                  checked={
                                    data.Impulsivity !== undefined &&
                                    data.Impulsivity["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="RecurrentSelfHarm"
                                  label="Recurrent suicidal behavior/gestures/threats or self-mutilating behavior"
                                  divClassName="ms-3"
                                  checked={
                                    data.RecurrentSelfHarm !== undefined &&
                                    data.RecurrentSelfHarm["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="AffectiveInstability"
                                  label="Affective instability due to marked reactivity of mood (e.g., intense dysphoria, irritability, or anxiety)"
                                  divClassName="ms-3"
                                  checked={
                                    data.AffectiveInstability !== undefined &&
                                    data.AffectiveInstability["@value"] ===
                                      "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col">
                                <Checkbox
                                  name="Emptiness"
                                  label="Chronic feelings of emptiness"
                                  divClassName="ms-3"
                                  checked={
                                    data.Emptiness !== undefined &&
                                    data.Emptiness["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="PoorAngerManagement"
                                  label="Inappropriate, intense anger or difficulty controlling anger"
                                  divClassName="ms-3"
                                  checked={
                                    data.PoorAngerManagement !== undefined &&
                                    data.PoorAngerManagement["@value"] ===
                                      "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                              <div className="col">
                                <Checkbox
                                  name="ParanoidIdeation"
                                  label="Transient, stress-related paranoid ideation or severe dissociative symptoms"
                                  divClassName="ms-3"
                                  checked={
                                    data.ParanoidIdeation !== undefined &&
                                    data.ParanoidIdeation["@value"] === "true"
                                  }
                                  commentsHandler={handleComments}
                                  onChange={handleChange}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-4">
                                <Checkbox
                                  name="BorderlineOther"
                                  label="Other:"
                                  divClassName="ms-3"
                                  checked={
                                    data.BorderlineOther !== undefined &&
                                    data.BorderlineOther["@value"] === "true"
                                  }
                                  onChange={handleChange}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                                {data.BorderlineOther["@value"] === "true" ? (
                                  <Input
                                    name="BorderlineOtherDetails"
                                    error={errors.BorderlineOtherDetails}
                                    value={
                                      data.BorderlineOtherDetails !==
                                        undefined &&
                                      data.BorderlineOtherDetails["@value"]
                                    }
                                    onChange={handleChange}
                                    identical={compare(
                                      "BorderlineOtherDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                ) : null}
                              </div>
                            </div>
                          </div>
                          {errors.Borderline !== undefined ? (
                            <div className="row">
                              <div className="col">
                                <div className="alert alert-danger">
                                  {errors.Borderline}
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <Input
                            name="BorderlineFrequency"
                            label="Frequency:"
                            error={errors.BorderlineFrequency}
                            value={
                              data.BorderlineFrequency !== undefined &&
                              data.BorderlineFrequency["@value"]
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("BorderlineFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="BorderlineIntensity"
                            label="Intensity:"
                            error={errors.BorderlineIntensity}
                            value={
                              data.BorderlineIntensity !== undefined &&
                              data.BorderlineIntensity["@value"]
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("BorderlineIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="BorderlineDuration"
                            label="Duration:"
                            error={errors.BorderlineDuration}
                            value={
                              data.BorderlineDuration !== undefined &&
                              data.BorderlineDuration["@value"]
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("PhobiasDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                  </div>
                  <div className="border rounded mb-2 px-2 pt-2">
                    <Checkbox
                      name="OtherSymptoms"
                      label="Other Symptoms"
                      divClassName="ms-3 fw-bold"
                      checked={
                        data.OtherSymptoms !== undefined &&
                        data.OtherSymptoms["@value"] === "true"
                      }
                      commentsHandler={handleComments}
                      onChange={handleOtherSymptomsChange}
                      disabled={formDisabled}
                    />
                    {data.OtherSymptoms !== undefined &&
                      data.OtherSymptoms["@value"] === "true" && (
                        <>
                          <Input
                            name="OtherSymptomsDetails"
                            label="Please describe:"
                            error={errors.OtherSymptomsDetails}
                            value={
                              data.OtherSymptomsDetails !== undefined
                                ? data.OtherSymptomsDetails["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("OtherSymptomsDetails")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OtherSymptomsFrequency"
                            label="Frequency:"
                            error={errors.OtherSymptomsFrequency}
                            value={
                              data.OtherSymptomsFrequency !== undefined
                                ? data.OtherSymptomsFrequency["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("OtherSymptomsFrequency")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OtherSymptomsIntensity"
                            label="Intensity:"
                            error={errors.OtherSymptomsIntensity}
                            value={
                              data.OtherSymptomsIntensity !== undefined
                                ? data.OtherSymptomsIntensity["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("OtherSymptomsIntensity")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                          <Input
                            name="OtherSymptomsDuration"
                            label="Duration:"
                            error={errors.OtherSymptomsDuration}
                            value={
                              data.OtherSymptomsDuration !== undefined
                                ? data.OtherSymptomsDuration["@value"]
                                : ""
                            }
                            onChange={(e) => {
                              handleChange(e);
                              buildClinicalNarrativeSummary();
                            }}
                            identical={compare("OtherSymptomsDuration")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </>
                      )}
                  </div>
                </ExpandDiv>
              )}
              <ExpandDiv
                id="OMHAS"
                expandStatus={expandStatus}
                className="mt-2"
                title="OHIO MENTAL HEALTH &amp; ADDICTION SERVICES (OMHAS) Review/Recommendations (as needed)"
              >
                <Checkbox
                  name="OMHASNA"
                  label="N/A (Skip Section)"
                  divClassName="ms-3"
                  checked={
                    data.OMHASNA !== undefined &&
                    data.OMHASNA["@value"] === "true"
                  }
                  commentsHandler={handleComments}
                  onChange={handleChange}
                  disabled={formDisabled}
                />
                {data.OMHASNA === undefined ||
                data.OMHASNA["@value"] !== "true" ? (
                  <>
                    <RadioButton
                      name="AgeGroup"
                      label="Is the client an adolescent or an adult?"
                      error={errors.AgeGroup}
                      value={data.AgeGroup && data.AgeGroup["@value"]}
                      options={ageGroupOptions}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    <div className="border m-2">
                      <div className="p-2">
                        <b>
                          Degree of Severity at Time of Admission (complete and
                          attach level of care form):
                        </b>
                      </div>
                      <div className="container border-top">
                        <div className="row border-bottom mt-2">
                          <div className="col">
                            Acute Intoxication Withdrawal:
                          </div>
                          <div className="col">
                            <Select
                              name="IntoxWithdrawalLevel"
                              error={errors.IntoxWithdrawalLevel}
                              value={data.IntoxWithdrawalLevel["@value"]}
                              options={
                                data.AgeGroup !== undefined
                                  ? data.AgeGroup["@value"] === "0"
                                    ? adolescentDimension1Options
                                    : data.AgeGroup["@value"] === "1"
                                    ? adultDimension1Options
                                    : []
                                  : levelOfCareOptions
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom mt-2">
                          <div className="col">
                            Biomedical Conditions/Complications:
                          </div>
                          <div className="col">
                            <Select
                              name="BiomedicalConditionsLevel"
                              error={errors.BiomedicalConditionsLevel}
                              value={data.BiomedicalConditionsLevel["@value"]}
                              options={
                                data.AgeGroup !== undefined
                                  ? data.AgeGroup["@value"] === "0"
                                    ? adolescentDimension2Options
                                    : data.AgeGroup["@value"] === "1"
                                    ? adultDimension2Options
                                    : []
                                  : levelOfCareOptions
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom mt-2">
                          <div className="col">
                            Emotional/ Behavioral/ Cognitive Conditions and/or
                            Complications:
                          </div>
                          <div className="col">
                            <Select
                              name="CognitiveConditionsLevel"
                              error={errors.CognitiveConditionsLevel}
                              value={data.CognitiveConditionsLevel["@value"]}
                              options={
                                data.AgeGroup !== undefined
                                  ? data.AgeGroup["@value"] === "0"
                                    ? adolescentDimension3Options
                                    : data.AgeGroup["@value"] === "1"
                                    ? adultDimension3Options
                                    : []
                                  : levelOfCareOptions
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom mt-2">
                          <div className="col">Readiness to Change:</div>
                          <div className="col">
                            <Select
                              name="ReadinessToChangeLevel"
                              error={errors.ReadinessToChangeLevel}
                              value={data.ReadinessToChangeLevel["@value"]}
                              options={
                                data.AgeGroup !== undefined
                                  ? data.AgeGroup["@value"] === "0"
                                    ? adolescentDimension4Options
                                    : data.AgeGroup["@value"] === "1"
                                    ? adultDimension4Options
                                    : []
                                  : levelOfCareOptions
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom mt-2">
                          <div className="col">Relapse Potential:</div>
                          <div className="col">
                            <Select
                              name="RelapsePotentialLevel"
                              error={errors.RelapsePotentialLevel}
                              value={data.RelapsePotentialLevel["@value"]}
                              options={
                                data.AgeGroup !== undefined
                                  ? data.AgeGroup["@value"] === "0"
                                    ? adolescentDimension5Options
                                    : data.AgeGroup["@value"] === "1"
                                    ? adultDimension5Options
                                    : []
                                  : levelOfCareOptions
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom mt-2">
                          <div className="col">Recovery Environment:</div>
                          <div className="col">
                            <Select
                              name="RecoveryEnvironmentLevel"
                              error={errors.RecoveryEnvironmentLevel}
                              value={data.RecoveryEnvironmentLevel["@value"]}
                              options={
                                data.AgeGroup !== undefined
                                  ? data.AgeGroup["@value"] === "0"
                                    ? adolescentDimension6Options
                                    : data.AgeGroup["@value"] === "1"
                                    ? adultDimension6Options
                                    : []
                                  : levelOfCareOptions
                              }
                              onChange={handleChange}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom mt-2">
                          <div className="col">
                            <b>
                              Level of Care Recommended (complete and attach
                              level of care form):
                            </b>
                          </div>
                          <div className="col">
                            <Select
                              name="LevelOfCareRecommendedLevel"
                              error={errors.LevelOfCareRecommendedLevel}
                              value={data.LevelOfCareRecommendedLevel["@value"]}
                              options={
                                data.AgeGroup !== undefined
                                  ? data.AgeGroup["@value"] === "0"
                                    ? adolescentDimension1Options
                                    : data.AgeGroup["@value"] === "1"
                                    ? adultDimension1Options
                                    : []
                                  : levelOfCareOptions
                              }
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        <div className="row border-bottom mt-2">
                          <div className="col">
                            <b>Level of Care Placed:</b>
                          </div>
                          <div className="col">
                            <Select
                              name="LevelOfCarePlacedLevel"
                              error={errors.LevelOfCarePlacedLevel}
                              value={data.LevelOfCarePlacedLevel["@value"]}
                              options={
                                data.AgeGroup !== undefined
                                  ? data.AgeGroup["@value"] === "0"
                                    ? adolescentDimension1Options
                                    : data.AgeGroup["@value"] === "1"
                                    ? adultDimension1Options
                                    : []
                                  : levelOfCareOptions
                              }
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                        {data.LevelOfCareRecommendedLevel["@value"] !==
                        data.LevelOfCarePlacedLevel["@value"] ? (
                          <>
                            <div className="row border-bottom mt-2">
                              <div className="col">
                                <b>
                                  If Not Placed in Recommended Level of Care,
                                  Indicate Reason:
                                </b>
                              </div>
                              <div className="col">
                                <Select
                                  name="NotPlacedReason"
                                  error={errors.NotPlacedReason}
                                  value={data.NotPlacedReason["@value"]}
                                  options={reasonNotPlacedOptions}
                                  onChange={(e) => {
                                    handleChange(e);
                                    buildClinicalNarrativeSummary();
                                  }}
                                  commentsHandler={handleComments}
                                  disabled={formDisabled}
                                />
                              </div>
                            </div>
                            {data.NotPlacedReason["@value"] === "7" && (
                              <div className="row border-bottom mt-2">
                                <div className="col">Please indicate:</div>
                                <div className="col-4">
                                  <Input
                                    name="NotPlacedReasonDetails"
                                    error={errors.NotPlacedReasonDetails}
                                    value={
                                      data.NotPlacedReasonDetails["@value"]
                                    }
                                    onChange={(e) => {
                                      handleChange(e);
                                      buildClinicalNarrativeSummary();
                                    }}
                                    identical={compare(
                                      "NotPlacedReasonDetails"
                                    )}
                                    commentsHandler={handleComments}
                                    disabled={formDisabled}
                                  />
                                </div>
                              </div>
                            )}
                          </>
                        ) : null}
                        <div className="row border-bottom mt-2">
                          <div className="col">
                            <b>Comments/Recommendations:</b>
                          </div>
                          <div className="col-8">
                            <TextArea
                              name="OMHASComments"
                              error={errors.OMHASComments}
                              value={data.OMHASComments["@value"]}
                              onChange={(e) => {
                                handleChange(e);
                                buildClinicalNarrativeSummary();
                              }}
                              identical={compare("OMHASComments")}
                              commentsHandler={handleComments}
                              disabled={formDisabled}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : null}
              </ExpandDiv>
              <ExpandDiv
                id="ClinicalNarrative"
                expandStatus={expandStatus}
                className="mt-2"
                title="Clinical Narrative Summary"
              >
                Include etiology of presenting problem and maintenance of
                problem; mental health history; alcohol/drug history; severity
                of problem; where problem occurs (functioning at home, at work,
                in community); onset of problem (acute vs. chronic); client
                motivation for treatment; whether problem is known to be
                responsive to treatment.
                <TextArea
                  name="ClinicalNarrativeSummary"
                  error={errors.ClinicalNarrativeSummary}
                  value={data.ClinicalNarrativeSummary["@value"]}
                  onChange={handleChange}
                  identical={compare("ClinicalNarrativeSummary")}
                  commentsHandler={handleComments}
                  disabled
                />
                <TextArea
                  name="ClinicalNarrativeSummaryComments"
                  label="Clinical Narrative Additional Comments"
                  error={errors.ClinicalNarrativeSummaryComments}
                  value={data.ClinicalNarrativeSummaryComments["@value"]}
                  onChange={(e) => {
                    handleChange(e);
                    buildClinicalNarrativeSummary();
                  }}
                  identical={compare("ClinicalNarrativeSummaryComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                  rows="5"
                  placeholder="Include information on who the ongoing clinician will be, the date of their care planning session, and any other comments necessary for the clinical narrative summary..."
                />
              </ExpandDiv>
              <ExpandDiv
                id="Diagnosis"
                expandStatus={expandStatus}
                className="mt-2"
                title="Diagnosis"
              >
                <FieldTable
                  name="DiagnosisDetails"
                  commentsHandler={handleComments}
                >
                  <br />
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col-3 py-2 border-end">ICD-10 Code</div>
                      <div className="col py-2">Diagnosis</div>
                    </div>
                    {data.DiagnosisDetails["@value"] &&
                      JSON.parse(data.DiagnosisDetails["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col-3 py-2 border-end d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(index, "DiagnosisDetails");
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "DiagnosisDetails"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <Input
                                    name="Code"
                                    value={item.Code}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "DiagnosisDetails"
                                      );
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(index, "DiagnosisDetails");
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  {item.Code}
                                </>
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Diagnosis"
                                  value={item.Diagnosis}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "DiagnosisDetails"
                                    );
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Diagnosis
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <>
                      <button
                        className="btn btn-primary mb-2"
                        onClick={() => {
                          handleAddRow("DiagnosisDetails", {
                            IsEditable: true,
                          });
                          buildClinicalNarrativeSummary();
                        }}
                      >
                        Add Diagnosis
                      </button>
                    </>
                  ) : null}
                  {errors.DiagnosisDetails !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.DiagnosisDetails}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
              </ExpandDiv>
              <ExpandDiv
                id="TreatmentRecommendations"
                expandStatus={expandStatus}
                className="mt-2"
                title="Treatment Recommendations/Needs"
              >
                <b>Assessed Needs</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="SkillsTraining"
                        label="Specific Skills Training"
                        divClassName="ms-3"
                        checked={data.SkillsTraining["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="FosterFamilySupport"
                        label="Foster Family Support"
                        divClassName="ms-3"
                        checked={data.FosterFamilySupport["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Occupational"
                        label="Occupational"
                        divClassName="ms-3"
                        checked={data.Occupational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="SymptomManagementSkills"
                        label="Symptom Management Skills"
                        divClassName="ms-3"
                        checked={
                          data.SymptomManagementSkills["@value"] === "true"
                        }
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Respite"
                        label="Respite"
                        divClassName="ms-3"
                        checked={data.Respite["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="ProsocialActivities"
                        label="Prosocial Activities"
                        divClassName="ms-3"
                        checked={data.ProsocialActivities["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="DecisionMaking"
                        label="Decision-Making"
                        divClassName="ms-3"
                        checked={data.DecisionMaking["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MedicationManagement"
                        label="Medication Management"
                        divClassName="ms-3"
                        checked={data.MedicationManagement["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="CaseManagement"
                        label="Case Management"
                        divClassName="ms-3"
                        checked={data.CaseManagement["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="FinancialSupport"
                        label="Financial Support"
                        divClassName="ms-3"
                        checked={data.FinancialSupport["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MedicalDentalVision"
                        label="Medical/Dental/Vision"
                        divClassName="ms-3"
                        checked={data.MedicalDentalVision["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="SocialSkills"
                        label="Development of Social/Interpersonal Skills"
                        divClassName="ms-3"
                        checked={data.SocialSkills["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="CommunitySupport"
                        label="Community/Environmental Support"
                        divClassName="ms-3"
                        checked={data.CommunitySupport["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="HealthyHabits"
                        label="Nutrition/Healthy Habits"
                        divClassName="ms-3"
                        checked={data.HealthyHabits["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="Mentoring"
                        label="Mentoring/Coaching"
                        divClassName="ms-3"
                        checked={data.Mentoring["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="EducationSchool"
                        label="Education/School"
                        divClassName="ms-3"
                        checked={
                          data.EducationSchool !== undefined &&
                          data.EducationSchool["@value"] === "true"
                        }
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="FamilySupport"
                        label="Family Support"
                        divClassName="ms-3"
                        checked={data.FamilySupport["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="Vocational"
                        label="Vocational"
                        divClassName="ms-3"
                        checked={data.Vocational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col"></div>
                  </div>
                </div>
                {errors.AssessedNeeds !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.AssessedNeeds}
                      </div>
                    </div>
                  </div>
                ) : null}
                <TextArea
                  name="TreatmentNeedsComments"
                  label="Comments:"
                  error={errors.TreatmentNeedsComments}
                  value={data.TreatmentNeedsComments["@value"]}
                  onChange={handleChange}
                  identical={compare("TreatmentNeedsComments")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <br />
                <FieldTable
                  name="TreatmentRecommendations"
                  commentsHandler={handleComments}
                >
                  <b>Treatment Recommendations</b>
                  <div className="container border mb-2">
                    <div className="row border-bottom">
                      <div className="col py-2 border-end">Recommendation</div>
                      <div className="col py-2 border-end">Status</div>
                      <div className="col py-2">Reason</div>
                    </div>
                    {data.TreatmentRecommendations["@value"] &&
                      JSON.parse(data.TreatmentRecommendations["@value"]).map(
                        (item, index) => (
                          <div key={item._id} className="row border-bottom">
                            <div className="col py-2 border-end d-flex">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <>
                                  <i
                                    className="fas fa-save me-2 d-print-none"
                                    onClick={() => {
                                      handleSaveRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <i
                                    className="far fa-trash-alt me-2 d-print-none"
                                    onClick={() => {
                                      handleDeleteRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  <Select
                                    name="Recommendation"
                                    value={item.Recommendation}
                                    options={treatmentRecommendationOptions}
                                    onChange={(e) => {
                                      handleRowChange(
                                        e,
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                    disabled={formDisabled}
                                  />
                                </>
                              ) : (
                                <>
                                  <i
                                    className="fas fa-pen me-2 d-print-none"
                                    onClick={() => {
                                      handleEditRow(
                                        index,
                                        "TreatmentRecommendations"
                                      );
                                      buildClinicalNarrativeSummary();
                                    }}
                                  ></i>
                                  {item.Recommendation}
                                </>
                              )}
                            </div>
                            <div className="col py-2 border-end">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <div className="container">
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Deferred-${index}`}
                                        label="SAFY will be providing"
                                        checked={item.Deferred === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "TreatmentRecommendations"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Referred-${index}`}
                                        label="SAFY will be referring to another agency/individual"
                                        checked={item.Referred === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "TreatmentRecommendations"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      />
                                    </div>
                                  </div>
                                  <div className="row">
                                    <div className="col">
                                      <Checkbox
                                        name={`Declined-${index}`}
                                        label="Declined by Client"
                                        checked={item.Declined === "true"}
                                        onChange={(e) => {
                                          e.currentTarget = {
                                            ...e.currentTarget,
                                            type: "checkbox",
                                            name: e.currentTarget.name.split(
                                              "-"
                                            )[0],
                                          };
                                          handleRowChange(
                                            e,
                                            index,
                                            "TreatmentRecommendations"
                                          );
                                          buildClinicalNarrativeSummary();
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                getTreatmentRecommendationStatus(item)
                              )}
                            </div>
                            <div className="col py-2">
                              {item.IsEditable === undefined ||
                              item.IsEditable ? (
                                <Input
                                  name="Reason"
                                  value={item.Reason}
                                  onChange={(e) => {
                                    handleRowChange(
                                      e,
                                      index,
                                      "TreatmentRecommendations"
                                    );
                                    buildClinicalNarrativeSummary();
                                  }}
                                  disabled={formDisabled}
                                />
                              ) : (
                                item.Reason
                              )}
                            </div>
                          </div>
                        )
                      )}
                  </div>
                  {!formDisabled ? (
                    <button
                      className="btn btn-primary mb-2"
                      onClick={() => {
                        handleAddRow("TreatmentRecommendations", {
                          IsEditable: true,
                        });
                        buildClinicalNarrativeSummary();
                      }}
                    >
                      Add Treatment Recommendation
                    </button>
                  ) : null}
                  {errors.TreatmentRecommendations !== undefined ? (
                    <div className="row">
                      <div className="col">
                        <div className="alert alert-danger">
                          {errors.TreatmentRecommendations}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </FieldTable>
                <br />
                <b>Further Evaluation Needs</b>
                <div className="container">
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="MorePsychiatric"
                        label="Psychiatric"
                        divClassName="ms-3"
                        checked={data.MorePsychiatric["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MorePsychological"
                        label="Psychological"
                        divClassName="ms-3"
                        checked={data.MorePsychological["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MoreNeurological"
                        label="Neurological"
                        divClassName="ms-3"
                        checked={data.MoreNeurological["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MoreEducational"
                        label="Educational"
                        divClassName="ms-3"
                        checked={data.MoreEducational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="MoreMedical"
                        label="Medical"
                        divClassName="ms-3"
                        checked={data.MoreMedical["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MoreDental"
                        label="Dental"
                        divClassName="ms-3"
                        checked={data.MoreDental["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MoreSubstanceUse"
                        label="Alcohol/Drug"
                        divClassName="ms-3"
                        checked={data.MoreSubstanceUse["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MoreVocational"
                        label="Vocational"
                        divClassName="ms-3"
                        checked={data.MoreVocational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <Checkbox
                        name="MoreOccupational"
                        label="Occupational"
                        divClassName="ms-3"
                        checked={data.MoreOccupational["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MoreVisualAuditory"
                        label="Visual/Auditory"
                        divClassName="ms-3"
                        checked={data.MoreVisualAuditory["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MoreNutritional"
                        label="Nutritional"
                        divClassName="ms-3"
                        checked={data.MoreNutritional["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <Checkbox
                        name="MoreOther"
                        label="Other"
                        divClassName="ms-3"
                        checked={data.MoreOther["@value"] === "true"}
                        commentsHandler={handleComments}
                        onChange={handleChange}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-9"></div>
                    <div className="col">
                      {data.MoreOther["@value"] === "true" ? (
                        <Input
                          name="MoreOtherDetails"
                          error={errors.MoreOtherDetails}
                          value={data.MoreOtherDetails["@value"]}
                          onChange={handleChange}
                          identical={compare("MoreOtherDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                {errors.EvaluationNeeds !== undefined ? (
                  <div className="row">
                    <div className="col">
                      <div className="alert alert-danger">
                        {errors.EvaluationNeeds}
                      </div>
                    </div>
                  </div>
                ) : null}
                <br />
                <RadioButton
                  name="DiagnosisEducation"
                  label="Education regarding diagnosis provided to client/family:"
                  error={errors.DiagnosisEducation}
                  value={data.DiagnosisEducation["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="RecommendationEducation"
                  label="Education regarding recommendations provided to client/family:"
                  error={errors.RecommendationEducation}
                  value={data.RecommendationEducation["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="ClientAgrees"
                  label="Client in agreement with recommendations:"
                  error={errors.ClientAgrees}
                  value={data.ClientAgrees["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <RadioButton
                  name="FamilyAgress"
                  label="Family in agreement with recommendations:"
                  error={errors.FamilyAgress}
                  value={data.FamilyAgress["@value"]}
                  options={yesNoOptions}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              </ExpandDiv>
              {showNarrative === "Show" ? (
                <div className="clinicalNarrative bg-white border p-2">
                  <label>
                    <b>Clinical Narrative Summary:</b>
                  </label>
                  <textarea
                    value={data.ClinicalNarrativeSummary["@value"]}
                    rows="5"
                    disabled
                  />
                </div>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default OHPSD;
